import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";

// ─── THEME ────────────────────────────────────────────────────────────────────
// Two looks live side by side. "classic" is the original design and stays the
// default. "modern" (Iznik & tezhip) can be previewed with ?theme=modern, and
// ?theme=classic switches back; the choice is remembered on that device.
// To make the new look the default for everyone, set REACT_APP_THEME=modern.
const THEME_KEY = "mithaq:theme";
const readTheme = () => {
  try {
    const q = new URLSearchParams(window.location.search).get("theme");
    if (q === "modern" || q === "classic" || q === "paper" || q === "editorial") { localStorage.setItem(THEME_KEY, q); return q; }
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === "modern" || saved === "classic" || saved === "paper" || saved === "editorial") return saved;
  } catch (e) {}
  const env = process.env.REACT_APP_THEME;
  return env === "modern" || env === "paper" || env === "editorial" ? env : "classic";
};
const THEME = typeof window !== "undefined" ? readTheme() : "classic";
const MODERN = THEME === "modern";
// "paper": a calm, Notion-inspired daylight look in the classic brand colors.
// Preview with ?theme=paper. It shares the flat structure of the modern look
// (no gradients or glows, small star accents) but not its lattice or lapis.
const EDITORIAL = THEME === "editorial";
// Editorial builds on paper's flat, light structure, so PAPER is true for both;
// EDITORIAL then adds its own palette, type, homepage and dashboard.
const PAPER = THEME === "paper" || EDITORIAL;
const FLAT = MODERN || PAPER;
// The switch only shows on the classic look for someone who has tried the new one.
const THEME_TRIED = (() => { try { return !!localStorage.getItem(THEME_KEY); } catch (e) { return false; } })();
const switchTheme = (t) => {
  try { localStorage.setItem(THEME_KEY, t); } catch (e) {}
  window.location.href = window.location.pathname;
};

const C = EDITORIAL ? {
  // Editorial: a field journal. Warm paper, green-black ink, the brand green
  // for deep sections, gold leaf on dark and a darker gold for text on light.
  cream: "#F5EFE4", parchment: "#DCD1BE", soft: "#ECE4D5",
  green: "#1C3A2E", gl: "#2D5A44",
  gold: "#B8892A", goldL: "#D4A843",
  text: "#1A201C", muted: "#5A564D", white: "#FBF8F1",
  amber: "#7A4A1C", amberL: "#C4732A",
  rose: "#8E2F22",
  turq: "#2D5A44",
  goldText: "#7D5C1C", onGold: "#14291F",
  stone: "#8A8072",
  night: "#12261E", sand: "#C8B89A", leafA: "#2D5A44", leafB: "#4A7C5E",
} : PAPER ? {
  // Paper: warm daylight canvas, white cards, green as the one structural
  // accent, gold only for small decoration. Brand colors from classic.
  cream: "#F8F4EE", parchment: "#E7E0D5", soft: "#F1ECE3",
  green: "#1C3A2E", gl: "#2D5A44",
  gold: "#B8892A", goldL: "#D4A843",
  text: "#1F1D1A", muted: "#5F5A54", white: "#FFFFFF",
  amber: "#7A4A1C", amberL: "#C4732A",
  rose: "#8E2F22",
  turq: "#2D5A44",
  goldText: "#83611E", onGold: "#14291F",
  stone: "#8A8178",
  night: "#1C3A2E", sand: "#C8B89A", leafA: "#2D5A44", leafB: "#4A7C5E",
} : MODERN ? {
  // Iznik & tezhip: lapis, gold leaf, porcelain, turquoise
  cream: "#F3F5F4", parchment: "#D9E0DE", soft: "#E8EEEC",
  green: "#1B2F5E", gl: "#2B4A85",
  gold: "#A8812F", goldL: "#C9A24A",
  text: "#161B26", muted: "#5B6372", white: "#FFFFFF",
  amber: "#7A4A1C", amberL: "#B5652A",
  rose: "#8E2F22",
  turq: "#1F7A78",
  // Text-safe gold (≥4.5:1 on porcelain, soft and white) and text on gold buttons.
  goldText: "#795D22", onGold: "#161B26",
  stone: "#5B6372",
  // Dark pages (public profile, family view): background, secondary text, avatar.
  night: "#101C38", sand: "#B4BFD1", leafA: "#2B4A85", leafB: "#3E64A6",
} : {
  cream: "#F8F3EC", parchment: "#EFE8DC", soft: "#F2EBE1",
  green: "#1C3A2E", gl: "#2D5A44",
  gold: "#B8892A", goldL: "#D4A843",
  text: "#1A1814", muted: "#6F665E", white: "#FFFFFF",
  amber: "#7A4A1C", amberL: "#C4732A",
  rose: "#3A1C1C",
  turq: "#2D5A44",
  // Text-safe gold (≥4.5:1 on cream, parchment and white) and text on gold buttons.
  goldText: "#83611E", onGold: "#14291F",
  // The original grey, kept for faint tints and lines (text uses muted).
  stone: "#7A7067",
  // Dark pages (public profile, family view): background, secondary text, avatar.
  night: "#1A2E1A", sand: "#C8B89A", leafA: "#2D5A2D", leafB: "#4A7C4A",
};
// A theme color at a given opacity, e.g. tint(C.gold, 0.1). Use this instead
// of a hard-coded rgba() so tints follow the look (classic or modern).
const tint = (hex, a) => { const n = parseInt(hex.slice(1), 16); return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`; };
// Fills that are gradients on classic. Paper is flat, so they become solid
// colors there; classic and modern get exactly the strings they always had.
const G_GREEN = PAPER ? "#1C3A2E" : `linear-gradient(135deg,${C.green},${C.gl})`;
const G_GOLD = PAPER ? C.goldL : `linear-gradient(135deg,${C.gold},${C.goldL})`;
const G_GOLD90 = PAPER ? C.gold : `linear-gradient(90deg,${C.gold},${C.goldL})`;
const G_GREEN90 = PAPER ? C.green : `linear-gradient(90deg,${C.green},${C.gl})`;
const G_RULE = PAPER ? "transparent" : `linear-gradient(90deg,${C.gold},transparent)`;
const G_LEAF = PAPER ? C.leafA : `linear-gradient(135deg,${C.leafA},${C.leafB})`;
const Fd = MODERN ? "'Marcellus', 'Times New Roman', serif" : "'Cormorant Garamond', Georgia, serif";
const Fb = MODERN ? "'Figtree', system-ui, sans-serif" : EDITORIAL ? "'Jost', system-ui, -apple-system, 'Segoe UI', sans-serif" : PAPER ? "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif" : "'Jost', sans-serif";
// Display weight: light on classic, regular on modern, firm on paper.
const FW = EDITORIAL ? 500 : PAPER ? 600 : MODERN ? 400 : 300;
const Fa = "'Amiri', 'Times New Roman', serif";   // Arabic

// ─── GEOMETRY: eight-pointed star (khatam), lattice and arch ──────────────────
const starPoints = (cx, cy, R) => {
  const r = R * Math.cos(Math.PI / 4) / Math.cos(Math.PI / 8);
  const pts = [];
  for (let i = 0; i < 16; i++) {
    const rad = i % 2 === 0 ? R : r;
    const a = (i * Math.PI) / 8 - Math.PI / 2;
    pts.push(`${(cx + rad * Math.cos(a)).toFixed(2)},${(cy + rad * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(" ");
};
function Star({ size = 14, fill = "currentColor", stroke, strokeWidth = 1, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0, ...style }}>
      <polygon points={starPoints(12, 12, 11.5)} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
    </svg>
  );
}
// Repeating star-and-cross lattice, as a CSS background image.
const latticeBg = (color, opacity = 0.18, s = 56) => {
  const c = s / 2, h = (s / Math.SQRT2) / 2;
  const sq = `${c - h},${c - h} ${c + h},${c - h} ${c + h},${c + h} ${c - h},${c + h}`;
  const dm = `${c},${c - c} ${s},${c} ${c},${s} 0,${c}`;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${s}' height='${s}'><g fill='none' stroke='${color}' stroke-opacity='${opacity}' stroke-width='1'><polygon points='${sq}'/><polygon points='${dm}'/></g></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};
// A medallion: a filled star with a fine inner star, for charters.
function Medallion({ size = 40, fill = C.green, inner = C.goldL, done }) {
  // Paper: a fine gold outline until the thing is done, then solid green.
  if (PAPER && !done && fill === C.green) return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true" style={{ flexShrink: 0 }}>
      <polygon points={starPoints(20, 20, 18.5)} fill={C.white} stroke={C.gold} strokeWidth="1.2" />
      <polygon points={starPoints(20, 20, 10)} fill="none" stroke={C.gold} strokeWidth="0.9" />
    </svg>
  );
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true" style={{ flexShrink: 0 }}>
      <polygon points={starPoints(20, 20, 19)} fill={fill} />
      <polygon points={starPoints(20, 20, 11)} fill="none" stroke={inner} strokeWidth="1.1" />
      {done && <path d="M14.5 20.5l3.6 3.6 7.4-8" fill="none" stroke={C.white} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
  );
}

// ─── MICRO-REFRAMES ───────────────────────────────────────────────────────────
const REFRAMES = {
  physical: {
    icon: "🌸",
    title: "This section is not about what you require. It's about how attraction works in you.",
    body: "Every question here asks about your own inner experience — not a checklist for another person. There is nothing shallow about caring about attraction in a marriage. The Prophet ﷺ encouraged looking at a potential spouse for a reason.\n\nWhat becomes a problem is pretending you don't care while searching for answers through back channels — and what becomes a wound is discovering something you wish you'd known earlier, that the other person wished they'd told you.",
    sub: "Honesty here protects both of you.",
  },
  mental: { icon:"🧠", title:"Before you begin this section", body:"This section is harder to write than it is to read. The person receiving this isn't looking for a clean record — they're looking for self-awareness. Someone who knows their patterns is someone who can work on them.", sub:"There is no correct answer here. Only an honest one." },
  conflict_history: { icon:"💔", title:"A note before this one", body:"Everyone has handled conflict badly. Everyone has patterns they're not proud of. This section isn't asking whether you're perfect — it's asking whether you've learned anything.", sub:"Honesty here is a filter, not a risk." },
  who_i_am: { icon:"🪞", title:"This is not a dating profile", body:"Nobody is going to grade your answers. The goal isn't to sound good — it's to know yourself well enough that the right person recognizes you, and the wrong person self-selects out.", sub:"Accuracy matters more than impressiveness here." },
  assumptions: { icon:"🔍", title:"The hardest things to see are the ones we carry without knowing", body:"These questions are designed to surface expectations you've probably never said out loud — to a potential spouse or even to yourself.", sub:"What you name here, you can talk about. What stays unnamed runs the show." },
  inlaws: { icon:"🏠", title:"Family is the longest conversation in a marriage", body:"Most marriage conflicts trace back to unspoken expectations about family loyalty, boundaries, and belonging.", sub:"Naming this now means your future spouse isn't fighting alone later." },
  parenting: { icon:"👶", title:"You will disagree about parenting. Every couple does.", body:"The question isn't whether you'll have different instincts — you will. The question is whether you've thought about your own instincts honestly enough to work through the differences.", sub:"There is no objectively correct way to raise a child. There is only intentionality." },
  unspoken: { icon:"🚩", title:"The things that seem too small to mention are often the things that end marriages", body:"Resentment almost never starts with a catastrophe. It starts with a toilet seat, a smell, a habit that was never discussed because it seemed too petty to bring up.", sub:"Nothing here is too small. Everything here is fair game." },
  money_values: { icon:"💰", title:"This section is not about what you earn. It's about what money has meant in your life.", body:"Money is one of the most emotionally loaded topics in any marriage — not because of numbers, but because of what those numbers represent. Safety. Power. Freedom. Status. Love. Anxiety.\n\nEvery question here is about your history and your orientation — not your income, not your requirements, not your intentions.", sub:"There is no correct answer. Only an honest one." },
};

// ─── STORY QUESTIONS ──────────────────────────────────────────────────────────
const STORY_QS = [
  { id:"s1", type:"text", label:"On a hard day", text:"How do you show up when life is heavy?", placeholder:"Be honest — do you go quiet, do you reach out, do you clean the whole house?", hint:"This tells a future spouse more than any résumé ever could.", example:"e.g. 'I go very quiet and need about 48 hours before I can talk. I sometimes cancel plans without explaining why, which I know is hard for people around me.'" },
  { id:"s2", type:"text", label:"Love's first teacher", text:"What did your family teach you about love — for better or worse?", placeholder:"What patterns did you inherit? What did you consciously choose to keep or leave behind?", hint:"We all carry blueprints. Naming yours is an act of courage.", example:"e.g. 'In my family, love was expressed through doing — cooking, providing, showing up. Saying I love you was rare. I'm working on learning the verbal version.'" },
  { id:"s3", type:"text", label:"The gap between perception & reality", text:"What do people almost always assume about you that is wrong?", placeholder:"The misread that has cost you connections, opportunities, or understanding...", hint:"This is your chance to correct the record before it costs you a connection.", example:"e.g. 'People assume I'm confident because I speak clearly in public. In private I struggle with self-doubt more than anyone around me knows.'" },
  { id:"s4", type:"text", label:"The question nobody asks", text:"What do you wish someone would ask you that they never do?", placeholder:"The thing that, if they asked, you'd light up — because finally, someone got past the surface.", hint:"Write the question. Then answer it.", example:"e.g. 'Nobody ever asks what I'm actually afraid of. I'm afraid of being truly known and still found lacking.'" },
  { id:"s5", type:"text", label:"Right now — the real version", text:"Who are you at this exact moment in your life — not the polished version?", placeholder:"What are you figuring out? What are you proud of? What are you still healing?", hint:"Nobody is finished. Show your actual self, not your best self.", example:"e.g. 'I'm in a good place professionally and a messy place emotionally. I recently ended a friendship that was taking more than it gave and I'm still processing it.'" },
];

const GROWTH_QS = [
  { id:"g1", type:"text", label:"Active work", text:"What are you actively, intentionally working on improving in yourself right now?", placeholder:"Not vague aspirations — what are you actually doing about it?", hint:"Habits, therapy, books, conversations, du'a. Specifics matter.", example:"e.g. 'I've been in therapy for 8 months working on why I shut down when I feel criticized. I've also started journaling after Fajr — inconsistently, but I'm building the habit.'" },
  { id:"g2", type:"choice", label:"Trajectory", text:"When it comes to your deen, which direction describes you best right now?", options:["Growing — I'm more consistent than I was a year ago","Stable — I have my practice and I protect it","Rebuilding — I fell off and I'm finding my way back","Exploring — I'm figuring out what faith means to me"] },
  { id:"g3", type:"text", label:"Five-year self", text:"Who do you intend to be in 5 years — spiritually, relationally, professionally?", placeholder:"Not what you'll achieve. Who you'll have become.", hint:"Are you growing in a direction that needs a compatible co-traveler?", example:"e.g. 'I want to be the kind of person my children could describe as emotionally safe. That means doing work now that they'll never see but will definitely feel.'" },
  { id:"g4", type:"multi", label:"Growth edges", text:"Where are your honest growing edges right now?", options:["Emotional regulation","Consistency in prayer","Financial discipline","Communicating needs clearly","Setting boundaries with family","Showing up for others","Managing stress and worry","Breaking generational patterns"] },
  { id:"g5", type:"text", label:"What you don't say enough", text:"What have you built, overcome, or become that you don't say out loud enough?", placeholder:"Say it here.", hint:"Not for ego — for clarity about what you bring.", example:"e.g. 'I put myself through graduate school while caring for a sick parent and I've never told anyone the full weight of what that cost me — or what it made me.'" },
];

// ─── PHYSICAL EXPECTATIONS QUESTIONS ─────────────────────────────────────────
// Direction: every question is about the person's own experience of attraction,
// never a requirement list for the other. No one should finish this feeling
// lacking — they should finish it feeling understood.
const PHYSICAL_QS = [
  {
    id:"ph1", type:"choice", label:"Your honest starting point",
    text:"How does physical attraction work for you in the context of marriage?",
    options:[
      "It needs to be present from the start — I can't build on something that isn't there",
      "It matters deeply, but I genuinely believe it grows with time and knowing someone",
      "I prioritize other qualities first and trust that attraction follows real connection",
      "I'm honestly still figuring out how I feel about this",
    ],
    hint:"There is no correct answer. This is about knowing yourself.",
  },
  {
    id:"ph2", type:"multi", label:"What attraction means to you",
    text:"When you feel genuinely attracted to someone, what is usually at the root of it?",
    options:[
      "Visual appearance — how they look",
      "Physical health and how they carry themselves",
      "Their voice, manner of speaking, the way they choose words",
      "Confidence and ease in their own skin",
      "Energy and presence when you're in the same room",
      "Emotional warmth — how they make people around them feel",
      "Intellect and how they think",
      "The way they move through the world — their character made visible",
    ],
    hint:"Most people find that attraction is more layered than they initially believed. This question is an invitation to notice that.",
  },
  {
    id:"ph3", type:"text", label:"What experience has taught you",
    text:"Have you ever felt attracted to someone whose appearance wasn't what you expected or initially hoped for? What happened?",
    placeholder:"You don't have to have a tidy answer. What did you actually notice?",
    hint:"This is a behavioral question — past experience reveals patterns that preferences alone don't.",
    example:"e.g. 'Yes — someone I initially dismissed visually became someone I was deeply drawn to after a few conversations. I don't fully understand it, but it taught me to stay longer in rooms I'd usually leave early.'",
  },
  {
    id:"ph5", type:"text", label:"What you want known before we meet",
    text:"Is there anything about yourself — physical, personal, situational — that you'd rather name yourself than have someone discover or wonder about?",
    placeholder:"This is your space to offer information as an act of honesty rather than have it surface in a way you can't control.",
    hint:"This shifts the agency entirely to you. Whatever you choose to share here, you're sharing it — not having it found.",
    example:"e.g. 'I wear hijab and also have a disability that isn't visible in photos. Both are part of who I am. I'd rather you know before we meet than have either thing be a surprise in the room.'",
    sensitive: true,
  },
  {
    id:"ph6", type:"choice", label:"How you think about attraction and growth",
    text:"If attraction were modest at a first meeting but grew steadily over several months of getting to know someone — how would you feel about that journey?",
    options:[
      "That sounds like exactly how I'd want it to work",
      "I could embrace that if everything else felt right",
      "I'm not sure — I think I'd always wonder if it was 'enough'",
      "I'd find that difficult — I need to feel something early to want to continue",
    ],
    hint:"Neither answer is wrong. This is about compatibility of approach, not correctness of feeling.",
  },
];

// ─── CHARTERS ─────────────────────────────────────────────────────────────────
// The same choices for every "who does this chore" question. Husband / wife
// rather than me / my spouse, so two people who agree compare as the same.
const CHORE_OPTIONS = ["Mostly the husband","Mostly the wife","Shared fairly","Whoever's free","Hire help when we can"];

const CHARTERS = [
  { id:"readiness", icon:"🌿", title:"Marriage Readiness Survey", sub:"Core values & life goals", tag:"Start Here", color:"#1C3A2E", questions:17, qs:[
    // The big picture
    { id:"q1", type:"scale", text:"How do you prioritize these in your life?", items:["Family","Faith & Spirituality","Career","Personal Growth","Financial Stability"] },
    { id:"q2", type:"choice", text:"How important is it to you that you and your spouse practice the faith together?", options:["Essential","Important","Nice, but not essential","Not important"] },
    { id:"q3", type:"multi", text:"What makes a marriage successful to you?", options:["Companionship & partnership","Raising children together","Supporting each other's dreams","Emotional & spiritual growth","Lifelong loyalty"] , hint:"Pick any." },
    { id:"q4", type:"text", text:"How do you picture your life in 10 years?", placeholder:"Describe your vision...", example:"e.g. 'Living in a city with a strong Muslim community, in a home that feels calm rather than busy, with work that funds our life but doesn't consume it.'" },
    { id:"q12", type:"text", label:"Why now", text:"Why do you feel ready for marriage now?", placeholder:"A sentence or two." },
    { id:"q18", type:"choice", label:"Married before", text:"Have you been married before?", options:["No","Yes — and I'd like to tell you about it in person"], hint:"Only your choice is shared, never details. The story is yours to tell in person." },
    { id:"q19", type:"choice", label:"Children from before", text:"Do you have children from before?", options:["No","Yes — they live with me","Yes — they live mostly with their other parent"], hint:"Only your choice is shared, never details. The story is yours to tell in person." },
    // Hopes & worries
    { id:"q16", type:"text", label:"Looking forward to", text:"What are you most looking forward to in marriage?", placeholder:"A sentence or two." },
    { id:"q17", type:"text", label:"Worries", text:"What worries you most about marriage?", placeholder:"A sentence or two — naming it is the first step." },
    // How you live
    { id:"q5", type:"choice", text:"When you disagree with someone close, what do you usually do first?", options:["Avoid it","Talk it through calmly","Look for a compromise","Take time alone first"] },
    { id:"q6", type:"choice", label:"Togetherness", text:"How much togetherness do you picture day to day?", options:["Doing most things together","A balance of together and apart","Plenty of independence"] },
    { id:"q7", type:"choice", label:"Planner or spontaneous", text:"Are you more of a planner or spontaneous?", options:["Mostly a planner","A mix of both","Mostly spontaneous"] },
    { id:"q8", type:"choice", label:"Health & fitness", text:"How do health and fitness fit into your life?", options:["A regular routine","Casual, now and then","Not much","I'm working on it"] },
    { id:"q9", type:"choice", label:"Smoking or vaping", text:"How do you feel about smoking or vaping?", options:["I don't, and it would be a deal-breaker in a spouse","I don't, but it wouldn't be a deal-breaker","I do, and I'm trying to quit","I do"] },
    // Your future home
    { id:"q13", type:"multi", label:"Home atmosphere", text:"What atmosphere do you want at home?", options:["Peaceful","Playful","Spiritually nurturing","Organized","Social and welcoming","Focused and disciplined"], hint:"Pick up to three." },
    { id:"q14", type:"choice", label:"Home feel", text:"Your home feels most like you when it's…", options:["Minimal and calm","Cozy and lived-in","Busy and full of people"] },
    { id:"q15", type:"multi", label:"Picture of home", text:"Any of these in your picture of home?", options:["Pets","A garden","City life","A quieter, rural life","A big family table"], hint:"Pick any." },
  ]},
  { id:"money_values", icon:"💰", title:"Money, Values & History", sub:"Your relationship with wealth — not your balance", tag:"New", color:"#2A3A1C", questions:8, reframe:"money_values", qs:[
    {
      id:"mv1", type:"text", label:"Where it started",
      text:"What did money represent in your family growing up?",
      placeholder:"Was it a source of safety, anxiety, conflict, power, scarcity, or something else entirely?",
      hint:"Our earliest relationship with money is usually the one that runs deepest — and longest.",
      example:"e.g. 'Money was a constant source of tension. My parents argued about it quietly but often. I learned to associate financial strain with emotional distance — which means I over-correct now by needing to feel financially settled before I feel emotionally safe in a relationship.'",
    },
    {
      id:"mv2", type:"text", label:"How it shaped you",
      text:"How has your upbringing around money shaped the way you think and feel about it today?",
      placeholder:"What patterns did you inherit — consciously or unconsciously?",
      hint:"This is about pattern recognition, not blame.",
      example:"e.g. 'I grew up comfortably but my parents never talked about money openly. So I have a decent relationship with earning but almost no language for having honest financial conversations. I'm working on that.'",
    },
    {
      id:"mv3", type:"choice", label:"Security vs status",
      text:"When you think about financial stability in a marriage, which is closer to what you actually want?",
      options:[
        "Security — enough to feel safe, not anxious about next month",
        "Comfort — a good life, travel, experiences, without constant calculation",
        "Status — a visible level of success that reflects well on both of us",
        "Freedom — enough to have choices, not necessarily abundance",
        "I'm honestly still figuring out what I actually want vs what I've been told to want",
      ],
      hint:"None of these answers are wrong. But the gap between what you say and what you actually feel is where conflict lives.",
    },
    {
      id:"mv4", type:"text", label:"The discomfort test",
      text:"How do you honestly feel around people who have significantly more than you — and significantly less?",
      placeholder:"Both directions matter here. What comes up internally?",
      hint:"This is one of the most revealing questions you can ask yourself. The answer tells you a lot about where your self-worth is located.",
      example:"e.g. 'Around people with more, I notice a quiet competitiveness I don't love in myself. Around people with less, I sometimes feel guilty rather than just grateful — which tells me my relationship with money and identity isn't as clean as I'd like it to be.'",
    },
    {
      id:"mv5", type:"text", label:"The reversal question",
      text:"If your financial situation changed dramatically — in either direction — what would actually change about what you want in a marriage?",
      placeholder:"Be honest. If you became significantly wealthier or significantly less wealthy, would your criteria shift?",
      hint:"This is one of the most clarifying questions there is. The answer reveals whether your attraction to a person is tied to their circumstances or to who they actually are.",
      example:"e.g. 'Honestly? If I lost most of what I have, I'd probably still want the same kind of person — but I notice I'd feel less deserving of asking for what I want. That insecurity is mine to work on, not theirs to fix.'",
    },
    {
      id:"mv6", type:"text", label:"Money in difficulty",
      text:"Describe a specific time in your life when money was genuinely tight or uncertain. How did you handle it — practically and emotionally?",
      placeholder:"What did that period reveal about you?",
      hint:"Behavioral questions reveal character in ways that values statements never do.",
      example:"e.g. 'After I finished my degree I had almost nothing for about a year. I was too proud to ask for help and that pride cost me — I made worse decisions trying to appear fine. I understand that pattern now and I'd handle it differently.'",
    },
    {
      id:"mv7", type:"text", label:"Generosity and its limits",
      text:"What does genuine generosity look like to you in a marriage — and where does it cross into something that concerns you?",
      placeholder:"What feels like love expressed through giving? What feels like being taken advantage of?",
      hint:"The line between generosity and resentment is one of the most important ones to know before you need to draw it.",
      example:"e.g. 'Generosity to me looks like giving without tracking. But I notice I start to feel resentful when giving feels expected rather than received with gratitude. I'm trying to understand whether that's a boundary or a condition I'm placing on love.'",
    },
    {
      id:"mv8", type:"multi", label:"Your honest orientation",
      text:"Which of these statements feel genuinely true about you right now?",
      options:[
        "I would be equally interested in someone regardless of whether their income was higher or lower than mine",
        "Financial compatibility matters to me — I want someone in a similar economic reality",
        "I care more about someone's relationship with money than the amount of it they have",
        "I've noticed that financial security affects how emotionally available I am in a relationship",
        "I want to be wanted for who I am, not what I have or earn — and I worry about this",
        "I want a partner who could stand on their own financially if they had to",
        "I'm working on separating my self-worth from my financial situation",
      ],
      hint:"You can select as many as feel true. This section is for your own clarity — not a test.",
    },
  ]},
  { id:"physical", icon:"🌸", title:"Physical Expectations & Attraction", sub:"Your honest relationship with attraction", tag:"New", color:"#3A1C2A", questions:5, reframe:"physical", qs: PHYSICAL_QS },
  { id:"who_i_am", icon:"🪞", title:"Who I Actually Am", sub:"Beyond the profile — your real self", tag:"Essential", color:"#2C3A1C", questions:5, reframe:"who_i_am", qs:[
    { id:"w3", type:"text", text:"What would your closest friend say is your most underrated quality?", placeholder:"The thing they see in you that you sometimes forget.", example:"e.g. 'My friend says I make people feel like they matter. I forget that's not common.'" },
    { id:"w4", type:"multi", text:"Which of these feel true about you in relationships?", options:["I need more time to open up than most people expect","I love deeply but struggle to express it verbally","I'm the one who holds space for everyone else","I can be intense — my feelings run deep","I tend to fix things rather than just sit with them","I need alone time even when I love you","I'm more sensitive than I appear"] , hint:"Pick any." },
    { id:"w6", type:"text", label:"A good ordinary day", text:"What does a good ordinary day look like for you?", placeholder:"Not a holiday — a normal Tuesday that goes well." },
    { id:"w7", type:"text", label:"What drains me", text:"What drains you that others might not notice?", placeholder:"A sentence or two." },
    { id:"w8", type:"multi", label:"Showing care", text:"When words don't come easily, how do you show you care?", options:["Doing things for people","Spending time with them","Small gifts or gestures","Physical warmth — a hug, sitting close","Checking in with a message"], hint:"Pick any." },
  ]},
  { id:"faith_journey", icon:"☪️", title:"Faith Journey", sub:"Where you've been & where you're headed spiritually", tag:"Foundation", color:"#1C3830", questions:11, qs:[
    { id:"fj1", type:"choice", text:"How would you describe your relationship with Islam right now?", options:["Deeply rooted & consistent","Committed but growing","Rebuilding after a difficult season","Still finding my footing"] },
    { id:"fj2", type:"text", text:"Describe the arc of your faith — where you started, where you wandered, where you are now.", placeholder:"This isn't a test. There are no wrong answers. Just your honest story.", example:"e.g. 'I was raised religious, lost my practice in university, and came back after a period of real questioning. My faith now feels chosen rather than inherited — and that makes it stronger.'" },
    { id:"fj3", type:"multi", text:"What anchors your spiritual life day to day, right now?", options:["Salah (5 daily prayers)","Qur'an recitation or reflection","Du'a and personal connection with Allah","Islamic community & friends","Islamic podcasts, lectures, or books","Serving others as ibadah"] , hint:"Pick any." },
    { id:"fj6", type:"choice", label:"Salah today", text:"Where is your salah right now?", options:["All five, on time","All five, sometimes late","Some prayers most days","I'm working on it"], hint:"Answer for how things are today, not how you'd like them to look. 'I'm working on it' is honest and respected." },
    { id:"fj4", type:"text", text:"What does your faith ask of you that you find genuinely difficult?", placeholder:"Honesty here builds more trust than perfection ever could.", example:"e.g. 'Sabr. I understand it intellectually but when I'm in it, I resist. I'm working on this.'" },
    { id:"fj7", type:"multi", label:"Learning the deen", text:"Where do you learn your deen from most?", options:["A local imam or masjid","Classes or courses","Books","Online scholars and lectures","Family"], hint:"Pick any." },
    // Modest dress — three short questions so a hope can be voiced without pressure
    { id:"fj10", type:"choice", label:"How I dress today", text:"How do you feel about how you dress today?", options:["I'm content with how I dress now","I'd like to grow more modest in my own time","I'm still figuring it out"], hint:"Modesty is a journey for both spouses. There's no wrong answer, and no answer here makes anyone more or less Muslim." },
    { id:"fj11", type:"choice", label:"A hope for my spouse", text:"If you hoped your spouse would dress more modestly, how would you want to handle it?", options:["I'd mention it once, gently, and leave it to them","I'd hope we grow into it together, at their pace","It would matter a lot to me before marriage","It isn't something I'd ask of a spouse"], hint:"Modesty is a journey for both spouses. There's no wrong answer, and no answer here makes anyone more or less Muslim." },
    { id:"fj12", type:"choice", label:"Who has a say", text:"Who should have a say in how each of you dresses?", options:["Each of us, for ourselves","The two of us, together","Our families should have a voice too"], hint:"Modesty is a journey for both spouses. There's no wrong answer, and no answer here makes anyone more or less Muslim." },
    { id:"fj8", type:"choice", label:"Seasons of doubt", text:"If one of you went through a season of doubt or weaker faith, how would you want the other to respond?", options:["Patiently, without pressure","Gently encouraging, day by day","Seeking help from someone we both trust","I'm honestly not sure"] },
    { id:"fj5", type:"text", text:"What do you hope marriage will do for your faith?", placeholder:"A sentence or two.", example:"e.g. 'I want a marriage where we remind each other of Allah without it feeling like a lecture. Where the tone of the home is just... peaceful.'" },
  ]},
  { id:"assumptions", icon:"🔍", title:"Assumptions Audit", sub:"Unspoken expectations that quietly sabotage", tag:"Essential", color:"#3A1C2C", questions:5, reframe:"assumptions", qs:[
    { id:"a1", type:"multi", text:"Which of these assumptions have you caught yourself carrying into marriage conversations?", options:["My spouse will naturally adapt to my family's way","Love means never having to explain what you need","If they're practicing, we're compatible","Conflict means something is wrong with us","We'll figure out the hard stuff as it comes","My parenting style is obviously the right one","Our children will grow up the way I did","My spouse's family will accept me because we share the faith"] },
    { id:"a2", type:"text", text:"What expectation do you have of a spouse that you've never said out loud?", placeholder:"Something you feel entitled to but have never named...", example:"e.g. 'I expect to be chosen first, before their friends and before their family's demands. I've never said that to anyone.'" },
    { id:"a3", type:"text", text:"What did you grow up assuming marriage looks like, that you're now questioning?", placeholder:"Inherited blueprints often run invisibly. Name one of yours.", example:"e.g. 'I learned that the person who loves more is the one who gets hurt. So I've been protecting myself by loving less first. That's not healthy and I know it.'" },
    { id:"a4", type:"choice", text:"When a relationship feels 'off,' your first internal assumption is usually:", options:["Something is wrong with them","Something is wrong with me","We're just incompatible","This is a solvable communication problem"] },
    { id:"a5", type:"text", text:"What is one thing you are willing to be wrong about when it comes to what marriage will look like?", placeholder:"Flexibility named here becomes wisdom in practice.", example:"e.g. 'I've always assumed I need to live far from my parents to have a healthy marriage. I'm willing to be wrong about that if boundaries are clear.'" },
  ]},
  { id:"conflict_history", icon:"💔", title:"Conflict History & Patterns", sub:"How you act when things get hard, and what you've learned", tag:"Deep", color:"#3A1C1C", questions:7, reframe:"conflict_history", qs:[
    { id:"ch1", type:"text", text:"What has a relationship with anyone close — family, friends or otherwise — taught you about how you act when you feel hurt?", placeholder:"Not what they did. What did you do?", example:"e.g. 'I discovered I punish people with silence when I feel dismissed. I don't yell — I just become absent. I'm working on naming the hurt instead.'" },
    { id:"ch2", type:"multi", text:"Which conflict patterns have you caught yourself repeating?", options:["Shutting down and going silent","Saying things in anger I regret","Bringing up old issues mid-fight","Needing to win more than resolve","Avoiding the real issue entirely","Making assumptions before listening","None that I've noticed"] },
    { id:"ch3", type:"choice", text:"When you have genuinely hurt someone you love, your response is typically:", options:["Apologize quickly — the relationship matters more","Explain my reasoning first, then apologize","Give it time — I need to process before I can apologize genuinely","I struggle to apologize — I'm working on this"] },
    { id:"ch4", type:"text", text:"What does healthy conflict resolution look like to you — based on what you've actually experienced?", placeholder:"What have you seen work? What did you once think worked but didn't?", example:"e.g. 'The only time conflict actually resolved was when both people stopped trying to win and got curious about what the other person actually needed.'" },
    { id:"ch5", type:"text", text:"What would you want a future spouse to know about what triggered your worst moments — so they can help you, not recreate them?", placeholder:"This is intimate. Be as honest as you're ready to be.", example:"e.g. 'Being dismissed when I'm trying to express something serious. I shut completely down and it can take days. Knowing this in advance changes how it lands.'" },
    { id:"ch6", type:"multi", text:"During a disagreement, what do you need most from your spouse?", options:["Time to process","Reassurance","Space","Soft tone","Directness"], hint:"Pick any." },
    { id:"ch7", type:"choice", label:"Raising my voice", text:"When you're frustrated, do you raise your voice?", options:["Rarely or never","Sometimes","Often"], hint:"Answer for how things are today — honesty here helps your spouse more than a good impression." },
  ]},
  { id:"parenting", icon:"👶", title:"Parenting Philosophy", sub:"How you'll raise the next generation together", tag:"Essential", color:"#2A1C3A", questions:15, reframe:"parenting", qs:[
    // Wanting children
    { id:"p6", type:"choice", label:"Wanting children", text:"Do you want children?", options:["Yes","Probably","Not sure","No"] },
    { id:"p7", type:"choice", label:"How many", text:"How many would you hope for?", options:["One","Two or three","Four or more","Whatever Allah gives","We'd decide together"] },
    { id:"p8", type:"choice", label:"When", text:"When would you hope to have your first child?", options:["Within the first year","After a year or two","After a few years","Whenever Allah wills"] },
    { id:"p15", type:"choice", label:"Stepchildren", text:"How would you feel about being a parent to your spouse's children from before?", options:["I'd welcome it fully","I'd want to grow into it, at their pace","I'm not sure yet","It would be hard for me"] },
    { id:"p9", type:"multi", label:"If children don't come easily", text:"If children didn't come easily, what would you be open to?", options:["Fertility treatment","Fostering or kafala","Accepting it as Allah's decree","I'd need time to think"], hint:"Pick any. Private: your partner only sees whether you see this the same way." },
    // Raising them
    { id:"p2", type:"multi", label:"Values", text:"Which values are most important to raise your children with?", options:["Deep Islamic identity & practice","Academic excellence","Emotional intelligence & empathy","Financial literacy from young age","Service to community","Independent critical thinking","Physical health & discipline"] , hint:"Pick any." },
    { id:"p10", type:"choice", label:"Faith in daily life", text:"How would you want faith to be part of their daily life?", options:["Structured from early on — Qur'an classes, prayer routines","Gradually, as they're ready","Mostly through our example at home"] },
    { id:"p3", type:"choice", label:"Schooling", text:"Which schooling do you lean toward?", options:["Islamic school","Public school","Private school","Homeschooling","Depends on the child"] },
    { id:"p1", type:"choice", label:"Discipline instinct", text:"When a child misbehaves, what's your first instinct?", options:["Explain calmly why it was wrong","Give a consequence immediately","Understand what need the behavior was expressing","Redirect toward something better"] },
    { id:"p12", type:"choice", label:"Smacking", text:"How do you feel about smacking as discipline?", options:["Never okay","Only as a rare last resort","Acceptable when needed"] },
    { id:"p11", type:"choice", label:"Screens", text:"Screens for young children:", options:["Very limited","Some, with clear rules","Not a big concern for me"] },
    // Sharing the work
    { id:"p13", type:"choice", label:"Hands-on care", text:"Who do you picture doing most of the hands-on care — night feeds, nappies, school runs?", options:CHORE_OPTIONS },
    { id:"p14", type:"choice", label:"Grandparents' help", text:"Help from grandparents with childcare:", options:["We'd love regular help","Occasional help","We'd rather manage ourselves"] },
    // Reflection
    { id:"p4", type:"text", text:"What is one thing from your own upbringing that you will absolutely do differently with your children?", placeholder:"This isn't about blaming your parents — it's about conscious parenting.", example:"e.g. 'My parents never acknowledged when they were wrong. I want my children to see me apologize — genuinely.'" },
    { id:"p5", type:"text", text:"When you and your spouse deeply disagree on how to handle something with a child — what does the resolution process look like?", placeholder:"Who has final say? How do you protect the child from the disagreement?", example:"e.g. 'I think we discuss it privately, present a unified front, then keep discussing until we actually agree — not just defer.'" },
  ]},
  { id:"inlaws", icon:"🏠", title:"Extended Family & In-Laws", sub:"Loyalty, boundaries & the family you're inheriting", tag:"Deep", color:"#3A2A1C", questions:12, reframe:"inlaws", qs:[
    // Your family
    { id:"il5", type:"text", text:"What do you need your spouse to understand about your family — before they meet them?", placeholder:"Context is kindness. Give it freely.", example:"e.g. 'My dad shows love through criticism. He will say something that sounds harsh. It's not contempt — it's his version of caring.'" },
    { id:"il2", type:"text", text:"What will your family expect from you after marriage?", placeholder:"Visits, calls, involvement in decisions, roles…", example:"e.g. 'My mother expects weekly visits and to be consulted on major decisions. The visits I'm fine with. The consultation part is where I'd need to redraw something.'" },
    { id:"il10", type:"choice", label:"Family closeness", text:"How close is your family day to day?", options:["We talk every day","A few times a week","Weekly or less","It's complicated"] },
    // Time & space
    { id:"il7", type:"choice", label:"Seeing parents", text:"How often would you want to see each other's parents?", options:["Every week","A couple of times a month","Monthly","On holidays and occasions"] },
    { id:"il6", type:"choice", label:"Living with parents", text:"Living with parents in the early years of marriage:", options:["Not something I'd want","For a short while, if needed","Open to it long-term","I'd prefer it"] },
    { id:"il11", type:"choice", label:"Dropping by", text:"How would you feel about family dropping by without calling first?", options:["I'd love it","Fine now and then","I'd rather they call first"] },
    // Decisions & privacy
    { id:"il8", type:"choice", label:"Parents' role", text:"For big decisions — children, money, where we live — what role should parents play?", options:["We hear their advice, then decide ourselves","We decide, then let them know","We'd want their agreement","Their view should carry real weight"] },
    { id:"il12", type:"choice", label:"What stays between us", text:"How much of your marriage should stay between the two of you?", options:["Most things stay between us","Big things stay private; everyday things are fine to share","We'd share most things with family"] },
    { id:"il4", type:"text", text:"What boundary with your own family are you ready to keep once married, even if it causes tension?", placeholder:"Naming this now means your spouse isn't fighting alone later.", example:"e.g. 'I will not allow my parents to speak critically about my spouse in front of me without me addressing it in the moment.'" },
    // When it's hard
    { id:"il1", type:"choice", label:"When they clash", text:"If your parent and your spouse clash, what would you most likely do first?", options:["Back my spouse, then talk to my parent privately","Try to mediate between them","Step back and let them sort it out","Side with my parent out of respect"] },
    { id:"il3", type:"multi", label:"Hard to live with", text:"Which of these would be hard for you to live with?", options:["Daily calls or messages from parents","Expected weekly visits","Requests for money","Opinions on how we raise our children","Unasked-for advice about our marriage","Different expectations of husbands and wives","A parent living with us long-term"], hint:"Pick any." },
    { id:"il13", type:"text", label:"If my family hurts you", text:"If your spouse ever felt hurt by your family, how would you want them to tell you?", placeholder:"A sentence or two." },
  ]},
  { id:"nikah", icon:"🕌", title:"Nikah & Relationship Charter", sub:"Intention, commitments & how we'll treat each other", tag:"Foundation", color:"#2D5A44", questions:17, qs:[
    // Intention
    { id:"n3", type:"text", label:"Intention", text:"What is your intention (niyyah) for marriage?", placeholder:"A sentence or two.", example:"e.g. 'I want our home to be where both of us feel safe enough to be weak. That's the version of marriage I'm seeking.'" },
    { id:"n2", type:"text", label:"Non-negotiables", text:"Name up to three things — in faith or daily life — you couldn't compromise on in a marriage.", placeholder:"Up to three, briefly.", example:"e.g. 'An alcohol-free home. Praying Fajr. Honesty about money.'" },
    // Commitments
    { id:"n1", type:"multi", label:"Commitments", text:"Which commitments matter most to you?", options:["Honesty, even when it's uncomfortable","Asking before assuming","Protecting each other's dignity and privacy","Talking to each other directly, not through others","No suspicion or emotional games","Never using love as leverage","Covering each other's faults, not exposing them","Deciding big things together (shura)","Putting Allah before our egos","Praying for each other","Asking a trusted elder or counselor when we're stuck"], hint:"Pick any." },
    { id:"n4", type:"choice", label:"Mahr", text:"How important is it to you that mahr is discussed before the nikah?", options:["Very important — we should talk about it ourselves","I'd rather our families handle it","Not a priority for me"], hint:"This only asks how you'd like the conversation to happen. Mithaq doesn't ask about amounts." },
    // Communication
    { id:"n5", type:"multi", label:"Feeling cared for", text:"I feel most cared for when my spouse…", options:["Listens without rushing to fix","Tells me in words","Shows up through actions","Gives thoughtful gifts","Is affectionate","Gives me uninterrupted time"], hint:"Pick any." },
    { id:"n17", type:"choice", label:"Decisions without me", text:"How do you feel when a decision that affects you is made without asking you?", options:["It doesn't bother me much","It stings, but I'd let it go","It would really hurt me"], hint:"This is about how it feels, not who should decide." },
    { id:"n6", type:"choice", label:"Check-ins", text:"How often would you want a proper check-in about how you're both doing?", options:["Weekly","Every couple of weeks","Monthly","Only when something comes up"] },
    // Disagreement & repair
    { id:"n7", type:"multi", label:"In a disagreement", text:"In a disagreement, which of these would you commit to?", options:["No raised voices or name-calling","Taking a break, and agreeing when we'll come back","Pausing to make wudu before it escalates","No serious talks late at night","Not going to sleep without a check-in","Making things right within a day"], hint:"Pick any." },
    { id:"n8", type:"choice", label:"Making up", text:"After a disagreement, how soon do you usually want to make up?", options:["Within the hour","The same day","After a night's sleep","Once I've had real time to think"] },
    { id:"n14", type:"choice", label:"Polygyny", text:"How do you feel about polygyny for your own marriage?", options:["Not something I could accept","Not something I'm looking for","I'm open to it","I'd want to talk about it in person"], hint:"For many couples this isn't on the table at all, and a quick answer is fine — it's here so no one is surprised later. This asks how you feel about your own marriage, not your view of what Islam permits. Private: your partner only sees whether you see this the same way." },
    { id:"n9", type:"choice", label:"When we're stuck", text:"If you two got stuck on something serious, who would you turn to first?", options:["An imam","A Muslim counselor","A trusted elder in the family","A couple we both respect","We'd work it out ourselves"] },
    // Spiritual connection
    { id:"n10", type:"multi", label:"Spiritual closeness", text:"I feel closest to my spouse spiritually when they…", options:["Remind me of Allah gently","Make du'a for me","Pray with me","Read Qur'an or learn with me","Are gentle with me when I'm weak"], hint:"Pick any." },
    { id:"n11", type:"choice", label:"Praying together", text:"Praying together at home would ideally be…", options:["Whenever we're both home","Once a day","Now and then","Not something I picture"] },
    // Home & deen
    { id:"n12", type:"choice", label:"Fiqh at home", text:"When a fiqh question comes up at home, how would you want to decide?", options:["Follow one madhhab","Ask a scholar we both trust","Look at the evidence together","I haven't thought about it"] },
    { id:"n13", type:"text", label:"First-week habit", text:"Name one Islamic habit you'd want in your home from the first week.", placeholder:"Just one." },
    // The nikah contract
    { id:"n15", type:"choice", label:"Conditions in the contract", text:"Would you want to talk about adding conditions (shurut) to the nikah contract?", options:["Yes, I'd like to","Open to it if my spouse wants","I'd rather keep it simple","I don't know much about this yet"] },
    { id:"n16", type:"choice", label:"Knowing our rights", text:"How do you feel about learning each other's rights around divorce and khula before the nikah?", options:["It's wise to understand them","Just the basics","I'd rather not — it feels like planning to fail"] },
  ]},
  { id:"financial", icon:"💼", title:"Financial Charter", sub:"How money will actually run in your home", tag:"Practical", color:"#3D4E2A", questions:14, qs:[
    // Running the money
    { id:"f1", type:"choice", label:"Managing money", text:"Who do you picture managing the household finances day to day?", options:["Mostly the husband","Mostly the wife","Together","Split by category — one does bills, the other savings"] },
    { id:"f2", type:"choice", label:"Accounts", text:"How would you set up your accounts?", options:["Joint for everything","Joint plus personal accounts","Mostly separate, fully transparent"] },
    { id:"f4", type:"choice", label:"Budgeting", text:"How do you like to handle a monthly budget?", options:["A detailed budget by category","Rough spending limits","No formal budget — just keeping each other aware"] },
    { id:"f5", type:"choice", label:"Talking over purchases", text:"Above what amount should a purchase be talked over first?", options:["Under $100","$100–$500","$500–$1,000","Over $1,000"] },
    { id:"f6", type:"choice", label:"Money check-ins", text:"How often would you want a money check-in together?", options:["Weekly","Monthly","Every few months","Only before big decisions"] },
    // Roles & home
    { id:"f7", type:"choice", label:"Household costs", text:"How do you picture covering the household's costs?", options:["Husband covers them","Mostly the husband — wife contributes if she wants","Both, in proportion to income","Both, equally","Mostly the wife"] },
    { id:"f8", type:"choice", label:"Rent or buy", text:"In the first years, would you rather rent or buy?", options:["Rent","Buy as soon as we can","Depends where we live"] },
    { id:"f9", type:"choice", label:"Interest", text:"How do you feel about mortgages or loans with interest?", options:["Avoid completely — halal financing or saving","Only if there's truly no alternative","Comfortable using them","Still learning about this"] },
    // Giving & family
    { id:"f10", type:"choice", label:"Zakat & sadaqah", text:"How should zakat and regular sadaqah be handled?", options:["Together, as a household","Each of us handles our own","Each pays our own zakat; sadaqah together"] },
    { id:"f11", type:"choice", label:"Helping family", text:"Do you expect to help your parents or siblings with money?", options:["Yes, regularly","Sometimes, when needed","Not likely","They're more likely to help us"] },
    { id:"f12", type:"choice", label:"Sending money to family", text:"When one of you wants to send money to family, how should it be decided?", options:["Each decides for their own family","Talk it over first","Agree a set amount together"] },
    // Planning ahead
    { id:"f13", type:"choice", label:"Education savings", text:"Saving for children's education:", options:["Start early","Once we're settled","Not a priority"] },
    { id:"f14", type:"multi", label:"If money gets tight", text:"If money ever got tight, what would you want to do first?", options:["Cut back together","Take on more work","Draw on savings","Ask family for help"], hint:"Pick any." },
    { id:"f3", type:"text", label:"Debts & commitments", text:"What debts or regular commitments would you bring into the marriage?", placeholder:"Kinds and rough amounts are enough.", example:"e.g. 'About $28k in student loans, paid monthly. No credit card debt. I send some money to my parents each month.'" },
  ]},
  { id:"mental", icon:"🧠", title:"Mental Health Charter", sub:"Hard seasons, support & emotional care", tag:"Wellbeing", color:"#4A3728", questions:10, reframe:"mental", qs:[
    // How you carry stress
    { id:"m4", type:"multi", label:"When stress builds", text:"When stress builds over weeks, what tends to happen to you?", options:["I go quiet","I get irritable or short","My sleep or eating changes","I keep busy and push through","I pull away from people","I turn more to prayer and du'a","I talk it through with someone"], hint:"Pick any." },
    { id:"m5", type:"text", label:"Early signs", text:"What might your spouse notice first when you're struggling?", placeholder:"A sentence or two — the small signs." },
    { id:"m6", type:"choice", label:"Telling someone", text:"When something has been weighing on you, how soon do you usually tell someone close?", options:["Right away","After a few days","Only once it's serious","I usually keep it to myself"] },
    // When you're low
    { id:"m7", type:"multi", label:"What helps", text:"During a hard stretch, what helps most from a spouse?", options:["Quiet presence","Words of encouragement","Help solving the problem","Space alone","Islamic reminders and du'a","Practical help (meals, errands)"], hint:"Pick any." },
    { id:"m8", type:"text", label:"What doesn't help", text:"What doesn't help, even when it's meant kindly?", placeholder:"A sentence or two." },
    { id:"m2", type:"text", label:"Emotional needs", text:"What do you want your future spouse to understand about your emotional needs?", placeholder:"Be honest — this creates safety...", example:"e.g. 'When I'm overwhelmed I need 30 minutes of silence before I can talk. Asking me what's wrong before that window closes makes it worse.'" },
    // Supporting each other
    { id:"m9", type:"choice", label:"If my spouse is struggling", text:"How would you feel if your spouse were struggling for weeks and didn't want to talk about it?", options:["I'd give them space and wait","I'd gently keep checking in","I'd worry and push to talk","I'm honestly not sure"] },
    { id:"m10", type:"choice", label:"Counseling", text:"If either of you needed counseling or therapy, how would you feel about it?", options:["Very open","Open, especially with a Muslim counselor","Open if things got serious","I'd find it hard"] },
    // Faith and mental health
    { id:"m11", type:"choice", label:"Faith & mental health", text:"How do faith and mental health fit together for you?", options:["Hand in hand — faith and therapy both help","I'm still learning about this","I find them hard to reconcile","I haven't thought much about it"] },
    // Something to share in person — a flag, never details (deliberately not private)
    { id:"m13", type:"choice", label:"Something to share in person", text:"Is there anything about your mental or emotional health you'd want your spouse to understand before marriage?", options:["No","Yes — I'd like to tell you in person","Not sure"], hint:"Only your choice is shared, never any details. It just tells your partner a conversation is needed." },
  ]},
  { id:"career", icon:"🎯", title:"Career & Ambition Charter", sub:"Work, purpose & lifestyle balance", tag:"Practical", color:"#2A3B4E", questions:17, qs:[
    // Your work
    { id:"ca2", type:"text", label:"Current path", text:"What is your career path or ambition right now?", placeholder:"Where are you, and where are you trying to go?" },
    { id:"ca1", type:"text", label:"Purpose & work", text:"How does your work connect to your sense of purpose?", placeholder:"A sentence or two." },
    { id:"ca3", type:"choice", label:"Career centrality", text:"How central is your work to your identity or self-worth?", options:["It's core to who I am","It's important but not everything","It's a means to support what matters","I'm actively trying to separate the two"] },
    { id:"ca4", type:"choice", label:"Trajectory", text:"Are you aiming to climb, stabilize, or simplify your career long-term?", options:["Climb — I want to advance significantly","Stabilize — I want consistency and sustainability","Simplify — I want to scale back over time","Still figuring it out"] },
    { id:"ca17", type:"choice", label:"Work spillover", text:"How often does your work spill into evenings, weekends or travel?", options:["Rarely","Sometimes","Often","It's unpredictable"] },
    // Time & balance
    { id:"ca5", type:"text", label:"Time balance", text:"In a normal week, how would you like your time to split between work, family, faith and rest?", placeholder:"Be honest about what you actually want, not what sounds good." },
    { id:"ca13", type:"text", label:"Protecting the marriage", text:"What boundaries are needed to protect your relationship from overwork?", placeholder:"e.g. No work talk after Maghrib. We celebrate every milestone together.", example:"e.g. 'If work overwhelms the family, we revisit the balance immediately.'" },
    // Earning
    { id:"ca6", type:"choice", label:"If the wife earns more", text:"How would you feel if the wife earned more than the husband?", options:["Completely fine","Fine, with some adjustment","It would be hard for me","I haven't thought about it"] },
    { id:"ca7", type:"text", label:"Earning gap", text:"If one of you earned much less than the other, how do you think it would feel — for each of you?", placeholder:"Be honest about your feelings, not your ideals." },
    { id:"ca8", type:"choice", label:"Work arrangement", text:"How do you picture both of you working?", options:["Both full-time","One full-time, one part-time","One works, the other runs the home","Flexible, depending on life stage"] },
    // Children & pauses
    { id:"ca14", type:"choice", label:"Children & work", text:"When children come, whose work do you picture changing first?", options:["The wife's","The husband's","Both of ours, a little","Neither — we'd arrange childcare"] },
    { id:"ca10", type:"text", label:"What feels fair", text:"What would feel fair to you about how work changes when children come?", placeholder:"A sentence or two." },
    { id:"ca15", type:"choice", label:"Career pauses", text:"How would you feel about one of you pausing work for a while — for children, health or study?", options:["Fully supportive","Supportive, if we can afford it","It would be hard for me"] },
    // Moves & faith
    { id:"ca9", type:"choice", label:"Relocation", text:"If one of you got a great job in another city, how would you feel about moving?", options:["I'd move gladly","I'd consider it seriously","Only for a short time","I'd rather not move"] },
    { id:"ca16", type:"choice", label:"Work & faith", text:"Would you leave a well-paid job if it conflicted with your faith — riba, haram products?", options:["Yes","Only with something else lined up","It depends","No"] },
    // Support
    { id:"ca11", type:"text", label:"Burnout support", text:"If you burned out or wanted to change careers, how would you want your spouse to respond?", placeholder:"How do you want to be treated in that moment?" },
    { id:"ca12", type:"text", label:"Validation needs", text:"What kind of validation or reassurance do you need from your spouse about work?", placeholder:"Do you need them to show interest, celebrate wins, give space?" },
  ]},
  { id:"cultural", icon:"🌍", title:"Cultural Identity Charter", sub:"Heritage, family & where you'll make a home", tag:"Identity", color:"#2A4A4E", questions:15, qs:[
    // Who you are
    { id:"cu1", type:"text", label:"Identity", text:"How do you primarily identify culturally?", placeholder:"e.g. Turkish, Desi-American, Somali, Arab, African American, Revert..." },
    { id:"cu2", type:"multi", label:"Upbringing", text:"I was raised in:", options:["A deeply traditional culture","A blended or multicultural home","A Western secular culture","A faith-centered household with cultural pride"] },
    // Culture at home
    { id:"cu3", type:"choice", label:"Culture at home", text:"What place do you want your culture to have in your home?", options:["Central","Shared equally with my spouse's culture","Light — with faith at the center"] },
    { id:"cu4", type:"text", label:"Cultural practices", text:"Which cultural practices would feel like a loss if they disappeared from your home?", placeholder:"Food, language, dress, holidays…" },
    { id:"cu5", type:"choice", label:"Culture & Islam", text:"How should cultural customs fit with Islam?", options:["Welcome, as long as they don't contradict Islam","Kept only if they fit Islamic values","Kept light — I'd rather keep things faith-centered","Celebrated as part of who we are"] },
    // Your families
    { id:"cu16", type:"choice", label:"Family & a different culture", text:"How would your family feel about you marrying someone from a different culture?", options:["Welcoming","Some adjustment needed","Real resistance","We share the same culture"] },
    { id:"cu13", type:"text", label:"Pressure points", text:"Are there pressures in your family around caste, race, lineage, or marrying outside the culture?", placeholder:"Name them honestly — silence here becomes conflict later." },
    { id:"cu17", type:"choice", label:"Wedding expectations", text:"What will your family likely expect for the wedding?", options:["A big traditional wedding","Something modest","They'll follow our lead","I don't know yet"] },
    { id:"cu19", type:"choice", label:"Non-Muslim family", text:"If you have non-Muslim family, how do you picture your relationship with them as a couple?", options:["Close — we'd visit and include them","Warm, with some boundaries around celebrations","Mostly distant","This doesn't apply to me"] },
    // Where you'll live
    { id:"cu8", type:"choice", label:"Long-term home", text:"Where do you picture living long-term?", options:["Stay in the US","Move to another country someday","Split time between countries","Open — wherever life takes us"] },
    { id:"cu15", type:"choice", label:"Visiting home", text:"How often would you want to visit your family's home country?", options:["Every year","Every few years","Rarely","It isn't part of my life"] },
    { id:"cu18", type:"choice", label:"Legal status", text:"Is there anything about your legal status or paperwork that could affect where or how we live?", options:["No","Yes — I'd like to tell you in person","Not sure"], hint:"Only your choice is shared, never any details. It just tells your partner a conversation is needed." },
    // Children
    { id:"cu9", type:"text", label:"Children's languages", text:"What languages do you want your children to speak fluently?", placeholder:"A sentence is enough." },
    { id:"cu10", type:"text", label:"What to pass on", text:"What from your culture would you most want your children to carry — a value, a story, a custom?", placeholder:"What do you want them to know about where they come from?" },
    { id:"cu11", type:"text", label:"Confident identity", text:"What would help your children feel confident being both Muslim and at home in Western society?", placeholder:"A sentence or two." },
  ]},
  { id:"joy", icon:"🎭", title:"Joy, Play & Humor Charter", sub:"Laughter, rest & shared delight", tag:"Lifestyle", color:"#3D2A4E", questions:14, qs:[
    // How you play
    { id:"jo1", type:"multi", label:"Playful side", text:"My playful side shows through:", options:["Goofy humor or impressions","Witty or dry banter","Games, puzzles, or sports","Music, dancing, or mimicry","Pranks or memes","Deep storytelling with funny twists","I'm still learning how to play more freely"] },
    { id:"jo2", type:"multi", label:"Humor style", text:"Humor I usually enjoy:", options:["Family-friendly / Clean","Sarcastic / Dark","Absurd / Random","Observational / Intelligent","Physical comedy","None / I'm not drawn to comedy much"] },
    { id:"jo3", type:"text", label:"Favorite content", text:"What are your favorite funny movies, comedians, or content creators?", placeholder:"What makes you genuinely laugh?" },
    { id:"jo14", type:"choice", label:"Being silly together", text:"How do you feel about being silly together — nicknames, racing each other, inside jokes?", options:["Love it","Yes, in private","Not really me"] },
    // Rest & weekends
    { id:"jo4", type:"text", label:"Saturday morning", text:"Describe your ideal Saturday morning — what's happening in the house?", placeholder:"Early riser or sleeping in? Quiet or lively? Solo or together?", example:"e.g. 'Qur'an on low, coffee, no rush, both of us in pajamas doing our own thing until noon.'" },
    { id:"jo5", type:"multi", label:"Ideal weekend", text:"What would make the weekend feel rejuvenating and joyful?", options:["Outdoor adventures (hiking, markets, road trips)","Cozy in bed with movies/books","Exploring new restaurants or halal eats","Serving the community together","Hosting friends or game night","Doing absolutely nothing — just being"] },
    { id:"jo13", type:"choice", label:"Time for fun", text:"How often would you want a day or evening set aside just for fun together?", options:["Weekly","Every couple of weeks","Monthly","Whenever it happens"] },
    { id:"jo6", type:"multi", label:"Recharge activity", text:"What's your zone-out activity when you need to unwind?", options:["Gaming / Streaming","Watching sports","Reading / Podcasts","Walking / Driving","DIY hobbies","Just scrolling / social media"] },
    { id:"jo7", type:"choice", label:"Unwinding time", text:"How would you want your spouse to treat your unwinding time?", options:["Leave me to it","Join me sometimes","Help me keep it balanced","Ask before interrupting"] },
    // Sharing joy
    { id:"jo8", type:"text", label:"Playful activities", text:"What makes you feel playful or young again?", placeholder:"Things that bring out the lighter side of you." },
    { id:"jo9", type:"text", label:"Shared shows", text:"What shows or films would you happily watch together?", placeholder:"Be honest — not what sounds cultured, what you'd actually watch." },
    { id:"jo10", type:"text", label:"Places of lightness", text:"What places make you feel free or light — nature, cities, cafés, libraries?", placeholder:"Where do you exhale?" },
    { id:"jo12", type:"text", label:"Something to try", text:"What's something you enjoy that you'd love your spouse to try with you?", placeholder:"A sentence is enough." },
    { id:"jo11", type:"text", label:"Laughing together", text:"What's made you laugh together so far?", placeholder:"Or if it's early days — what made you laugh the first time?" },
  ]},
  { id:"unspoken", icon:"🚩", title:"Unspoken Realities Charter", sub:"Daily life at home, affection & trust", tag:"Sensitive", color:"#4E2A2A", questions:30, reframe:"unspoken", qs:[
    // Personal care
    { id:"un1", type:"choice", label:"Daily freshness", text:"How important is daily freshness to you — a shower, deodorant, feeling clean?", options:["Very important","Important, but flexible","Not something I think about"] },
    { id:"un2", type:"choice", label:"Fresh breath", text:"How much do fresh breath and oral care matter to you in close moments?", options:["Very much","Somewhat","Not much"] },
    { id:"un3", type:"choice", label:"Scents", text:"Strong perfumes and scents feel…", options:["Lovely","Fine in moderation","Overwhelming"] },
    { id:"un4", type:"choice", label:"Fresh clothes", text:"Clean, fresh clothes every day are…", options:["Essential","Nice","Not a big deal"] },
    // Home & chores
    { id:"un6", type:"choice", label:"Cooking", text:"Who do you picture doing most of the cooking?", options:CHORE_OPTIONS },
    { id:"un7", type:"choice", label:"Laundry", text:"Who do you picture doing most of the laundry?", options:CHORE_OPTIONS },
    { id:"un8", type:"choice", label:"Dishes", text:"Who do you picture doing most of the dishes?", options:CHORE_OPTIONS },
    { id:"un21", type:"choice", label:"Bathroom cleaning", text:"Who do you picture doing most of the bathroom cleaning?", options:CHORE_OPTIONS },
    { id:"un22", type:"choice", label:"Running the household", text:"Who do you picture keeping track of the household — groceries, bills, appointments?", options:CHORE_OPTIONS },
    { id:"un9", type:"choice", label:"Clutter", text:"How much does clutter or mess around the house bother you?", options:["Really bothers me","Somewhat","Not much"] },
    // Intimacy & affection — the 🔒 ones are compared privately (PRIVATE_QUESTIONS)
    { id:"un23", type:"choice", label:"Intimacy & feeling loved", text:"How important is physical intimacy in how you feel loved?", options:["Very important","Important","Less than other things"], hint:"Private: your partner only sees whether you see this the same way." },
    { id:"un15", type:"choice", label:"Everyday affection", text:"Affection outside of intimacy — a hug, holding hands, sitting close — is…", options:["Essential","Nice to have","Not my way of showing love"], hint:"Private: your partner only sees whether you see this the same way." },
    { id:"un24", type:"choice", label:"Talking about intimacy", text:"How comfortable are you talking about intimacy with your spouse?", options:["Very comfortable","I'd like to be, with time","It's hard for me"], hint:"Private: your partner only sees whether you see this the same way." },
    { id:"un25", type:"multi", label:"Raising a concern", text:"If something about intimacy isn't working, how would you want it raised?", options:["Directly, at a calm time","Gently, with reassurance first","In writing first","With time to think before we talk","With a counselor's help"], hint:"Pick any. Private: your partner only sees whether you see this the same way." },
    { id:"un26", type:"choice", label:"The first months", text:"The first months of intimacy will probably…", options:["Come naturally","Take patience and learning together","Not sure"], hint:"Private: your partner only sees whether you see this the same way." },
    { id:"un27", type:"multi", label:"Different needs or pace", text:"If your needs or pace differ, what would help most?", options:["Talking openly","Learning together","Giving it time","A trusted counselor"], hint:"Pick any. Private: your partner only sees whether you see this the same way." },
    { id:"un28", type:"choice", label:"Islamic guidance", text:"Have you learned what Islam teaches about intimacy in marriage?", options:["Yes","Some","Not yet — I'd like to learn together"] },
    { id:"un29", type:"choice", label:"Health or body", text:"Is there anything about your health or body you'd want your spouse to understand before marriage?", options:["No","Yes — I'd like to tell you in person","Not sure"], hint:"Only your choice is shared, never any details. It just tells your partner a conversation is needed." },
    { id:"un30", type:"choice", label:"Public affection", text:"Affection in public:", options:["Comfortable","Small gestures only","Prefer none in public"] },
    // Digital trust
    { id:"un16", type:"choice", label:"Phone passwords", text:"Would you share phone passwords with your spouse?", options:["Yes","No","Only if needed"] },
    { id:"un31", type:"choice", label:"Each other's phones", text:"Looking at each other's phones:", options:["Never without asking","Fine anytime","Only if something feels wrong — and we talk first"] },
    { id:"un17", type:"choice", label:"Opposite-gender friendships", text:"Friendships with the opposite gender, for either of us:", options:["Professional only","Friendly, with clear boundaries","Better avoided"] },
    { id:"un18", type:"text", label:"When I feel insecure", text:"When something makes you feel insecure, what do you usually do now?", placeholder:"A sentence or two — what actually happens." },
    { id:"un32", type:"text", label:"What I'd want to do", text:"And what would you want to do instead — or keep doing?", placeholder:"A sentence or two." },
    // Daily rhythms
    { id:"un19", type:"choice", label:"Recharge style", text:"I recharge best:", options:["With people","Alone","With my spouse"] },
    { id:"un33", type:"choice", label:"Alone time", text:"How much alone time do you need?", options:["Daily","A few times a week","Rarely"] },
    { id:"un34", type:"multi", label:"After a hard day", text:"After a hard day, what helps you most?", options:["Quiet","Talking it through","Affection","Food & comfort","Movement"], hint:"Pick any." },
    { id:"un35", type:"multi", label:"Habits that wear on me", text:"Which small habits would wear on you most over time?", options:["Interrupting","Leaving mess around","Running late","Sarcasm","Phones during time together","Pulling away instead of talking"], hint:"Pick any." },
    { id:"un20", type:"text", label:"Being told something bothered", text:"How would you want your spouse to tell you that something you did bothered them?", placeholder:"Tone, timing, words — what helps you hear it?" },
    // Faith in daily life
    { id:"un36", type:"choice", label:"Kindness as deen", text:"Kindness in small daily things at home is part of our deen.", options:["Strongly agree","Somewhat","Haven't thought about it"] },
  ]},
  { id:"aging", icon:"🧓", title:"Aging & End-of-Life Charter", sub:"Planning with faith & clarity", tag:"Legacy", color:"#1C3A38", questions:12, qs:[
    // Growing older
    { id:"ag1", type:"multi", label:"Aging vision", text:"I imagine growing older as:", options:["A shared spiritual journey","A season of slowing down and simplifying","A time to mentor and pass on wisdom","Scary or uncertain — I struggle thinking about it"] },
    { id:"ag2", type:"multi", label:"Old age location", text:"Where would you want to live in old age?", options:["Near family","In a Muslim community","Overseas"], hint:"Pick any." },
    { id:"ag3", type:"text", label:"Caring for each other", text:"If one of you developed a long-term illness, disability or memory loss, how do you picture caring for each other?", placeholder:"What does care look like in your vision of marriage?" },
    // Caring for parents
    { id:"ag12", type:"choice", label:"Parents now", text:"Are either of your parents unwell or relying on you now?", options:["No","Somewhat","Yes, significantly"] },
    { id:"ag13", type:"choice", label:"Caring for a parent", text:"If a parent needed daily care later in life, how would you want to handle it?", options:["They'd live with us","We'd care for them nearby","We'd arrange paid care","Siblings would share it"] },
    { id:"ag6", type:"text", label:"Both sets of parents", text:"If both sets of parents needed help at the same time, how would you share it fairly?", placeholder:"What feels fair to you?" },
    // Practical planning
    { id:"ag14", type:"multi", label:"Planning ahead", text:"Which of these do you have, or plan to set up?", options:["An Islamic will","Healthcare power of attorney","Life insurance","A burial fund or savings"], hint:"Pick any." },
    { id:"ag7", type:"choice", label:"Burial location", text:"Where would you want to be buried?", options:["Where we live","My family's home country","Wherever I pass away"] },
    { id:"ag8", type:"multi", label:"Janazah wishes", text:"For your janazah, what matters most to you?", options:["A quick, simple burial by the Sunnah","Family from abroad being able to attend","Costs covered so they don't burden my family","A local imam or community leading it"], hint:"Pick any." },
    // If one of you goes first
    { id:"ag9", type:"text", label:"After passing", text:"What should your spouse know or expect if you went first?", placeholder:"What would you want them to know if you went first?", example:"e.g. 'I want you to remarry, but only when you feel whole. Give sadaqah in my name from our savings.'" },
    { id:"ag10", type:"text", label:"Legacy communication", text:"How would you want to leave love and reassurance behind?", placeholder:"A letter, du'a list, inheritance plan, video for children?", example:"e.g. 'I want to record video messages for our children for major milestones in case I'm not there.'" },
    { id:"ag11", type:"multi", label:"Life goals", text:"Before we die, we want to:", options:["Go to Hajj / Umrah","Memorize Qur'an or key surahs","Raise God-conscious children","Establish sadaqah jariyah","Seek forgiveness from family or friends","Serve orphans, the elderly, or converts","Write letters to our children"] },
  ]},
];


const SHORT_DATE = new Date().toLocaleDateString("en-US",{month:"short",year:"numeric"});

const ONBOARD_SLIDES = [
  { icon:"🤝", label:"The problem we're solving", title:"The Muslim marriage process has been broken for a generation.", body:"Not because the values are wrong. Because the conversations got too shallow. A name, a career, a photo, a family background — and then two people are expected to decide if they want to build a life together. Something important got lost.", cta:"Tell me more →" },
  { icon:"🪞", label:"What this is", title:"This is not a dating profile. It's a self-portrait.", body:"Nobody is going to grade your answers. The goal isn't to sound good — it's to know yourself well enough that the right person recognizes you, and the wrong person self-selects out.\n\nIf you perform and attract someone based on a version of you that isn't real, you've just signed up for an exhausting performance for years.", cta:"Keep going →" },
  { icon:"📅", label:"Answers have an expiry date", title:"Everything you write here is \"true as of today.\"", body:"Your answers will be timestamped. Not because you'll be held to them forever — because you won't. People change. Circumstances shift. Faith grows.\n\nEvery 90 days, Mithaq will ask: does this still feel true? Updating your answers is a feature — it shows self-awareness, not inconsistency.", cta:"One more →" },
  { icon:"✋", label:"You're allowed to not know", title:"\"I'm still working through this\" is a complete answer.", body:"For every question in this app, you have three options:\n\n① Answer honestly\n② Say you're still working through it\n③ Leave it for later\n\nChoosing option ② isn't a gap in your profile. On this platform, it's a signal of self-awareness — and that is one of the most attractive qualities a person can bring to a marriage.", cta:"I'm ready to begin →" },
];

// ─── CONFIDENTIALITY COMMITMENT TEXT ─────────────────────────────────────────
const CONFIDENTIALITY_TEXT = {
  title: "Before you share this profile",
  body: "What you're about to share was built in private — through honest reflection and real vulnerability. The person who made it trusted this process with things they may never have said out loud before.\n\nAs someone receiving it, you are being invited in. That is not a small thing.",
  commitments: [
    "I will read this with the same care it was written with",
    "I will not share it, forward it, or discuss its contents with others",
    "I will let my own impressions form before seeking anyone else's opinion",
    "I understand that what I feel about this person is mine to feel — and that no one else's judgment should reach them before my own does",
  ],
  cta: "I understand and I commit to this →",
  sub: "This isn't a legal agreement. It's a values commitment — the kind that matters more.",
};

// ─── QUESTION LOOKUP MAP ──────────────────────────────────────────────────────
const QUESTION_MAP = {};
CHARTERS.forEach(charter => {
  if (charter.qs) {
    charter.qs.forEach(q => {
      QUESTION_MAP[`${charter.id}__${q.id}`] = q.text || q.label || q.id;
    });
  }
});

// ─── QUESTION & ANSWER HELPERS ────────────────────────────────────────────────
const QUESTION_INFO = {};   // "section__qid" → question object
const SECTION_TITLES = { story: "🪞 Your Story", growth: "🌱 Growth Portfolio" };
STORY_QS.forEach(q => { QUESTION_INFO[`story__${q.id}`] = q; });
GROWTH_QS.forEach(q => { QUESTION_INFO[`growth__${q.id}`] = q; });
CHARTERS.forEach(c => {
  SECTION_TITLES[c.id] = `${c.icon} ${c.title}`;
  (c.qs || []).forEach(q => { QUESTION_INFO[`${c.id}__${q.id}`] = q; });
});

// Questions compared by alignment only — a partner's exact answer is never
// sent. The database holds the same list (migration 008: private_questions).
const PRIVATE_QUESTIONS = new Set([
  "unspoken__un23", "unspoken__un15", "unspoken__un24", "unspoken__un25", "unspoken__un26", "unspoken__un27",
  "parenting__p9", "nikah__n14",
]);
// "Tell you in person" flags: shown to the partner, never on shared links,
// and never given a note (they must never hold details). The database holds
// the same list (migration 015: partner_only_questions).
const PARTNER_ONLY = new Set(["unspoken__un29", "mental__m13", "cultural__cu18", "readiness__q18", "readiness__q19"]);
// Optional "say more" note under a charter's multiple-choice questions.
// Notes on private questions are never shown to the partner (migration 024).
const NOTE_MAX = 500;
const noteModeFor = (sectionId, q) => {
  if (!q || q.type === "text") return null;
  const k = `${sectionId}__${q.id}`;
  if (PARTNER_ONLY.has(k)) return null;
  return PRIVATE_QUESTIONS.has(k) ? "private" : "shared";
};
const isAlign = (v) => !!v && typeof v === "object" && !Array.isArray(v) && "__align" in v;
const ALIGN_TEXT = {
  same: "You see this the same way.",
  overlap: "You see this partly the same way.",
  different: "You see this differently.",
  unanswered: "Not answered yet.",
};
const parseAns = (v) => {
  if (typeof v !== "string" || v === "__working__") return v;
  try { return JSON.parse(v); } catch (e) { return v; }
};
const formatAns = (v) => {
  if (v === "__working__") return "Still working through this";
  if (isAlign(v)) return `Answered privately — ${(ALIGN_TEXT[v.__align] || "").toLowerCase()}`;
  if (Array.isArray(v)) return v.join(", ");
  if (v && typeof v === "object") return Object.entries(v).map(([k, n]) => `${k}: ${n}/5`).join(" · ");
  return v == null ? "" : String(v);
};

// ─── PRIVATE NOTES ────────────────────────────────────────────────────────────
const NOTE_TAGS = [
  { id: "ask",        icon: "💬", label: "Ask more about this" },
  { id: "clarify",    icon: "❓", label: "Clarify what they meant" },
  { id: "appreciate", icon: "🤍", label: "Loved this" },
  { id: "concern",    icon: "⚠️", label: "A concern for me" },
];
const tagInfo = (id) => NOTE_TAGS.find(t => t.id === id) || NOTE_TAGS[0];

// Loads and saves the signed-in person's notes about one other person.
function useNotes(viewerId, subjectId, subjectName) {
  const [notes, setNotes] = useState({});
  useEffect(() => {
    if (!viewerId || !subjectId || viewerId === subjectId) return;
    supabase.from("notes").select("*").eq("subject_id", subjectId).then(({ data }) => {
      const m = {};
      (data || []).forEach(n => { m[`${n.section_id}__${n.question_id}`] = n; });
      setNotes(m);
    });
  }, [viewerId, subjectId]);

  const save = async (sectionId, qid, answerVal, tag, body) => {
    const key = `${sectionId}__${qid}`;
    const row = {
      author_id: viewerId, subject_id: subjectId, subject_name: subjectName || "",
      section_id: sectionId, section_title: SECTION_TITLES[sectionId] || sectionId,
      question_id: qid, question_text: QUESTION_INFO[key]?.text || qid,
      answer_text: formatAns(answerVal), tag, body,
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase.from("notes")
      .upsert(row, { onConflict: "author_id,subject_id,section_id,question_id" })
      .select().single();
    if (!error && data) setNotes(p => ({ ...p, [key]: data }));
    return error;
  };
  const remove = async (sectionId, qid) => {
    const key = `${sectionId}__${qid}`;
    const n = notes[key];
    if (!n) return;
    await supabase.from("notes").delete().eq("id", n.id);
    setNotes(p => { const c = { ...p }; delete c[key]; return c; });
  };
  return { notes, save, remove };
}

// A small "add a note" control that sits under an answer.
function NoteControl({ note, onSave, onDelete, dark }) {
  const [open, setOpen] = useState(false);
  const [tag, setTag] = useState(note?.tag || "ask");
  const [body, setBody] = useState(note?.body || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const begin = () => { setTag(note?.tag || "ask"); setBody(note?.body || ""); setErr(""); setOpen(true); };
  const save = async () => {
    setSaving(true); setErr("");
    const error = await onSave(tag, body.trim());
    setSaving(false);
    if (error) setErr("Couldn't save — please try again"); else setOpen(false);
  };
  const del = async () => { setSaving(true); await onDelete(); setSaving(false); setOpen(false); };

  const muted = dark ? tint(C.sand,0.7) : C.muted;
  const text = dark ? C.cream : C.text;
  const line = dark ? tint(C.sand,0.2) : C.parchment;
  const surface = dark ? "rgba(255,255,255,0.05)" : C.soft;

  if (!open) {
    if (!note) return (
      <button onClick={begin} className="no-print" style={{background:"none",border:"none",padding:"6px 0 0",color:muted,fontSize:12,letterSpacing:0.3}}>
        ＋ Add a note
      </button>
    );
    const t = tagInfo(note.tag);
    return (
      <button type="button" className="btn-card btn-stack" onClick={begin} style={{marginTop:8,padding:"8px 12px",background:surface,border:`1px solid ${line}`,borderRadius:8,cursor:"pointer"}}>
        <div style={{fontSize:12,color:dark?C.goldL:C.goldText,fontWeight:500}}>{t.icon} {t.label}{note.discussed ? " · ✓ discussed" : ""}</div>
        {note.body && <div style={{fontSize:12,color:text,marginTop:4,lineHeight:1.6}}>{note.body}</div>}
      </button>
    );
  }

  return (
    <div className="no-print" style={{marginTop:10,padding:14,background:surface,border:`1px solid ${dark?tint(C.gold,0.35):C.gold}`,borderRadius:10}}>
      <div style={{fontSize:12,color:muted,marginBottom:8}}>Private note — only you can see this</div>
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
        {NOTE_TAGS.map(t => (
          <button key={t.id} onClick={()=>setTag(t.id)} style={{fontSize:12,padding:"6px 10px",borderRadius:20,border:`1px solid ${tag===t.id?C.gold:line}`,background:tag===t.id?(dark?tint(C.gold,0.18):tint(C.gold,0.1)):"transparent",color:tag===t.id?(dark?C.goldL:C.green):text}}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      <textarea aria-label="Private note — only you can see this" value={body} onChange={e=>setBody(e.target.value)} rows={2} placeholder="Optional — what do you want to say or ask?"
        style={{width:"100%",fontSize:13,lineHeight:1.6,padding:"10px 12px",border:`1px solid ${line}`,borderRadius:8,background:dark?"rgba(0,0,0,0.15)":C.white,color:text,marginBottom:10}} />
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        <button disabled={saving} onClick={save} style={{background:G_GOLD,color:C.onGold,border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,fontWeight:600,opacity:saving?0.6:1}}>{saving?"Saving...":"Save note"}</button>
        <button onClick={()=>setOpen(false)} style={{background:"none",border:"none",color:muted,fontSize:13}}>Cancel</button>
        {note && <button disabled={saving} onClick={del} style={{background:"none",border:"none",color:muted,fontSize:13,marginLeft:"auto",textDecoration:"underline"}}>Remove</button>}
      </div>
      {err && <div style={{fontSize:12,color:dark?"#e8a0a0":C.rose,marginTop:8}}>{err}</div>}
    </div>
  );
}

const CHECKIN_MONTHS = 6;
const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const sameAns = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// ─── STAGES ───────────────────────────────────────────────────────────────────
// Deeper charters open as a couple moves forward together. The database
// enforces this (migration 007: question_stages) — keep the two in sync.
const STAGES = [
  { id: "getting_to_know", label: "Getting to know", short: "getting to know each other", icon: "🌱",
    sub: "Values, faith, vision, work and joy." },
  { id: "serious", label: "Serious", short: "serious", icon: "🤝",
    sub: "Money, culture, family, parenting and emotional health. You decide together what “serious” means for you." },
  { id: "engaged", label: "Engaged", short: "engaged", icon: "💍",
    sub: "Daily realities and intimacy, debts, later life — and your nikah summary." },
];
const MARRIED = { id: "married", label: "Married", short: "married", icon: "🕌", sub: "Your first year together." };
const stageRank = (id) => id === "married" ? 4 : Math.max(0, STAGES.findIndex(s => s.id === id)) + 1;
const stageInfo = (id) => id === "married" ? MARRIED : (STAGES.find(s => s.id === id) || STAGES[0]);
const atLeastEngaged = (id) => stageRank(id) >= 3;
const CHARTER_STAGES = {
  readiness: "getting_to_know", who_i_am: "getting_to_know", faith_journey: "getting_to_know",
  nikah: "getting_to_know", joy: "getting_to_know", career: "getting_to_know", physical: "getting_to_know",
  money_values: "serious", financial: "serious", cultural: "serious", mental: "serious",
  assumptions: "serious", conflict_history: "serious", parenting: "serious", inlaws: "serious",
  unspoken: "engaged", aging: "engaged",
};
const QUESTION_STAGES = {
  financial__f3: "engaged", parenting__p9: "engaged",
  nikah__n14: "serious", nikah__n15: "engaged", nikah__n16: "engaged",
};
const charterStage = (sectionId) => CHARTER_STAGES[sectionId] || "getting_to_know";
const questionStage = (sectionId, qid) => QUESTION_STAGES[`${sectionId}__${qid}`] || charterStage(sectionId);

// ─── GUIDED PATH ──────────────────────────────────────────────────────────────
// Why each part matters (shown on cards, intros and the next-step card), an
// intro for charters that don't have a reframe, and a note after finishing.
const SELF_GUIDE = {
  story:   { title: "Your Story", why: "Who you are beyond a profile — the first thing anyone should read about you.", minutes: 10 },
  growth:  { title: "Growth Portfolio", why: "Where you're headed, not just where you are today.", minutes: 8 },
  witness: { title: "Character Witness", why: "Someone who knows you well adds their voice to yours." },
};
const CHARTER_GUIDE = {
  readiness: {
    why: "The big picture first: faith, family, and what each of you thinks a good marriage is.",
    intro: { title: "Start with the big picture", body: "Before the details, it helps to know what each of you is actually building toward. This survey covers the foundations — the things every other conversation rests on." },
    after: "You've named your foundations. Every other charter builds on these.",
  },
  who_i_am: {
    why: "How you really are on an ordinary day — the person someone will actually live with.",
    after: "You've described the real you, not the polished one. That's what someone needs to know.",
  },
  faith_journey: {
    why: "Where your faith has been and where it's going, so neither of you has to guess.",
    intro: { title: "Faith is a journey, not a résumé", body: "Answer as you are today, not as you think a good Muslim should sound. Your spouse will live with the real you — let them meet that person here.\n\nTwo people rarely stand in exactly the same place in their deen, and they don't need to. What matters is understanding each other's path, and whether you can walk it together with kindness." },
    after: "Knowing each other's faith story makes it easier to support it, not judge it.",
  },
  nikah: {
    why: "Your intention for the marriage, and how you'll treat each other — in good days and in disagreements.",
    intro: { title: "Why are you marrying?", body: "This charter is about niyyah — what you hope your marriage will be in the sight of Allah — and the everyday commitments that protect it: how you talk, how you disagree, and how you grow in faith together." },
    after: "We are not perfect, but we are committed to loving with intention, correcting with compassion, and walking together toward Jannah, one step at a time.",
  },
  career: {
    why: "Who works, how much, and what happens when children come. It shapes every week of a marriage.",
    intro: { title: "Work shapes the whole household", body: "Who works, how much, and what happens when children arrive or someone wants to change paths — these decide how your days actually run. Many couples assume they agree and find out later they didn't." },
    after: "You've put your expectations about work into words. Most couples never do.",
  },
  joy: {
    why: "What makes you laugh and rest. A marriage needs lightness as much as agreement.",
    intro: { title: "Marriage needs joy too", body: "Not every conversation has to be heavy. How you play, rest and laugh is part of whether you'll enjoy each other's company for decades." },
    after: "Joy is a real part of compatibility, and you just took it seriously.",
  },
  physical: {
    why: "How attraction works for you — named honestly, and with dignity.",
    after: "You've been honest about something most people only hint at.",
  },
  money_values: {
    why: "What money meant in the home you grew up in, which shapes how you'll handle it in yours.",
    after: "Your history with money is on the page. That makes the practical conversations easier.",
  },
  financial: {
    why: "Who manages money, how accounts work, and what each of you owes — before it causes friction.",
    intro: { title: "The practical side of money", body: "Money is one of the most common sources of conflict in marriage — usually not because of how much there is, but because of unspoken assumptions about who decides. It's easiest to get on the table now." },
    after: "Clear money roles now prevent a lot of quiet resentment later.",
  },
  inlaws: {
    why: "Boundaries, loyalty and expectations with both families — the longest conversation in a marriage.",
    after: "You've named your family's expectations, so your spouse won't discover them alone.",
  },
  parenting: {
    why: "How you'd raise children together, decided calmly now rather than in a tired moment later.",
    after: "You've thought about the parent you want to be. That's where good parenting starts.",
  },
  cultural: {
    why: "Heritage, language and where you'll make a home — especially important when your backgrounds differ.",
    intro: { title: "Where you come from comes with you", body: "Culture shapes food, holidays, language, family roles and what feels normal. Naming it now helps you build a home where both of you belong." },
    after: "Your heritage is part of what you bring, and now it's named.",
  },
  mental: {
    why: "How each of you handles stress and hard feelings, and what you need from each other.",
    after: "Self-awareness is one of the most valuable things you can bring to a marriage.",
  },
  conflict_history: {
    why: "What past hurt taught you about how you act under pressure — so it doesn't repeat.",
    after: "Knowing your patterns is the first step to not repeating them.",
  },
  assumptions: {
    why: "Expectations you carry without noticing — the ones that cause the biggest surprises.",
    after: "What you've named, you can now talk about.",
  },
  unspoken: {
    why: "Daily habits, hygiene and intimacy — the small things that quietly decide how a home feels.",
    after: "Nothing here was too small to mention. You've just prevented a lot of quiet frustration.",
  },
  aging: {
    why: "Later life, caring for parents and your final wishes. Hard to raise, and a gift to have settled.",
    intro: { title: "Planning with faith for later life", body: "Caring for aging parents, illness, and what you want after you're gone are among the hardest things to bring up — and among the most loving to have discussed. Take your time with this one." },
    after: "You've spoken about things most couples avoid. That's a gift to each other.",
  },
};
const guideFor = (id) => CHARTER_GUIDE[id] || { why: "", after: "One more honest conversation, ready when you are." };
// The order charters are suggested in, within each stage.
const STAGE_ORDER = {
  getting_to_know: ["readiness", "nikah", "faith_journey", "who_i_am", "career", "joy", "physical"],
  serious: ["money_values", "financial", "inlaws", "parenting", "cultural", "mental", "conflict_history", "assumptions"],
  engaged: ["unspoken", "aging"],
};
const estMinutes = (qs) => {
  const m = (qs || []).reduce((t, q) => t + (q.type === "text" ? 2.5 : 0.75), 0);
  return Math.max(5, Math.round(m / 5) * 5);
};

// The one thing to do next, from where this person is.
function computeNextStep({ storyDone, growthDone, done, partnership }) {
  const connected = partnership?.status === "active";
  const pending = partnership?.status === "pending";
  const byId = (id) => CHARTERS.find(c => c.id === id);
  const charterStep = (id, extra) => {
    const c = byId(id);
    return { kind: "charter", charter: c, title: c.title, why: guideFor(id).why,
             cta: "Begin", minutes: estMinutes(c.qs), ...extra };
  };
  if (!storyDone) return { kind: "story", title: "Write your story", why: SELF_GUIDE.story.why, cta: "Start here", minutes: SELF_GUIDE.story.minutes,
    note: "Everything in Mithaq starts with knowing yourself. Five questions, in your own words." };
  if (!done.includes("readiness")) return charterStep("readiness", { note: "Your foundations: the first charter to compare with anyone." });
  if (!growthDone) return { kind: "growth", title: "Add your growth portfolio", why: SELF_GUIDE.growth.why, cta: "Continue", minutes: SELF_GUIDE.growth.minutes };

  const cur = connected ? stageRank(partnership.stage) : 1;
  const open = STAGES.slice(0, cur).flatMap(s => STAGE_ORDER[s.id]);
  const later = STAGES.slice(cur).flatMap(s => STAGE_ORDER[s.id]);
  const nextOpen = open.find(id => !done.includes(id) && byId(id)?.qs.length);
  const nextLater = later.find(id => !done.includes(id) && byId(id)?.qs.length);

  if (!connected && !pending) {
    const alt = nextOpen || nextLater;
    return { kind: "partner", title: "Invite the person you're getting to know", cta: "Invite",
      why: "You each answer privately. Nothing is shared until you both choose to compare a charter.",
      note: "Not there yet? That's fine — keep going on your own.",
      alt: alt ? charterStep(alt) : null };
  }
  if (nextOpen) return charterStep(nextOpen, pending ? { note: "Your invite is waiting to be accepted. Keep going in the meantime." } : {});
  if (connected && partnership.stage === "married") {
    return { kind: "partner", cta: "Open", title: "Your first year together",
      why: "Short, private check-ins at 1, 3, 6 and 12 months help you catch small things before they grow." };
  }
  if (connected) {
    const isLast = partnership.stage === "engaged";
    return { kind: "partner", cta: "Open",
      title: isLast ? "Review your nikah summary together" : "Compare what you've finished, then go deeper",
      why: isLast ? "Bring your agreements together on one page to discuss with your imam."
                  : "Reveal finished charters side by side. When you're both ready, move to the next stage together.",
      alt: nextLater ? charterStep(nextLater, { note: "Answer ahead — private until you're both ready." }) : null };
  }
  if (nextLater) return charterStep(nextLater);
  return { kind: "rest", title: "You've answered everything", why: "Come back as life changes. Answers are true as of the day you write them, and you can update them anytime.", cta: null };
}

// ─── TRIAGE: how much a difference matters by default ─────────────────────────
// "must" = must resolve before nikah · "care" = discuss with care · "grow" = grow into.
// Either partner can privately raise any question to "must" for themselves.
const CHARTER_WEIGHT = { joy: "grow", who_i_am: "grow" };
const MUST_RESOLVE = new Set([
  "readiness__q2", "readiness__q9", "nikah__n4", "nikah__n12", "nikah__n14",
  "faith_journey__fj11", "faith_journey__fj12",
  "career__ca6", "career__ca8", "career__ca9", "career__ca14",
  "financial__f1", "financial__f2", "financial__f7", "financial__f9",
  "cultural__cu8",
  "parenting__p2", "parenting__p3", "parenting__p6", "parenting__p7", "parenting__p12",
  "inlaws__il1", "inlaws__il6", "inlaws__il8",
  "unspoken__un23",
  "aging__ag7",
]);
const GROW_INTO = new Set(["unspoken__un3", "unspoken__un19", "cultural__cu2"]);
const weightOf = (sectionId, qid) => {
  const k = `${sectionId}__${qid}`;
  if (MUST_RESOLVE.has(k)) return "must";
  if (GROW_INTO.has(k)) return "grow";
  return CHARTER_WEIGHT[sectionId] || "care";
};
const flagKey = (sectionId, qid) => `${sectionId}__${qid}`;

// ─── AMANAH PLEDGE ────────────────────────────────────────────────────────────
const AMANAH_PLEDGE = {
  title: "The amanah pledge",
  body: "What you share here is a trust. Before connecting, each of you makes the same promise.",
  promises: [
    "I will answer in my own words.",
    "I will guard what you share, and not speak of it to others.",
    "If this doesn't lead to nikah, I will keep your answers and your story to myself.",
  ],
  verse: "“And fulfil every covenant. Indeed, the covenant will be questioned about.” — Surah Al-Isra 17:34",
  check: "I take this pledge",
};

// ─── NIKAH SUMMARY ────────────────────────────────────────────────────────────
// The only practical topics that can ever appear on the summary. The database
// holds the same list (migration 007: _summary_questions) — keep them in sync.
const SUMMARY_GROUPS = [
  { id: "faith",    title: "Faith & the nikah" },
  { id: "work",     title: "Work & money" },
  { id: "home",     title: "Home & family" },
  { id: "children", title: "Children" },
  { id: "culture",  title: "Culture & where we'll live" },
  { id: "later",    title: "Later in life" },
];
const SUMMARY_TOPICS = {
  nikah__n1:     { group: "faith",    label: "Commitments we make to each other" },
  nikah__n4:     { group: "faith",    label: "How we'll approach the mahr conversation" },
  nikah__n7:     { group: "faith",    label: "How we'll handle disagreements" },
  nikah__n9:     { group: "faith",    label: "Who we'll turn to when we're stuck" },
  readiness__q2: { group: "faith",    label: "Shared religious practice in our marriage" },
  career__ca6:   { group: "work",     label: "If the wife earns more" },
  career__ca8:   { group: "work",     label: "How we'll both work" },
  career__ca9:   { group: "work",     label: "Relocating for work" },
  career__ca14:  { group: "work",     label: "Whose work changes first when children come" },
  financial__f1: { group: "work",     label: "Who manages our finances" },
  financial__f2: { group: "work",     label: "How our accounts are set up" },
  financial__f7: { group: "work",     label: "How we'll cover household costs" },
  financial__f9: { group: "work",     label: "Interest-based loans and mortgages" },
  unspoken__un6: { group: "home",     label: "Cooking" },
  unspoken__un7: { group: "home",     label: "Laundry" },
  unspoken__un8: { group: "home",     label: "Dishes" },
  unspoken__un21:{ group: "home",     label: "Bathroom cleaning" },
  unspoken__un22:{ group: "home",     label: "Keeping track of the household" },
  inlaws__il6:   { group: "home",     label: "Living with parents" },
  inlaws__il7:   { group: "home",     label: "Visiting our parents" },
  inlaws__il8:   { group: "home",     label: "Parents' role in our decisions" },
  inlaws__il1:   { group: "home",     label: "When a parent and spouse disagree" },
  inlaws__il4:   { group: "home",     label: "Boundaries with our families" },
  inlaws__il12:  { group: "home",     label: "What stays between the two of us" },
  parenting__p2: { group: "children", label: "Values we'll raise our children with" },
  parenting__p6: { group: "children", label: "Whether we want children" },
  parenting__p7: { group: "children", label: "How many children we hope for" },
  parenting__p10:{ group: "children", label: "Faith in our children's daily life" },
  parenting__p3: { group: "children", label: "Schooling" },
  parenting__p5: { group: "children", label: "When we disagree about our children" },
  cultural__cu3: { group: "culture",  label: "The place of our cultures at home" },
  cultural__cu5: { group: "culture",  label: "Cultural practices and Islam" },
  cultural__cu8: { group: "culture",  label: "Where we intend to live long-term" },
  cultural__cu9: { group: "culture",  label: "Languages for our children" },
  cultural__cu17:{ group: "culture",  label: "Wedding expectations" },
  aging__ag2:    { group: "later",    label: "Where we'd like to live in old age" },
  aging__ag13:   { group: "later",    label: "Caring for a parent later in life" },
  aging__ag7:    { group: "later",    label: "Burial location" },
  aging__ag8:    { group: "later",    label: "Janazah and burial wishes" },
};

// ─── JOURNAL ──────────────────────────────────────────────────────────────────
const JOURNAL_KINDS = [
  { id: "reflection", icon: "🪶", label: "Reflection" },
  { id: "dua",        icon: "🤲", label: "Du'a" },
  { id: "istikhara",  icon: "📿", label: "Istikhara" },
];
const ISTIKHARA_NOTE = "Before a big step, many people pray istikhara: two voluntary rak'ahs, then the du'a of istikhara, asking Allah to make the matter easy if it is good for you, and to turn it away if it is not.";

// ─── COMPARISON LOGIC ─────────────────────────────────────────────────────────
const COMPARE_GROUPS = [
  { id: "align",   title: "🟢 Where you align",           sub: "You answered the same way, or very close." },
  { id: "talk",    title: "🟡 Worth a conversation",       sub: "Some overlap, some difference — worth understanding why." },
  { id: "discuss", title: "🔴 Talk before the nikah",      sub: "Clear differences. Not a verdict — a conversation to have honestly." },
  { id: "read",    title: "📖 Read each other's words",    sub: "Open answers can't be scored. Read them slowly, together." },
];
function classifyPair(q, a, b) {
  if (isAlign(b)) return { same: "align", overlap: "talk", different: "discuss" }[b.__align] || "read";
  if (a == null || b == null) return "read";
  if (a === "__working__" || b === "__working__") return "talk";
  if (!q) return "read";
  if (q.type === "choice") return a === b ? "align" : "discuss";
  if (q.type === "multi") {
    const A = new Set(a || []), B = new Set(b || []);
    const inter = [...A].filter(x => B.has(x)).length;
    const union = new Set([...A, ...B]).size;
    if (inter === union) return "align";
    return inter > 0 ? "talk" : "discuss";
  }
  if (q.type === "scale") {
    let gap = 0;
    (q.items || []).forEach(i => { gap = Math.max(gap, Math.abs(((a || {})[i] || 3) - ((b || {})[i] || 3))); });
    return gap <= 1 ? "align" : gap <= 2 ? "talk" : "discuss";
  }
  return "read";
}

// ─── PARTNER INVITE HELPERS ───────────────────────────────────────────────────
const JOIN_KEY = "mithaq:joinCode";
const getJoinCode = () => { try { return localStorage.getItem(JOIN_KEY); } catch (e) { return null; } };
const setJoinCode = (code) => { try { localStorage.setItem(JOIN_KEY, code); } catch (e) {} };
const clearJoinCode = () => { try { localStorage.removeItem(JOIN_KEY); } catch (e) {} };
// After signing in to take notes on someone's profile, bring the person back there.
const RETURN_KEY = "mithaq:returnProfile";
const getReturnProfile = () => { try { return localStorage.getItem(RETURN_KEY); } catch (e) { return null; } };
const setReturnProfile = (id) => { try { localStorage.setItem(RETURN_KEY, id); } catch (e) {} };
const clearReturnProfile = () => { try { localStorage.removeItem(RETURN_KEY); } catch (e) {} };

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("landing");
  // On every screen change: start at the top, name the browser tab after the
  // screen, and move focus to the screen's heading so screen readers announce
  // the new view. Titles are fixed per screen on purpose, so no question text,
  // names or answers end up in tab titles or browser history.
  const firstView = useRef(true);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = SCREEN_TITLES[screen] ? `${SCREEN_TITLES[screen]} · Mithaq` : "Mithaq — prepare for marriage together";
    if (firstView.current) { firstView.current = false; return; }
    const id = requestAnimationFrame(() => {
      if (document.querySelector('[aria-modal="true"]')) return; // an open dialog keeps focus
      focusViewHeading();
    });
    return () => cancelAnimationFrame(id);
  }, [screen]);
  const [viewingProfileId, setViewingProfileId] = useState(null);
  const [familyToken, setFamilyToken] = useState(null);
  const [prerequisite, setPrerequisite] = useState("");
  const [prerequisiteDone, setPrerequisiteDone] = useState(false);
  // Load everything for a signed-in person. Returns false if it couldn't.
  async function loadSignedIn(authUser) {
      // Load profile
      let { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", authUser.id)
        .single();

      if (!profile) {
        // Safety net for accounts made before profiles were created automatically.
        const name = authUser.user_metadata?.name || (authUser.email || "").split("@")[0];
        const { data: created } = await supabase.from("profiles").insert({ id: authUser.id, name, email: authUser.email }).select().single();
        profile = created;
      }
      if (profile) {
        setUser({ name: profile.name, email: profile.email, id: authUser.id, shareToken: profile.share_token, emailUpdates: profile.email_updates !== false });
        if (profile.prerequisite) { setPrerequisite(profile.prerequisite); setPrerequisiteDone(true); }

        // Load completed sections
        const { data: completed } = await supabase
          .from("sections_completed")
          .select("section_id")
          .eq("user_id", authUser.id);

        if (completed) {
          const completedIds = completed.map(c => c.section_id);
          if (completedIds.includes("story")) setStoryDone(true);
          if (completedIds.includes("growth")) setGrowthDone(true);
          setDone(completedIds.filter(id => id !== "story" && id !== "growth"));
        }

        // Load saved answers
        const { data: savedAnswers } = await supabase
          .from("answers")
          .select("*")
          .eq("user_id", authUser.id);

        if (savedAnswers) {
          const newAnswers = {};
          const newStoryAnswers = {};
          const newGrowthAnswers = {};
          const newWorkingThrough = {};
          const newAnswerDates = {};

          savedAnswers.forEach(row => {
            const value = row.answer_value === "__working__" 
              ? "__working__" 
              : JSON.parse(row.answer_value);
            
            const date = new Date(row.answered_at).toLocaleDateString("en-US", {
              month: "short", year: "numeric"
            });

            if (row.answer_value === "__working__") {
              newWorkingThrough[row.question_id] = true;
            } else if (row.section_id === "story") {
              newStoryAnswers[row.question_id] = value;
            } else if (row.section_id === "growth") {
              newGrowthAnswers[row.question_id] = value;
            } else {
              newAnswers[row.question_id] = value;
            }
            newAnswerDates[row.question_id] = date;
          });

          setAnswers(newAnswers);
          setStoryAnswers(newStoryAnswers);
          setGrowthAnswers(newGrowthAnswers);
          setWorkingThrough(newWorkingThrough);
          setAnswerDates(newAnswerDates);
        }

        // Load "say more" notes
        const { data: savedNotes } = await supabase
          .from("answer_notes")
          .select("section_id, question_id, note")
          .eq("user_id", authUser.id);
        if (savedNotes) {
          const n = {};
          savedNotes.forEach(r => { n[`${r.section_id}__${r.question_id}`] = r.note; });
          savedNoteKeys.current = new Set(Object.keys(n));
          setAnswerNotes(n);
        }

        return true;
      }
      return false;
  }
  const [onboardStep, setOnboardStep] = useState(0);
  // Where the auth screen opens: "signup" after the intro, "login" from the landing's Sign in.
  const [authStart, setAuthStart] = useState("signup");
  const [form, setForm] = useState({name:"",email:"",password:""});
  useEffect(() => {
  // Check if URL is a profile link
  const path = window.location.pathname;
  // Partner invite link: /join/CODE — remember the code, then tidy the URL
  const joinMatch = path.match(/^\/join\/([A-Za-z0-9]+)/);
  if (joinMatch) {
    setJoinCode(joinMatch[1].toUpperCase());
    window.history.replaceState({}, "", "/");
  }
  const familyMatch = path.match(/^\/family\/([A-Za-z0-9]+)/);
  if (familyMatch) {
    setFamilyToken(familyMatch[1]);
    setScreen("family");
    return;
  }
  const profileMatch = path.match(/\/profile\/(.+)/);
  if (profileMatch) {
    setViewingProfileId(profileMatch[1]);
    setScreen("publicProfile");
    return;
  }
  if (path.startsWith("/reset-password")) {
    setScreen("resetPassword");
    return;
  }
  supabase.auth.getSession().then(async ({ data: { session } }) => {
    if (session) {
      if (await loadSignedIn(session.user)) goHome();
    } else if (joinMatch) {
      // Invited but not signed in yet — go straight to sign up / log in
      setScreen("auth");
    }
  });
}, []);
  const [user, setUser] = useState(null);
  const [active, setActive] = useState(null);
  const [activeType, setActiveType] = useState("charter");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [storyAnswers, setStoryAnswers] = useState({});
  const [growthAnswers, setGrowthAnswers] = useState({});
  const [answerDates, setAnswerDates] = useState({});
  const [workingThrough, setWorkingThrough] = useState({});
  const [answerNotes, setAnswerNotes] = useState({});      // "section__qid" → note
  const savedNoteKeys = useRef(new Set());
  const [done, setDone] = useState([]);
  const [storyDone, setStoryDone] = useState(false);
  const [growthDone, setGrowthDone] = useState(false);
  const [witness, setWitness] = useState({name:"",relationship:"",note:""});
  const [witnessDone, setWitnessDone] = useState(false);
  const [showExample, setShowExample] = useState({});
  const [profileTab, setProfileTab] = useState("story");
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareSettings, setShareSettings] = useState({
    mental: false, career: false, cultural: false,
    aging: false, joy: false, relationship: false, nikah: false,
    money_values: false, inlaws: false,
    assumptions: false, conflict_history: false, parenting: false,
    faith_journey: false, who_i_am: false, readiness: false,
  });
  
  const [copied, setCopied] = useState(false);
  const [partnership, setPartnership] = useState(null);
  const [compareSection, setCompareSection] = useState(null);
  const [openNotes, setOpenNotes] = useState(0);
  // Private "this really matters to me" marks. Never shown to a partner.
  const [flags, setFlags] = useState({});
  useEffect(() => {
    if (!user?.id) { setFlags({}); return; }
    supabase.from("importance_flags").select("section_id,question_id").then(({ data }) => {
      const m = {};
      (data || []).forEach(f => { m[flagKey(f.section_id, f.question_id)] = true; });
      setFlags(m);
    });
  }, [user?.id]);
  const toggleFlag = async (sectionId, qid) => {
    if (!user?.id) return;
    const k = flagKey(sectionId, qid);
    const on = !flags[k];
    setFlags(p => { const c = { ...p }; if (on) c[k] = true; else delete c[k]; return c; });
    const { error } = on
      ? await supabase.from("importance_flags").insert({ user_id: user.id, section_id: sectionId, question_id: qid })
      : await supabase.from("importance_flags").delete().eq("section_id", sectionId).eq("question_id", qid);
    if (error) setFlags(p => { const c = { ...p }; if (on) delete c[k]; else c[k] = true; return c; });
  };

  // Where to land after signing in
  const goHome = () => {
    if (getJoinCode()) { setScreen("partner"); return; }
    const rp = getReturnProfile();
    if (rp) { clearReturnProfile(); window.location.href = `/profile/${rp}`; return; }
    setScreen("dashboard");
  };

  useEffect(() => {
    if (screen !== "dashboard" || !user?.id) return;
    Promise.all([
      supabase.from("notes").select("id", { count: "exact", head: true }).eq("discussed", false),
      supabase.rpc("open_discussions"),
    ]).then(([n, d]) => setOpenNotes((n.count || 0) + (Array.isArray(d.data) ? d.data.length : 0)));
  }, [screen, user?.id]);

  const loadPartnership = async () => {
    const { data, error } = await supabase.rpc("my_partnership");
    if (!error) setPartnership(data && data[0] ? data[0] : null);
  };
  useEffect(() => {
    if (user?.id) loadPartnership();
    else setPartnership(null);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startCharter = (c) => {
    if (!c.qs.length) return;
    setActive(c); setActiveType("charter"); setStep(0);
    setScreen(done.includes(c.id) ? "survey" : "reframe");
  };
  const [finished, setFinished] = useState(null);   // what was just completed
  const startStory = () => { setActiveType("story"); setStep(0); setScreen("survey"); };
  const startGrowth = () => { setActiveType("growth"); setStep(0); setScreen("survey"); };

  const curQs = activeType==="story" ? STORY_QS : activeType==="growth" ? GROWTH_QS : active?.qs||[];
  const q = curQs[step];
  const qId = q?.id;
  const isWorking = workingThrough[qId];
  const curAns = isWorking ? "__working__"
    : activeType==="story" ? storyAnswers[qId]
    : activeType==="growth" ? growthAnswers[qId]
    : answers[qId];
  const canNext = isWorking || (curAns !== undefined && curAns !== "" && (Array.isArray(curAns)?curAns.length>0:true));

  const setAns = (val) => {
    if (workingThrough[qId]) return;
    setAnswerDates(p=>({...p,[qId]:SHORT_DATE}));
    if (activeType==="story") setStoryAnswers(p=>({...p,[qId]:val}));
    else if (activeType==="growth") setGrowthAnswers(p=>({...p,[qId]:val}));
    else setAnswers(p=>({...p,[qId]:val}));
  };

  const toggleWorking = () => {
    setWorkingThrough(p=>({...p,[qId]:!p[qId]}));
    setAnswerDates(p=>({...p,[qId]:SHORT_DATE}));
  };

const nextQ = async () => {
    // Save current answer to Supabase if user is logged in
    if (user?.id && q && curAns && curAns !== "__working__") {
      await supabase.from("answers").upsert({
        user_id: user.id,
        section_id: activeType === "story" ? "story" : activeType === "growth" ? "growth" : active?.id,
        question_id: qId,
        answer_value: JSON.stringify(curAns),
        answered_at: new Date().toISOString(),
      });
    }

    // Save or clear the optional "say more" note
    if (user?.id && q && activeType === "charter" && active && noteModeFor(active.id, q)) {
      const k = `${active.id}__${qId}`;
      const note = (answerNotes[k] || "").trim().slice(0, NOTE_MAX);
      if (note && !isWorking) {
        await supabase.from("answer_notes").upsert({
          user_id: user.id, section_id: active.id, question_id: qId, note, updated_at: new Date().toISOString(),
        });
        savedNoteKeys.current.add(k);
      } else if (savedNoteKeys.current.has(k)) {
        await supabase.from("answer_notes").delete()
          .eq("user_id", user.id).eq("section_id", active.id).eq("question_id", qId);
        savedNoteKeys.current.delete(k);
      }
    }

    // Save "still working through this" state
    if (user?.id && q && isWorking) {
      await supabase.from("answers").upsert({
        user_id: user.id,
        section_id: activeType === "story" ? "story" : activeType === "growth" ? "growth" : active?.id,
        question_id: qId,
        answer_value: "__working__",
        answered_at: new Date().toISOString(),
      });
    }

    if (step < curQs.length-1) {
      setStep(s=>s+1);
    } else {
      // Mark section as complete in Supabase
      if (user?.id) {
        const sectionId = activeType === "story" ? "story" : activeType === "growth" ? "growth" : active?.id;
        await supabase.from("sections_completed").upsert({
          user_id: user.id,
          section_id: sectionId,
          completed_at: new Date().toISOString(),
        });
      }
      const wasDone = activeType==="story" ? storyDone : activeType==="growth" ? growthDone : done.includes(active.id);
      if (activeType==="story") setStoryDone(true);
      else if (activeType==="growth") setGrowthDone(true);
      else setDone(p=>p.includes(active.id)?p:[...p,active.id]);
      if (wasDone) { setScreen("dashboard"); return; }
      setFinished(activeType==="charter" ? { type: "charter", charter: active } : { type: activeType });
      setScreen("finished");
    }
  };
  const doneCount = done.length+(storyDone?1:0)+(growthDone?1:0)+(witnessDone?1:0);
  const totalSections = CHARTERS.length+3;
  const progress = Math.round((doneCount/totalSections)*100);
  const profileUrl = user ? `mithaq.app/u/${user.name.toLowerCase().replace(/\s/g,"-").replace(/[^a-z-]/g,"")}-m4q` : "";

  const CSS = `
    @import url('https://fonts.googleapis.com/css2?${MODERN
      ? "family=Marcellus&family=Figtree:ital,wght@0,300;0,400;0,500;0,600;1,400&family=Amiri:wght@400;700"
      : EDITORIAL ? "family=Cormorant+Garamond:wght@400;500;600&family=Jost:wght@400;500&family=Amiri:wght@400;700"
      : PAPER ? "family=Cormorant+Garamond:ital,wght@0,500;0,600;1,500&family=Inter:wght@400;500;600&family=Amiri:wght@700"
      : "family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Jost:wght@300;400;500;600"}&display=swap');
    *{box-sizing:border-box;margin:0;padding:0;}
    button{cursor:pointer;font-family:${Fb};}
    textarea,input{font-family:${Fb};}
    textarea{resize:none;}
    /* Cards and toggles are real buttons. This strips the browser's button look
       so each one keeps its own inline styles (which still win where set).
       .btn-stack is for cards that stack their content: column flex keeps it at
       the top when the card is stretched to its row's height (a plain block
       button would center it vertically). */
    .btn-card{appearance:none;-webkit-appearance:none;font:inherit;color:inherit;letter-spacing:inherit;text-align:left;width:100%;display:block;margin:0;padding:0;border:none;background:none;}
    .btn-stack{display:flex;flex-direction:column;justify-content:flex-start;}
    /* Keyboard focus ring, on both looks. Two tones so it shows on light and
       dark surfaces alike: a dark outline with a thin cream band inside it. */
    :focus-visible{outline:2px solid ${C.turq};outline-offset:2px;box-shadow:0 0 0 2px ${C.cream}!important;}
    /* Reduce motion: no slide-ins, lifts or animated progress bars. */
    @media(prefers-reduced-motion:reduce){*{animation:none!important;transition:none!important;scroll-behavior:auto!important;}.card:hover{transform:none!important;}}
    .opt:hover{border-color:${C.gold}!important;background:${tint(C.gold,0.03)}!important;}
    /* Answer options: the native radio/checkbox stays in the page for the
       keyboard and screen readers but is visually hidden; its focus shows as a
       ring around the whole option, on both looks. */
    .vh-input{position:absolute;opacity:0;width:1px;height:1px;margin:0;pointer-events:none;}
    .opt:has(.vh-input:focus-visible){outline:2px solid ${C.turq};outline-offset:2px;box-shadow:0 0 0 2px ${C.cream};}
    @supports not selector(:has(*)){.vh-input:focus-visible + .opt-ind{outline:2px solid ${C.turq};outline-offset:2px;box-shadow:0 0 0 2px ${C.cream};}}
    .card{transition:transform .25s ease,box-shadow .25s ease,border-color .25s ease,background-color .25s ease;}
    .card:hover{transform:translateY(-3px);box-shadow:0 14px 40px ${tint(C.green,0.13)}!important;}
    .fade{animation:fu 0.4s ease both;}
    @keyframes fu{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
    input[type=range]{accent-color:${C.green};width:100%;}
    .modal-overlay{position:fixed;inset:0;background:rgba(26,24,20,0.7);z-index:100;display:flex;align-items:flex-end;justify-content:center;padding:0;}
    @media(min-width:600px){.modal-overlay{align-items:center;padding:24px;}}
    /* A dialog panel or a screen heading takes focus so screen readers announce
       it; they aren't controls, so no focus ring. */
    .modal-panel:focus,.modal-panel:focus-visible,[data-view-heading]:focus,[data-view-heading]:focus-visible{outline:none;box-shadow:none!important;}
    @media print{.no-print{display:none!important;} body{background:#fff!important;}}
    /* A partner's answers can't be selected or copied, and before the engaged
       stage they don't print. Screenshots rely on the amanah pledge. */
    .partner-private{-webkit-user-select:none;user-select:none;}
    .classic-nav button{white-space:nowrap;}
    .print-only{display:none;}
    @media print{.print-locked .partner-private{visibility:hidden!important;} .print-locked .print-only{display:block!important;}}
    ${FLAT ? `
    body{background:${C.cream};}
    .card{transition:border-color .2s ease,background-color .2s ease;}
    .card:hover{transform:none;box-shadow:none!important;border-color:${C.gold}!important;}
    /* The tools menu wraps onto more lines rather than scrolling sideways, so
       every button is always visible (a hidden scrollbar cut the last one off). */
    .navrow{display:flex;flex-wrap:wrap;gap:8px;}
    .navrow button{white-space:nowrap;}
    /* Tablet and up: two even rows of four equal-width buttons. */
    @media(min-width:600px){.navrow{display:grid;grid-template-columns:repeat(4,1fr);}.navrow button{text-align:center;}}
    .hero-grid{display:grid;grid-template-columns:1.1fr 0.9fr;gap:64px;align-items:center;}
    .arch-reveal{animation:archIn 1.1s cubic-bezier(.2,.7,.2,1) both;}
    @keyframes archIn{from{opacity:0;transform:translateY(18px) scale(.985)}to{opacity:1;transform:none}}
    .arch-verse-out{display:none;}
    @media(max-width:860px){.hero-grid{grid-template-columns:1fr;gap:36px;}.hero-arch{order:-1;max-width:280px;margin:0 auto;width:100%;}.arch-verse-in{display:none;}.arch-verse-out{display:block;}}
    ` : ""}
    ${EDITORIAL ? `
    /* Editorial: field-journal paper with a faint grain, long quiet sections. */
    body{background-color:${C.cream};background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.35 0 0 0 0 0.3 0 0 0 0 0.2 0 0 0 .05 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");}
    .ed{font-size:17px;line-height:1.6;}
    .ed-wrap{max-width:1160px;margin:0 auto;padding-inline:clamp(20px,5vw,56px);}
    .ed-nav{display:flex;align-items:center;justify-content:space-between;padding-block:26px;}
    .ed-link{background:none;border:none;color:${C.text};font-size:16px;padding:10px 2px;text-decoration:underline;text-decoration-color:${C.parchment};text-underline-offset:5px;text-decoration-thickness:1px;transition:text-decoration-color .2s ease;}
    .ed-link:hover{text-decoration-color:${C.gold};}
    .ed-btn{background:${C.green};color:${C.cream};border:none;border-radius:999px;padding:16px 34px;font-size:16px;font-weight:500;letter-spacing:.02em;transition:background-color .2s ease,transform .2s ease;}
    .ed-btn:hover{background:${C.gl};}
    .ed-btn:active{transform:scale(.98);}
    .ed-btn-gold{background:${C.goldL};color:${C.onGold};}
    .ed-btn-gold:hover{background:#E0B759;}
    .ed-hero{display:grid;grid-template-columns:1.15fr .85fr;gap:clamp(32px,6vw,96px);align-items:center;padding-block:clamp(32px,7vh,88px) clamp(64px,12vh,140px);}
    .ed-display{font-family:${Fd};font-weight:400;color:${C.green};font-size:clamp(46px,7.4vw,104px);line-height:.98;letter-spacing:-.025em;margin-bottom:32px;text-wrap:balance;}
    .ed-lede{font-size:clamp(18px,1.6vw,21px);line-height:1.6;max-width:34em;margin-bottom:36px;}
    .ed-actions{display:flex;flex-wrap:wrap;gap:14px 26px;align-items:center;margin-bottom:22px;}
    .ed-small{font-size:15px;color:${C.muted};}
    .ed-seal{margin:0;position:relative;display:grid;place-items:center;text-align:center;}
    .ed-seal-star{width:100%;max-width:420px;animation:edTurn 2.4s cubic-bezier(.16,1,.3,1) both;}
    .ed-seal-word{position:absolute;inset:0;display:grid;place-items:center;font-family:${Fa};font-weight:700;color:${C.green};font-size:clamp(64px,8.6vw,124px);line-height:1;padding-bottom:6%;animation:edInk 1.6s .35s cubic-bezier(.16,1,.3,1) both;}
    .ed-seal figcaption{display:flex;flex-direction:column;gap:2px;margin-top:6px;font-size:15px;color:${C.muted};}
    @keyframes edTurn{from{transform:rotate(-22.5deg) scale(.96);opacity:.35}to{transform:none;opacity:1}}
    @keyframes edInk{from{filter:blur(8px);opacity:.25;letter-spacing:.08em}to{filter:none;opacity:1;letter-spacing:0}}
    .ed-rise{animation:edRise .9s cubic-bezier(.16,1,.3,1) both;}
    @keyframes edRise{from{transform:translateY(14px);opacity:.4}to{transform:none;opacity:1}}
    .ed-band{padding-block:clamp(72px,14vh,148px);border-top:1px solid ${C.parchment};}
    .ed-dark{background:${C.green};color:${C.cream};border-top:none;}
    .ed-deep{background:${C.soft};}
    .ed-chapter{max-width:980px;}
    .ed-chapter-mark{font-size:15px;color:${C.goldL};margin-bottom:22px;display:flex;align-items:center;gap:12px;}
    .ed-chapter-mark::before{content:"";width:28px;height:1px;background:currentColor;opacity:.7;}
    .ed-mark-light{color:${C.goldText};}
    .ed-statement{font-family:${Fd};font-weight:400;font-size:clamp(30px,4.2vw,56px);line-height:1.12;letter-spacing:-.015em;color:${C.cream};margin-bottom:28px;text-wrap:balance;}
    .ed-body-dark{font-size:19px;color:${tint(C.cream,0.82)};margin-bottom:40px;}
    .ed-topics{list-style:none;display:flex;flex-wrap:wrap;gap:10px;}
    .ed-topics li{border:1px solid ${tint(C.goldL,0.4)};color:${C.cream};border-radius:999px;padding:7px 16px;font-size:15px;}
    .ed-h2{font-family:${Fd};font-weight:500;font-size:clamp(34px,4.4vw,58px);line-height:1.05;letter-spacing:-.015em;color:${C.green};margin-bottom:40px;text-wrap:balance;}
    .ed-split{display:grid;grid-template-columns:1fr 1fr;gap:clamp(40px,6vw,96px);align-items:start;}
    .ed-steps{list-style:none;display:grid;gap:30px;}
    .ed-steps li{display:grid;grid-template-columns:44px 1fr;gap:16px;align-items:baseline;}
    .ed-step-n{font-family:${Fd};font-size:24px;color:${C.goldText};font-weight:500;}
    .ed-steps h3{font-family:${Fd};font-size:26px;font-weight:600;color:${C.text};line-height:1.2;margin-bottom:6px;}
    .ed-steps p{color:${C.muted};font-size:17px;max-width:34em;}
    .ed-compare{margin:0;background:${C.white};border:1px solid ${C.parchment};border-radius:16px;padding:26px 26px 10px;box-shadow:0 1px 2px ${tint(C.text,0.04)},0 18px 44px -12px ${tint(C.green,0.16)};}
    .ed-compare figcaption{font-size:15px;color:${C.muted};margin-bottom:10px;}
    .ed-compare ul{list-style:none;}
    .ed-compare li{border-top:1px solid ${C.parchment};padding-block:18px;}
    .ed-compare-charter{font-size:14px;color:${C.muted};margin-bottom:4px;}
    .ed-compare-q{font-family:${Fd};font-size:22px;font-weight:500;line-height:1.3;margin-bottom:12px;color:${C.text};}
    .ed-pill{display:inline-block;font-size:15px;font-weight:500;border-radius:999px;padding:5px 14px;}
    .ed-compare-note{font-size:15px;color:${C.muted};margin-top:8px;}
    .ed-path{list-style:none;display:grid;grid-template-columns:repeat(4,1fr);gap:28px;position:relative;margin-top:12px;}
    .ed-path::before{content:"";position:absolute;left:6px;right:6px;top:6px;height:1px;background:${C.parchment};}
    .ed-path li{position:relative;padding-top:36px;}
    .ed-path-dot{position:absolute;top:0;left:0;width:13px;height:13px;border-radius:50%;background:${C.cream};border:1px solid ${C.gold};display:grid;place-items:center;}
    .ed-path-end .ed-path-dot{border:none;background:none;width:auto;height:auto;top:-1px;}
    .ed-path h3{font-family:${Fd};font-size:28px;font-weight:600;color:${C.green};line-height:1.15;}
    .ed-path-count{font-size:15px;color:${C.goldText};margin:4px 0 10px;}
    .ed-path p{font-size:16px;color:${C.muted};}
    .ed-trust{display:grid;grid-template-columns:.8fr 1.2fr;gap:clamp(32px,6vw,96px);align-items:start;}
    .ed-trust dl{display:grid;gap:0;}
    .ed-trust dl>div{border-top:1px solid ${C.parchment};padding-block:22px;display:grid;grid-template-columns:1fr 1.3fr;gap:20px;}
    .ed-trust dt{font-family:${Fd};font-size:24px;font-weight:600;color:${C.text};line-height:1.2;}
    .ed-trust dd{color:${C.muted};}
    .ed-verse{padding-block:clamp(88px,16vh,168px);}
    .ed-arabic{font-family:${Fa};font-size:clamp(28px,3.6vw,46px);line-height:1.9;color:${C.goldL};max-width:900px;margin:0 auto 28px;}
    .ed-verse-en{font-family:${Fd};font-size:clamp(22px,2.4vw,30px);line-height:1.45;color:${C.cream};max-width:760px;margin:0 auto 14px;}
    .ed-verse-ref{font-size:15px;color:${tint(C.cream,0.75)};}
    .ed-foot{display:flex;flex-wrap:wrap;justify-content:space-between;gap:12px;padding-block:32px;font-size:15px;color:${C.muted};}
    .ed-foot span:first-child{font-family:${Fd};font-size:20px;color:${C.green};font-weight:600;}
    .ed-dark :focus-visible,.ed-dark:focus-visible{outline-color:${C.goldL};}
    @media(max-width:900px){.ed-hero,.ed-split,.ed-trust{grid-template-columns:1fr;}.ed-seal{order:-1;max-width:300px;margin:0 auto;}.ed-path{grid-template-columns:1fr 1fr;row-gap:44px;}.ed-path::before{display:none;}.ed-trust dl>div{grid-template-columns:1fr;gap:6px;}}
    @media(max-width:520px){.ed-path{grid-template-columns:1fr;}}
    @media(max-width:900px){.ed-seal-star{max-width:240px;}}
    /* Editorial dashboard */
    .ed-dash-bar{position:sticky;top:env(safe-area-inset-top,0px);z-index:20;background:${tint(C.cream,0.86)};backdrop-filter:saturate(1.2) blur(10px);-webkit-backdrop-filter:saturate(1.2) blur(10px);border-bottom:1px solid ${C.parchment};}
    .ed-dash-bar-in{display:flex;align-items:center;justify-content:space-between;padding-block:12px;}
    .ed-chip{display:inline-flex;align-items:center;gap:8px;background:${C.white};border:1px solid ${C.parchment};color:${C.text};border-radius:999px;padding:9px 16px;font-size:15px;min-height:44px;transition:border-color .2s ease;}
    .ed-chip:hover{border-color:${C.stone};}
    .ed-count{background:${C.green};color:${C.cream};border-radius:999px;font-size:12px;min-width:20px;height:20px;display:inline-grid;place-items:center;padding:0 6px;}
    .ed-dash-main{max-width:920px;padding-block:clamp(36px,7vh,72px) 96px;}
    .ed-greet{margin-bottom:40px;}
    .ed-greet-salam{font-size:16px;color:${C.muted};margin-bottom:6px;}
    .ed-greet-h{font-family:${Fd};font-weight:400;color:${C.green};font-size:clamp(44px,6.4vw,76px);line-height:1;letter-spacing:-.02em;margin-bottom:30px;}
    .ed-stagebar{list-style:none;display:grid;grid-template-columns:repeat(3,1fr);gap:10px;}
    .ed-stagebar li{position:relative;padding-top:14px;display:flex;flex-direction:column;gap:2px;}
    .ed-stagebar li::before{content:"";position:absolute;top:0;left:0;right:0;height:3px;border-radius:2px;background:${C.parchment};}
    .ed-stagebar-fill{position:absolute;top:0;left:0;height:3px;border-radius:2px;background:${C.green};}
    .ed-stagebar-label{font-size:15px;font-weight:500;color:${C.muted};}
    .ed-stagebar li.is-now .ed-stagebar-label{color:${C.text};}
    .ed-stagebar-n{font-size:14px;color:${C.muted};}
    .ed-next{background:${C.green};color:${C.cream};border-radius:20px;padding:clamp(28px,5vw,48px);margin-bottom:64px;box-shadow:0 30px 60px -30px ${tint(C.green,0.55)};position:relative;overflow:hidden;}
    .ed-next::after{content:"";position:absolute;right:-90px;top:-90px;width:300px;height:300px;background:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cg fill='none' stroke='%23D4A843' stroke-opacity='.28' stroke-width='.7'%3E%3Crect x='40' y='40' width='120' height='120'/%3E%3Crect x='40' y='40' width='120' height='120' transform='rotate(45 100 100)'/%3E%3Ccircle cx='100' cy='100' r='60'/%3E%3C/g%3E%3C/svg%3E") center/contain no-repeat;pointer-events:none;}
    .ed-next-kicker{display:flex;align-items:center;gap:8px;font-size:15px;color:${C.goldL};margin-bottom:14px;}
    .ed-next h2{font-family:${Fd};font-weight:500;font-size:clamp(34px,4.6vw,52px);line-height:1.05;letter-spacing:-.01em;color:${C.cream};margin-bottom:14px;max-width:16em;}
    .ed-next-why{font-size:19px;line-height:1.55;color:${tint(C.cream,0.88)};max-width:34em;margin-bottom:8px;}
    .ed-next-note{font-size:16px;color:${tint(C.cream,0.75)};max-width:36em;}
    .ed-next-actions{display:flex;flex-wrap:wrap;align-items:center;gap:14px 22px;margin-top:28px;font-size:15px;color:${tint(C.cream,0.75)};}
    .ed-next-alt{margin-top:18px;font-size:15px;color:${tint(C.cream,0.75)};}
    .ed-next-alt button{background:none;border:none;color:${C.cream};font-size:15px;padding:6px 0;text-decoration:underline;text-underline-offset:4px;text-decoration-color:${tint(C.goldL,0.6)};}
    .ed-sec{margin-bottom:56px;}
    .ed-sec-head{display:flex;align-items:baseline;justify-content:space-between;gap:16px;border-bottom:1px solid ${C.text};padding-bottom:10px;margin-bottom:6px;}
    .ed-sec-head h2{font-family:${Fd};font-weight:500;font-size:clamp(28px,3.4vw,36px);color:${C.green};line-height:1.1;}
    .ed-sec-head span{font-size:15px;color:${C.muted};white-space:nowrap;}
    .ed-sec.is-later .ed-sec-head{border-bottom-color:${C.parchment};}
    .ed-sec.is-later .ed-sec-head h2{color:${C.muted};}
    .ed-sec-note{font-size:16px;color:${C.muted};margin:10px 0 8px;max-width:40em;}
    .ed-list{display:grid;}
    .ed-list-2{grid-template-columns:1fr 1fr;column-gap:40px;}
    .ed-row{appearance:none;background:none;border:none;border-bottom:1px solid ${C.parchment};display:grid;grid-template-columns:34px 1fr auto;gap:16px;align-items:start;text-align:left;padding:20px 0;width:100%;color:inherit;font:inherit;cursor:pointer;transition:background-color .2s ease;}
    .ed-row:hover .ed-row-title{color:${C.gl};text-decoration:underline;text-decoration-color:${C.gold};text-underline-offset:5px;text-decoration-thickness:1px;}
    .ed-row-mark{padding-top:2px;}
    .ed-row-body{display:flex;flex-direction:column;gap:4px;min-width:0;}
    .ed-row-title{font-family:${Fd};font-size:23px;font-weight:600;line-height:1.2;color:${C.text};}
    .ed-row-why{font-size:15px;line-height:1.5;color:${C.muted};}
    .ed-row-meta{font-size:14px;color:${C.muted};white-space:nowrap;padding-top:5px;}
    .ed-row-meta.is-done{color:${C.green};font-weight:500;}
    .ed-list-2 .ed-row{grid-template-columns:34px 1fr;}
    .ed-list-2 .ed-row-meta{grid-column:2;padding-top:0;}
    .ed-ahead{display:inline-flex;align-items:center;gap:8px;margin-top:6px;background:none;border:1px solid ${C.parchment};border-radius:999px;padding:10px 18px;min-height:44px;font-size:15px;color:${C.text};}
    .ed-ahead:hover{border-color:${C.stone};}
    .ed-notice{background:${C.white};border:1px solid ${C.parchment};border-radius:16px;padding:28px;margin-bottom:40px;}
    .ed-notice h2{font-family:${Fd};font-size:28px;font-weight:500;color:${C.green};margin-bottom:10px;}
    .ed-notice p{color:${C.text};margin-bottom:10px;max-width:40em;}
    .ed-notice-dua{font-family:${Fd};font-size:20px;color:${C.goldText};}
    .ed-dash-foot{text-align:center;padding-top:48px;display:grid;justify-items:center;gap:8px;color:${C.muted};font-size:15px;}
    .ed-dash-hadith{font-family:${Fd};font-size:24px;color:${C.green};max-width:24em;line-height:1.35;}
    .ed-menu{display:grid;gap:22px;}
    .ed-menu-top{display:flex;justify-content:space-between;align-items:center;}
    .ed-menu-group{display:grid;}
    .ed-menu-group>p{font-size:14px;color:${C.muted};padding-bottom:6px;border-bottom:1px solid ${C.parchment};}
    .ed-menu-group>button{background:none;border:none;border-bottom:1px solid ${C.soft};text-align:left;padding:12px 0;min-height:44px;font-family:${Fd};font-size:21px;font-weight:500;color:${C.text};}
    .ed-menu-group>button:hover{color:${C.gl};}
    .ed-looks{display:flex;flex-wrap:wrap;gap:8px;padding-top:12px;}
    .ed-looks button{background:${C.white};border:1px solid ${C.parchment};border-radius:999px;padding:8px 14px;font-size:14px;color:${C.text};min-height:40px;}
    .ed-looks button[aria-pressed="true"]{background:${C.green};color:${C.cream};border-color:${C.green};}
    .ed-chap{min-height:100vh;}
    .ed-chap-in{padding-block:24px 80px;}
    .ed-back{font-size:15px;}
    .ed-chap-grid{display:grid;grid-template-columns:minmax(0,1.3fr) minmax(0,.7fr);gap:clamp(32px,6vw,96px);align-items:center;padding-top:clamp(28px,6vh,72px);}
    .ed-chap-where{font-size:15px;color:${C.goldText};margin-bottom:6px;}
    .ed-chap-name{font-size:16px;color:${C.muted};margin-bottom:18px;}
    .ed-chap-title{font-family:${Fd};font-weight:400;color:${C.green};font-size:clamp(44px,6.6vw,88px);line-height:1;letter-spacing:-.02em;margin-bottom:28px;text-wrap:balance;}
    .ed-chap-body{font-size:clamp(18px,1.5vw,20px);line-height:1.65;max-width:36em;margin-bottom:32px;white-space:pre-line;}
    .ed-chap-why{margin:0 0 32px;padding:22px 0 0;border-top:1px solid ${C.gold};max-width:36em;}
    .ed-chap-why-label{font-size:14px;color:${C.goldText};margin-bottom:8px;}
    .ed-chap-why p:last-child{font-family:${Fd};font-size:clamp(22px,2.2vw,27px);line-height:1.35;color:${C.text};}
    .ed-chap-sub{font-family:${Fd};font-size:21px;color:${C.green};margin-bottom:24px;max-width:34em;}
    .ed-chap-facts{display:grid;grid-template-columns:repeat(3,auto);justify-content:start;gap:0;border-block:1px solid ${C.parchment};margin-bottom:18px;max-width:36em;}
    .ed-chap-facts div{padding:14px 28px 14px 0;}
    .ed-chap-facts div+div{padding-left:28px;border-left:1px solid ${C.parchment};}
    .ed-chap-facts dt{font-size:13px;color:${C.muted};}
    .ed-chap-facts dd{font-family:${Fd};font-size:24px;font-weight:600;color:${C.text};}
    .ed-chap-note{font-size:15px;color:${C.muted};max-width:38em;margin-bottom:32px;}
    .ed-chap-art{display:grid;place-items:center;}
    /* Questions: the question is the page. */
    #survey-question{font-size:clamp(30px,3.8vw,44px)!important;line-height:1.15!important;font-weight:500!important;letter-spacing:-.01em;}
    @media(max-width:900px){.ed-chap-grid{grid-template-columns:1fr;}.ed-chap-art{display:none;}}
    @media(max-width:480px){.ed-chap-facts div{padding-right:16px;}.ed-chap-facts div+div{padding-left:16px;}}
    @media(max-width:700px){.ed-list-2{grid-template-columns:1fr;}.ed-stagebar-n{font-size:13px;}.ed-row{grid-template-columns:34px 1fr;}.ed-row-meta{grid-column:2;padding-top:0;}}
    ` : ""}
    ${PAPER ? `
    body{background-color:${C.cream};color:${C.text};-webkit-font-smoothing:antialiased;font-feature-settings:"lnum" 1;}
    ::selection{background:${tint(C.gold,0.28)};color:${C.text};}
    input,textarea{caret-color:${C.green};}
    html{scrollbar-color:${C.parchment} transparent;}
    a{color:${C.green};text-underline-offset:3px;}
    /* Cards rest on hairlines and lift with a barely-there layered shadow. */
    .card:hover{border-color:${C.stone}!important;box-shadow:0 1px 2px ${tint(C.text,0.04)},0 6px 18px ${tint(C.text,0.06)}!important;}
    .opt:hover{border-color:${C.gl}!important;background:${tint(C.green,0.03)}!important;}
    /* The tracked all-caps label is a classic idiom; paper uses plain small
       labels instead, and lifts very light display weights. Scoped to paper. */
    [style*="text-transform: uppercase"]{text-transform:none!important;letter-spacing:0!important;font-size:13px!important;}
    [style*="font-weight: 300"]{font-weight:500!important;}
    .paper-hero{display:grid;grid-template-columns:1.15fr 0.85fr;gap:56px;align-items:center;}
    .paper-steps{grid-template-columns:repeat(3,1fr);}
    @media(max-width:860px){.paper-hero{grid-template-columns:1fr;gap:40px;}.paper-steps{grid-template-columns:1fr;}}
    ` : ""}
  `;

  return (
    <div style={{fontFamily:Fb,minHeight:"100vh",background:C.cream}}>
      <style>{CSS}</style>

      {screen==="landing" && <Landing onBegin={()=>{setAuthStart("signup");setScreen("onboard");}} onSignIn={()=>{setAuthStart("login");setScreen("auth");}} />}
      {screen==="onboard" && <Onboard step={onboardStep} onNext={()=>onboardStep<3?setOnboardStep(s=>s+1):setScreen("auth")} onBack={()=>onboardStep>0?setOnboardStep(s=>s-1):setScreen("landing")} onSkip={()=>setScreen("auth")} />}
      {screen==="auth" && <Auth startMode={authStart} form={form} setForm={setForm} onBack={()=>setScreen(authStart === "login" ? "landing" : "onboard")}
        onSignup={async()=>{
          const { data, error } = await supabase.auth.signUp({ email: form.email.trim(), password: form.password,
            options: { data: { name: form.name.trim() }, emailRedirectTo: window.location.origin } });
          if (error) return { error: error.message };
          if (!data.session) return { checkEmail: true };
          if (await loadSignedIn(data.user)) goHome();
          return {};
        }}
        onLogin={async()=>{
          const { data, error } = await supabase.auth.signInWithPassword({ email: form.email.trim(), password: form.password });
          if (error) return { error: /confirm/i.test(error.message) ? "Please confirm your email first — check your inbox for the link." : error.message, unconfirmed: /confirm/i.test(error.message) };
          if (await loadSignedIn(data.user)) goHome();
          return {};
        }}
        onForgot={async()=>{
          const { error } = await supabase.auth.resetPasswordForEmail(form.email.trim(), { redirectTo: `${window.location.origin}/reset-password` });
          return error ? { error: error.message } : { sent: true };
        }}
        onResend={async()=>{
          const { error } = await supabase.auth.resend({ type: "signup", email: form.email.trim(), options: { emailRedirectTo: window.location.origin } });
          return error ? { error: error.message } : { sent: true };
        }}
        onDemo={()=>{setUser({name:"Amira Hassan",email:"amira@email.com"});setScreen("dashboard");}} />}
      {screen==="resetPassword" && <ResetPasswordScreen onDone={()=>{ window.location.href = "/"; }} />}
      {screen==="account" && <AccountScreen user={user} setUser={setUser} onBack={()=>setScreen("dashboard")} />}
      {screen==="updates" && <UpdatesScreen partnership={partnership} onBack={()=>setScreen("dashboard")}
        onGo={(where, section)=>{ if (where === "compare" && section) { setCompareSection(section); setScreen("compare"); } else setScreen(where); }} />}
      {screen==="reframe" && active && (
        <ReframeScreen charter={active} rf={REFRAMES[active.reframe]} partnership={partnership} onBack={()=>setScreen("dashboard")} onBegin={()=>setScreen("survey")} />
      )}
      {screen==="finished" && finished && (
        <FinishedScreen finished={finished} partnership={partnership}
          next={computeNextStep({ storyDone, growthDone, done, partnership })}
          onAct={(st)=>{ if (st.kind==="story") startStory(); else if (st.kind==="growth") startGrowth(); else if (st.kind==="charter") startCharter(st.charter); else if (st.kind==="partner") setScreen("partner"); else setScreen("dashboard"); }}
          onDashboard={()=>setScreen("dashboard")} />
      )}

      {screen==="survey" && q && (
        <SurveyScreen q={q} step={step} total={curQs.length} curAns={curAns} canNext={canNext} isWorking={isWorking}
          setAns={setAns} onNext={nextQ} toggleWorking={toggleWorking} onBack={()=>setScreen("dashboard")}
          showExample={showExample} toggleExample={()=>setShowExample(p=>({...p,[qId]:!p[qId]}))}
          label={activeType==="story"?"🪞 Your Story":activeType==="growth"?"🌱 Growth Portfolio":`${active?.icon} ${active?.title}`}
          isNarrative={activeType!=="charter"} answerDate={answerDates[qId]}
          bg={activeType==="physical"||active?.id==="physical"?"#FDF6EF":activeType!=="charter"?C.soft:C.cream}
          laterStage={activeType==="charter" && active && qId && questionStage(active.id, qId) !== charterStage(active.id) ? stageInfo(questionStage(active.id, qId)) : null}
          flagged={activeType==="charter" && active ? !!flags[flagKey(active.id, qId)] : false}
          onToggleFlag={activeType==="charter" && active && user?.id && q && q.type!=="text" ? ()=>toggleFlag(active.id, qId) : null}
          noteMode={activeType==="charter" && active ? noteModeFor(active.id, q) : null}
          note={activeType==="charter" && active && qId ? (answerNotes[`${active.id}__${qId}`] || "") : ""}
          onNote={(v)=>{ if (active && qId) setAnswerNotes(p=>({...p,[`${active.id}__${qId}`]:v.slice(0, NOTE_MAX)})); }}
        />
      )}

      {screen==="dashboard" && (
        <Dashboard user={user} done={done} storyDone={storyDone} growthDone={growthDone} witnessDone={witnessDone}
          progress={progress} doneCount={doneCount} totalSections={totalSections}
          onCharter={startCharter} onStory={startStory} onGrowth={startGrowth}
          onWitness={()=>setScreen("witness")} onProfile={()=>setScreen("profile")} onShare={()=>setShowShareModal(true)} shareSettings={shareSettings} setShareSettings={setShareSettings} onPrerequisite={()=>setScreen("prerequisite")} prerequisiteDone={prerequisiteDone} onPreview={()=>{setViewingProfileId(user.shareToken||user.id);setScreen("publicProfile");}}
          partnership={partnership} onPartner={()=>setScreen("partner")}
          openNotes={openNotes} onTalkList={()=>setScreen("talk")} onJournal={()=>setScreen("journal")} onFamily={()=>setScreen("familyLinks")}
          onUpdates={()=>setScreen("updates")} onAccount={()=>setScreen("account")} />
      )}
      {screen==="partner" && <PartnerScreen user={user} partnership={partnership} onChanged={loadPartnership} onBack={()=>setScreen("dashboard")}
          onCompare={(id)=>{setCompareSection(id);setScreen("compare");}}
          onSummary={()=>setScreen("summary")} onJournal={()=>setScreen("journal")} onYearOne={()=>setScreen("yearOne")} />}
      {screen==="compare" && compareSection && <ComparisonScreen user={user} sectionId={compareSection} flags={flags} onToggleFlag={toggleFlag} onBack={()=>setScreen("partner")} onTalkList={()=>setScreen("talk")} />}
      {screen==="talk" && <TalkListScreen user={user} partnership={partnership} onBack={()=>setScreen("dashboard")} />}
      {screen==="summary" && <NikahSummaryScreen onBack={()=>setScreen("partner")} />}
      {screen==="journal" && <JournalScreen user={user} onBack={()=>setScreen("dashboard")} />}
      {screen==="familyLinks" && <FamilyLinksScreen user={user} done={done} storyDone={storyDone} growthDone={growthDone} onBack={()=>setScreen("dashboard")} />}
      {screen==="yearOne" && <YearOneScreen onBack={()=>setScreen("partner")} />}
      {screen==="family" && <FamilyView token={familyToken} />}
      {screen==="publicProfile" && <PublicProfile userId={viewingProfileId} currentUserId={user?.id} />}
      {screen==="prerequisite" && <PrerequisiteScreen user={user} prerequisite={prerequisite} setPrerequisite={setPrerequisite} prerequisiteDone={prerequisiteDone} setPrerequisiteDone={setPrerequisiteDone} onDone={()=>setScreen("dashboard")} />}

      {screen==="witness" && (
        <WitnessScreen witness={witness} setWitness={setWitness} witnessDone={witnessDone}
          onSubmit={()=>{setWitnessDone(true);setScreen("dashboard");}} onBack={()=>setScreen("dashboard")} />
      )}

      {screen==="profile" && (
        <ProfileScreen user={user} done={done} storyDone={storyDone} growthDone={growthDone} witnessDone={witnessDone}
          storyAnswers={storyAnswers} growthAnswers={growthAnswers} workingThrough={workingThrough}
          answers={answers} witness={witness} answerDates={answerDates} progress={progress}
          profileUrl={profileUrl} copied={copied} setCopied={setCopied}
          profileTab={profileTab} setProfileTab={setProfileTab}
          onBack={()=>setScreen("dashboard")} onStory={startStory} onGrowth={startGrowth}
          onWitness={()=>setScreen("witness")}
          onShare={()=>setShowShareModal(true)} />
      )}

      {/* ── CONFIDENTIALITY MODAL ── */}
      {showShareModal && (
        <Modal labelledBy="share-title" onClose={()=>setShowShareModal(false)}
          panelStyle={{background:C.white,borderRadius:"20px 20px 0 0",padding:"32px 24px 40px",maxWidth:560,width:"100%",maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{width:36,height:4,background:C.parchment,borderRadius:2,margin:"0 auto 28px"}} />
            <div id="share-title" style={{fontFamily:Fd,color:C.green,fontSize:"clamp(22px,5vw,30px)",fontWeight:300,lineHeight:1.25,marginBottom:16}}>{CONFIDENTIALITY_TEXT.title}</div>
            <div style={{width:32,height:2,background:G_RULE,marginBottom:20}} />
            <p style={{color:C.muted,fontSize:13,lineHeight:1.85,fontWeight:400,marginBottom:24,whiteSpace:"pre-line"}}>{CONFIDENTIALITY_TEXT.body}</p>

            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:24}}>
              {CONFIDENTIALITY_TEXT.commitments.map((c,i)=>(
                <div key={i} style={{display:"flex",gap:12,alignItems:"flex-start",padding:"10px 14px",background:C.soft,borderRadius:8,border:`1px solid ${C.parchment}`}}>
                  <div style={{width:20,height:20,borderRadius:"50%",background:G_GOLD,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",marginTop:1}}>
                    <span style={{color:C.green,fontSize:12,fontWeight:700}}>{i+1}</span>
                  </div>
                  <span style={{fontSize:13,color:C.text,fontWeight:400,lineHeight:1.65}}>{c}</span>
                </div>
              ))}
            </div>

            <div style={{fontSize:12,color:C.muted,textAlign:"center",marginBottom:20,fontStyle:"italic"}}>{CONFIDENTIALITY_TEXT.sub}</div>

            <div>
                <div style={{fontSize:12,color:C.muted,marginBottom:12,fontWeight:500}}>Choose what to share:</div>
                {[
                  {id:"mental", label:"🧠 Mental Health & Emotional Regulation"},
                  {id:"career", label:"💼 Career & Ambition"},
                  {id:"cultural", label:"🛂 Cultural Identity & Immigration"},
                  {id:"aging", label:"🧓 Aging & End-of-Life Planning"},
                  {id:"joy", label:"🎭 Joy, Play & Humor"},
                  {id:"relationship", label:"💍 Relationship Charter"},
                  {id:"nikah", label:"🕌 Nikah & Relationship Charter"},
                  {id:"money_values", label:"💰 Money, Values & History"},
                  {id:"inlaws", label:"🏠 Extended Family & In-Laws"},
                  {id:"assumptions", label:"🔍 Assumptions Audit"},
                  {id:"conflict_history", label:"💔 Conflict History & Patterns"},
                  {id:"parenting", label:"👶 Parenting Philosophy"},
                  {id:"faith_journey", label:"☪️ Faith Journey"},
                  {id:"who_i_am", label:"🪞 Who I Actually Am"},
                  {id:"readiness", label:"🌿 Marriage Readiness Survey"},
                ].filter(item => done.includes(item.id)).map(item => (
                  <button type="button" key={item.id} className="btn-card" aria-pressed={!!shareSettings[item.id]} onClick={()=>setShareSettings(p=>({...p,[item.id]:!p[item.id]}))} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",marginBottom:8,background:shareSettings[item.id]?C.soft:C.white,border:`1px solid ${shareSettings[item.id]?C.gold:C.parchment}`,borderRadius:8,cursor:"pointer"}}>
                    <span style={{fontSize:13,color:C.text}}>{item.label}</span>
                    <span aria-hidden="true" style={{fontSize:12,color:shareSettings[item.id]?C.goldText:C.muted}}>{shareSettings[item.id]?"✓ visible":"hidden"}</span>
                  </button>
                ))}
                <div style={{fontSize:12,color:C.muted,marginBottom:12,marginTop:4}}>🪞 Your Story and 🌱 Growth Portfolio are always visible. 🔒 Financial, Physical & Unspoken are always private.</div>
                <button onClick={async()=>{await supabase.from("profiles").update({share_settings:shareSettings}).eq("id",user.id);navigator.clipboard.writeText(`${window.location.origin}/profile/${user.shareToken||user.id}`);setCopied(true);setTimeout(()=>{setCopied(false);setShowShareModal(false);},1800);}} style={{width:"100%",background:copied?C.gl:G_GOLD,color:copied?C.cream:C.onGold,border:"none",borderRadius:10,padding:"15px 24px",fontSize:14,fontWeight:600,transition:"background-color 0.2s, color 0.2s"}}>
                  {copied?"✓ Copied!":"Copy Profile Link"}
                </button>
                <div style={{textAlign:"center",marginTop:14}}>
                  <button onClick={async()=>{
                    if(!window.confirm("Reset your profile link? Every link you've sent so far will stop working. You'll get a new link to share."))return;
                    const{data,error}=await supabase.rpc("reset_share_link");
                    if(error){alert(error.message);return;}
                    setUser(u=>({...u,shareToken:data}));alert("Done — your old links no longer work. Copy your new link above.");
                  }} style={{background:"none",border:"none",color:C.muted,fontSize:12,textDecoration:"underline",textUnderlineOffset:3}}>Reset my profile link</button>
                  <div style={{fontSize:12,color:C.muted,marginTop:4}}>Stops every link you've already sent</div>
                </div>
              </div>
        </Modal>
      )}
    </div>
  );
}

// ─── LANDING ──────────────────────────────────────────────────────────────────
function Landing(props) {
  if (EDITORIAL) return <LandingEditorial {...props} />;
  if (PAPER) return <LandingPaper {...props} />;
  return MODERN ? <LandingModern {...props} /> : <LandingClassic {...props} />;
}

// Paper: a calm daylight landing that says what Mithaq does and who sees what.
const PAPER_STEPS = [
  ["Answer on your own", "Charters on faith, money, family, work and daily life. \u201cI\u2019m still working through this\u201d is always a real answer."],
  ["See where you meet", "When you\u2019re both ready, compare a charter. Sensitive answers show only whether you see it the same way, partly, or differently."],
  ["Talk before you commit", "Harder topics open as things get serious, so each conversation comes at its time."],
];
// Real questions from the app, with the labels the comparison screen uses.
const PAPER_EXAMPLE = [
  ["In-Laws", "How often would you want to see each other\u2019s parents?", "same"],
  ["In-Laws", "For big decisions, what role should parents play?", "overlap"],
  ["Cultural Identity", "Where do you picture living long-term?", "different"],
];
function LandingPaper({onBegin, onSignIn}) {
  const tone = { same: [C.green, tint(C.green, 0.08)], overlap: [C.goldText, tint(C.gold, 0.12)], different: [C.text, C.soft] };
  return (
    <div style={{minHeight:"100vh",background:C.cream,color:C.text,display:"flex",flexDirection:"column"}}>
      <header style={{maxWidth:1080,width:"100%",margin:"0 auto",padding:"22px 24px",display:"flex",alignItems:"center",gap:10}}>
        <Star size={16} fill={C.gold} />
        <span style={{fontFamily:Fd,color:C.green,fontSize:24,fontWeight:600}}>Mithaq</span>
        <span lang="ar" dir="rtl" style={{fontFamily:Fa,fontWeight:700,color:C.goldText,fontSize:20,lineHeight:1}}>ميثاق</span>
        <button type="button" onClick={onSignIn} style={{marginLeft:"auto",background:C.white,border:`1px solid ${C.parchment}`,borderRadius:8,padding:"9px 16px",fontSize:15,fontWeight:500,color:C.text}}>Sign in</button>
      </header>

      <main style={{flex:1,maxWidth:1080,width:"100%",margin:"0 auto",padding:"40px 24px 64px"}}>
        <div className="paper-hero">
          <div>
            <h1 style={{fontFamily:Fd,color:C.green,fontSize:"clamp(40px,6vw,64px)",fontWeight:600,lineHeight:1.04,letterSpacing:"-0.02em",marginBottom:24,maxWidth:560,textWrap:"balance"}}>
              Prepare for marriage with intention.
            </h1>
            <p style={{fontSize:19,lineHeight:1.55,maxWidth:520,marginBottom:14}}>
              Answer privately, on your own time. Your partner sees where you agree, partly agree, or differ, so you know which conversations to have in person.
            </p>
            <p style={{fontSize:16,lineHeight:1.6,color:C.muted,maxWidth:520,marginBottom:32}}>
              Nothing you write is shared until you choose to share it.
            </p>
            <div style={{display:"flex",flexWrap:"wrap",gap:12,alignItems:"center"}}>
              <button type="button" onClick={onBegin} style={{background:C.green,color:C.cream,border:"none",borderRadius:999,padding:"14px 28px",fontSize:16,fontWeight:500}}>Begin</button>
              <button type="button" onClick={onSignIn} style={{background:"none",border:"none",color:C.text,fontSize:15,padding:"12px 4px",textDecoration:"underline",textUnderlineOffset:4,textDecorationColor:C.parchment}}>Already started? Sign in</button>
            </div>
          </div>

          <figure aria-label="Example of a comparison" style={{margin:0,background:C.white,border:`1px solid ${C.parchment}`,borderRadius:12,padding:"20px 20px 8px",boxShadow:`0 1px 2px ${tint(C.text,0.04)}, 0 8px 24px ${tint(C.text,0.05)}`}}>
            <figcaption style={{fontSize:14,color:C.muted,marginBottom:12}}>What comparing looks like</figcaption>
            <ul style={{listStyle:"none"}}>
              {PAPER_EXAMPLE.map(([charter, q, align]) => (
                <li key={q} style={{borderTop:`1px solid ${C.parchment}`,padding:"14px 0"}}>
                  <div style={{fontSize:13,color:C.muted,marginBottom:4}}>{charter}</div>
                  <div style={{fontSize:16,lineHeight:1.45,marginBottom:10}}>{q}</div>
                  <span style={{display:"inline-block",fontSize:14,fontWeight:500,color:tone[align][0],background:tone[align][1],borderRadius:999,padding:"4px 12px"}}>{ALIGN_TEXT[align]}</span>
                  {align !== "same" && <div style={{fontSize:14,color:C.muted,marginTop:8}}>Talk about it in person, gently.</div>}
                </li>
              ))}
            </ul>
          </figure>
        </div>

        <ol style={{listStyle:"none",marginTop:72,display:"grid",gap:28}} className="paper-steps">
          {PAPER_STEPS.map(([t, d]) => (
            <li key={t}>
              <Star size={12} fill={C.gold} style={{marginBottom:12}} />
              <h2 style={{fontSize:18,fontWeight:600,color:C.text,marginBottom:6}}>{t}</h2>
              <p style={{fontSize:16,lineHeight:1.6,color:C.muted}}>{d}</p>
            </li>
          ))}
        </ol>
      </main>

      <footer style={{borderTop:`1px solid ${C.parchment}`,background:C.soft}}>
        <div style={{maxWidth:1080,margin:"0 auto",padding:"32px 24px"}}>
          <p style={{fontFamily:Fd,color:C.green,fontSize:20,fontWeight:500,lineHeight:1.45,maxWidth:560}}>“And He created for you from yourselves mates that you may find tranquility in them…”</p>
          <p style={{color:C.goldText,fontSize:14,marginTop:8}}>Surah Ar-Rum 30:21</p>
        </div>
      </footer>
    </div>
  );
}

// ─── EDITORIAL LANDING ───────────────────────────────────────────────────────
// A chaptered story: one idea per screen, cream and deep green taking turns.
// Every chapter is fully visible at rest; only the opening title animates in.
const ED_TOPICS = ["Faith", "Money", "In-laws", "Parenting", "Career", "Culture", "Joy", "Emotional health", "Daily life", "Intimacy", "Later life"];
// A fine eight-pointed star drawn in hairlines, for the opening composition.
function KhatamLines({ size = 360, color = C.gold, opacity = 0.55 }) {
  return (
    <svg viewBox="0 0 200 200" width="100%" style={{ maxWidth: size, display: "block" }} aria-hidden="true">
      <g fill="none" stroke={color} strokeOpacity={opacity} strokeWidth="0.6">
        <polygon points={starPoints(100, 100, 96)} />
        <polygon points={starPoints(100, 100, 74)} />
        <circle cx="100" cy="100" r="52" />
        <rect x="54" y="54" width="92" height="92" />
        <rect x="54" y="54" width="92" height="92" transform="rotate(45 100 100)" />
      </g>
    </svg>
  );
}
function LandingEditorial({ onBegin, onSignIn }) {
  const counts = STAGES.map(st => [st, STAGE_ORDER[st.id].length]);
  const tone = { same: [C.green, tint(C.green, 0.09)], overlap: [C.goldText, tint(C.gold, 0.14)], different: [C.text, C.soft] };
  return (
    <div className="ed" style={{ background: C.cream, color: C.text }}>
      <header className="ed-wrap ed-nav">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Star size={15} fill={C.gold} />
          <span style={{ fontFamily: Fd, color: C.green, fontSize: 25, fontWeight: 600, letterSpacing: "0.01em" }}>Mithaq</span>
        </div>
        <button type="button" onClick={onSignIn} className="ed-link">Sign in</button>
      </header>

      <section className="ed-wrap ed-hero" aria-labelledby="ed-title">
        <div className="ed-hero-text">
          <h1 id="ed-title" className="ed-rise ed-display">Prepare for marriage with intention.</h1>
          <p className="ed-rise ed-lede" style={{ animationDelay: "120ms" }}>
            Answer privately, on your own time. Your partner sees where you agree, partly agree, or differ, so you know which conversations to have in person.
          </p>
          <div className="ed-rise ed-actions" style={{ animationDelay: "220ms" }}>
            <button type="button" onClick={onBegin} className="ed-btn">Begin</button>
            <button type="button" onClick={onSignIn} className="ed-link">Already started? Sign in</button>
          </div>
          <p className="ed-rise ed-small" style={{ animationDelay: "300ms" }}>Nothing you write is shared until you choose to share it.</p>
        </div>
        <figure className="ed-seal" aria-label="The word mithaq">
          <div className="ed-seal-star"><KhatamLines /></div>
          <div className="ed-seal-word" lang="ar" dir="rtl">ميثاق</div>
          <figcaption>
            <span style={{ fontFamily: Fd, fontSize: 22, color: C.green, fontWeight: 500 }}>mīthāq</span>
            <span>a solemn covenant</span>
          </figcaption>
        </figure>
      </section>

      <section className="ed-band ed-dark" aria-labelledby="ed-c1">
        <div className="ed-wrap ed-chapter">
          <p className="ed-chapter-mark">The conversations that get skipped</p>
          <h2 id="ed-c1" className="ed-statement">A name, a career, a photo, a family background. Then two people are asked to decide whether to build a life together.</h2>
          <p className="ed-body-dark">Mithaq makes room for the rest, one charter at a time.</p>
          <ul className="ed-topics" aria-label="What the charters cover">
            {ED_TOPICS.map(t => <li key={t}>{t}</li>)}
          </ul>
        </div>
      </section>

      <section className="ed-band" aria-labelledby="ed-c2">
        <div className="ed-wrap ed-split">
          <div>
            <p className="ed-chapter-mark ed-mark-light">How it works</p>
            <h2 id="ed-c2" className="ed-h2">Private first. Together when you're ready.</h2>
            <ol className="ed-steps">
              {PAPER_STEPS.map(([t, d], i) => (
                <li key={t}>
                  <span className="ed-step-n" aria-hidden="true">{["i", "ii", "iii"][i]}</span>
                  <div><h3>{t}</h3><p>{d}</p></div>
                </li>
              ))}
            </ol>
          </div>
          <figure className="ed-compare" aria-label="Example of a comparison">
            <figcaption>What comparing looks like</figcaption>
            <ul>
              {PAPER_EXAMPLE.map(([charter, q, align]) => (
                <li key={q}>
                  <div className="ed-compare-charter">{charter}</div>
                  <div className="ed-compare-q">{q}</div>
                  <span className="ed-pill" style={{ color: tone[align][0], background: tone[align][1] }}>{ALIGN_TEXT[align]}</span>
                  {align !== "same" && <div className="ed-compare-note">Talk about it in person, gently.</div>}
                </li>
              ))}
            </ul>
          </figure>
        </div>
      </section>

      <section className="ed-band ed-deep" aria-labelledby="ed-c3">
        <div className="ed-wrap">
          <p className="ed-chapter-mark ed-mark-light">The journey</p>
          <h2 id="ed-c3" className="ed-h2" style={{ maxWidth: 640 }}>Harder topics open as things get serious.</h2>
          <ol className="ed-path">
            {counts.map(([st, n]) => (
              <li key={st.id}>
                <span className="ed-path-dot" aria-hidden="true" />
                <h3>{st.label}</h3>
                <p className="ed-path-count">{n} {n === 1 ? "charter" : "charters"}</p>
                <p>{st.sub}</p>
              </li>
            ))}
            <li className="ed-path-end">
              <span className="ed-path-dot" aria-hidden="true"><Star size={14} fill={C.gold} /></span>
              <h3>Nikah</h3>
              <p>Bring a one-page summary of what you agreed to your imam.</p>
            </li>
          </ol>
        </div>
      </section>

      <section className="ed-band" aria-labelledby="ed-c4">
        <div className="ed-wrap ed-trust">
          <h2 id="ed-c4" className="ed-h2">Built for trust.</h2>
          <dl>
            <div><dt>Your answers stay yours</dt><dd>Nothing is shared until you choose to compare a charter.</dd></div>
            <div><dt>Sensitive answers stay quiet</dt><dd>For the most personal questions, your partner sees only whether you agree.</dd></div>
            <div><dt>Family sees a summary</dt><dd>A family link shows what you choose to share, never your private answers.</dd></div>
          </dl>
        </div>
      </section>

      <section className="ed-band ed-dark ed-verse" aria-label="Surah Ar-Rum 30:21">
        <div className="ed-wrap" style={{ textAlign: "center" }}>
          <Star size={18} fill={C.goldL} style={{ margin: "0 auto 28px", display: "block" }} />
          <p lang="ar" dir="rtl" className="ed-arabic">وَمِنْ آيَاتِهِ أَنْ خَلَقَ لَكُم مِّنْ أَنفُسِكُمْ أَزْوَاجًا لِّتَسْكُنُوا إِلَيْهَا وَجَعَلَ بَيْنَكُم مَّوَدَّةً وَرَحْمَةً</p>
          <p className="ed-verse-en">“And of His signs is that He created for you from yourselves mates that you may find tranquility in them; and He placed between you affection and mercy.”</p>
          <p className="ed-verse-ref">Surah Ar-Rum 30:21</p>
          <div style={{ marginTop: 44 }}><button type="button" onClick={onBegin} className="ed-btn ed-btn-gold">Begin</button></div>
        </div>
      </section>

      <footer className="ed-wrap ed-foot">
        <span>Mithaq</span>
        <span>The Qur'an calls marriage <em>mīthāqan ghalīẓā</em>, a firm covenant (An-Nisa 4:21).</span>
      </footer>
    </div>
  );
}

// The mihrab-arch window: lapis, a gold star lattice, ميثاق, and the verse.
function ArchWindow() {
  const id = "archclip";
  return (
    <div className="arch-reveal" style={{position:"relative",width:"100%",aspectRatio:"5 / 7"}}>
      <svg viewBox="0 0 100 140" preserveAspectRatio="none" style={{position:"absolute",inset:0,width:"100%",height:"100%"}} aria-hidden="true">
        <defs>
          <clipPath id={id} clipPathUnits="objectBoundingBox"><path d="M0,1 L0,0.3714 C0,0.1857 0.26,0.0643 0.5,0 C0.74,0.0643 1,0.1857 1,0.3714 L1,1 Z" /></clipPath>
        </defs>
        <path d="M0,140 L0,52 C0,26 26,9 50,0 C74,9 100,26 100,52 L100,140 Z" fill={C.green} />
      </svg>
      <div style={{position:"absolute",inset:0,backgroundImage:latticeBg(C.goldL, 0.22, 44),clipPath:`url(#${id})`,WebkitMaskImage:"linear-gradient(to bottom, #000 30%, rgba(0,0,0,0.25) 100%)",maskImage:"linear-gradient(to bottom, #000 30%, rgba(0,0,0,0.25) 100%)"}} />
      <svg viewBox="0 0 100 140" preserveAspectRatio="none" style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}} aria-hidden="true">
        <path d="M5,140 L5,53.5 C5,30 28,14 50,5.5 C72,14 95,30 95,53.5 L95,140" fill="none" stroke={C.goldL} strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
      </svg>
      <div style={{position:"absolute",left:"12%",right:"12%",top:"30%",bottom:"8%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"space-between",textAlign:"center"}}>
        <div>
          <Star size={18} fill={C.goldL} style={{marginBottom:14}} />
          <div lang="ar" dir="rtl" style={{fontFamily:Fa,fontWeight:700,color:C.goldL,fontSize:"clamp(52px,7.5vw,92px)",lineHeight:1.1}}>ميثاق</div>
          <div style={{fontFamily:Fd,color:"rgba(243,245,244,0.75)",fontSize:15,marginTop:8}}>a solemn covenant</div>
        </div>
        <div className="arch-verse-in" style={{background:"rgba(10,20,45,0.55)",borderTop:`1px solid rgba(201,162,74,0.35)`,padding:"14px 14px 12px",width:"100%"}}>
          <div style={{fontFamily:Fd,color:"#EEF1F0",fontSize:"clamp(14px,1.5vw,16px)",lineHeight:1.55}}>“And He created for you from yourselves mates that you may find tranquility in them…”</div>
          <div style={{color:C.goldL,fontSize:12,marginTop:6}}>Surah Ar-Rum 30:21</div>
        </div>
      </div>
    </div>
  );
}

function LandingModern({onBegin}) {
  const steps = [
    ["Answer privately", "Charters on faith, money, family, work and daily life — in your own words."],
    ["Compare together", "When you're both ready, reveal a charter side by side and talk through the differences."],
    ["Agree before the nikah", "Settle what matters, sign together, and bring a summary to your imam."],
  ];
  return (
    <div style={{minHeight:"100vh",background:C.cream}}>
      <div style={{maxWidth:1120,margin:"0 auto",padding:"0 24px"}}>
        <div style={{paddingTop:28,display:"flex",alignItems:"center",gap:10}}>
          <Star size={18} fill={C.gold} />
          <span style={{fontFamily:Fd,color:C.green,fontSize:24,letterSpacing:0.5}}>Mithaq</span>
        </div>
        <div className="hero-grid" style={{padding:"56px 0 72px"}}>
          <div>
            <h1 style={{fontFamily:Fd,color:C.green,fontSize:"clamp(40px,5.6vw,68px)",fontWeight:400,lineHeight:1.08,letterSpacing:-0.5,marginBottom:28,maxWidth:560}}>
              Prepare for marriage with intention.
            </h1>
            <p style={{color:C.text,fontSize:18,lineHeight:1.7,maxWidth:500,marginBottom:14}}>
              Most marriages that struggle don't fail on compatibility. They fail on the conversations nobody had before the nikah.
            </p>
            <p style={{color:C.muted,fontSize:16,lineHeight:1.75,maxWidth:500,marginBottom:36}}>
              Mithaq helps two people know themselves honestly, then talk through faith, money, family and daily life — before they commit.
            </p>
            <button onClick={onBegin} style={{background:C.green,color:C.cream,border:"none",borderRadius:6,padding:"16px 30px",fontSize:16,fontWeight:500,letterSpacing:0.2}}>
              Begin your journey
            </button>
            <ol style={{listStyle:"none",marginTop:52,display:"grid",gap:18,maxWidth:500}}>
              {steps.map(([t, d], i) => (
                <li key={t} style={{display:"flex",gap:14,alignItems:"flex-start"}}>
                  <span style={{position:"relative",width:30,height:30,flexShrink:0,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>
                    <Star size={30} fill="none" stroke={C.gold} strokeWidth={1.1} style={{position:"absolute",inset:0}} />
                    <span style={{fontFamily:Fd,fontSize:14,color:C.green,position:"relative"}}>{i + 1}</span>
                  </span>
                  <span>
                    <span style={{display:"block",fontFamily:Fd,fontSize:19,color:C.green}}>{t}</span>
                    <span style={{display:"block",fontSize:14,color:C.muted,lineHeight:1.6,marginTop:2}}>{d}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
          <div className="hero-arch">
            <ArchWindow />
            <div className="arch-verse-out" style={{textAlign:"center",marginTop:16}}>
              <div style={{fontFamily:Fd,color:C.green,fontSize:17,lineHeight:1.55}}>“And He created for you from yourselves mates that you may find tranquility in them…”</div>
              <div style={{color:C.goldText,fontSize:13,marginTop:6}}>Surah Ar-Rum 30:21</div>
            </div>
          </div>
        </div>
      </div>
      <div style={{height:14,backgroundImage:latticeBg(C.gold, 0.35, 14),borderTop:`1px solid ${C.parchment}`}} />
    </div>
  );
}

function LandingClassic({onBegin}) {
  return (
    <div style={{minHeight:"100vh",background:C.green,position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",right:-100,top:-100,width:450,height:450,borderRadius:"50%",background:"radial-gradient(circle,rgba(184,137,42,0.15) 0%,transparent 70%)"}} />
      <div style={{maxWidth:600,margin:"0 auto",padding:"0 24px",display:"flex",flexDirection:"column",minHeight:"100vh"}}>
        <div style={{paddingTop:32,fontFamily:Fd,color:C.goldL,fontSize:22,fontWeight:600,letterSpacing:1}}>Mithaq</div>
        <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"center",paddingBottom:60}} className="fade">
          <div style={{color:C.goldL,fontSize:12,letterSpacing:4,textTransform:"uppercase",marginBottom:18}}>A Sacred Toolkit for Marriage</div>
          <h1 style={{fontFamily:Fd,color:C.cream,fontSize:"clamp(36px,7vw,64px)",fontWeight:300,lineHeight:1.1,marginBottom:10}}>
            Prepare for marriage<br/><em style={{color:C.goldL}}>with intention.</em>
          </h1>
          <div style={{width:40,height:2,background:G_RULE,margin:"22px 0"}} />
          <p style={{color:"rgba(248,243,236,0.75)",fontSize:15,lineHeight:1.85,maxWidth:460,fontWeight:400,marginBottom:14}}>
            Most marriage attempts fail before they start — not from incompatibility, but from <em style={{color:"rgba(248,243,236,0.85)"}}>poor self-presentation and unspoken assumptions.</em>
          </p>
          <p style={{color:"rgba(248,243,236,0.75)",fontSize:13,lineHeight:1.8,maxWidth:440,fontWeight:400,marginBottom:32}}>
            Mithaq helps you package yourself honestly — your story, your growth, your actual self — and have the conversations that matter before the nikah.
          </p>
          <div style={{margin:"0 0 32px",padding:"16px 20px",borderLeft:"2px solid rgba(184,137,42,0.3)",background:"rgba(184,137,42,0.05)"}}>
            <div style={{fontFamily:Fd,color:C.goldL,fontSize:16,fontStyle:"italic",marginBottom:4}}>"And He created for you from yourselves mates that you may find tranquility in them..."</div>
            <div style={{color:"rgba(248,243,236,0.75)",fontSize:12,letterSpacing:1.5}}>— SURAH AR-RUM 30:21</div>
          </div>
          <button onClick={onBegin} style={{alignSelf:"flex-start",background:G_GOLD,color:C.onGold,border:"none",borderRadius:40,padding:"14px 32px",fontSize:15,fontWeight:600}}>
            Begin Your Journey →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ONBOARD ──────────────────────────────────────────────────────────────────
// Paper softens the first slide so it doesn't open on a judgment.
const PAPER_FIRST_SLIDE = { label:"Why Mithaq", title:"Good marriages rest on conversations people often skip.", body:"A name, a career, a photo, a family background \u2014 and then two people are asked to decide whether to build a life together.\n\nMithaq makes room for the rest: faith, money, family, work, and the small habits of daily life.", cta:"Tell me more" };
function Onboard({step,onNext,onBack,onSkip}) {
  const s0 = ONBOARD_SLIDES[step];
  const s = PAPER ? (step === 0 ? PAPER_FIRST_SLIDE : {...s0, cta: s0.cta.replace(/\s*\u2192$/, "")}) : s0;
  return (
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",padding:"0 24px"}}>
      <div style={{maxWidth:540,margin:"0 auto",width:"100%",flex:1,display:"flex",flexDirection:"column"}}>
        <div style={{paddingTop:28,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:13,padding:0}}>← Back</button>
          <div style={{display:"flex",gap:6}}>
            {ONBOARD_SLIDES.map((_,i)=><div key={i} style={{width:i===step?20:6,height:6,borderRadius:3,background:i===step?C.green:C.parchment,transition:"width 0.3s, background-color 0.3s"}} />)}
          </div>
          {PAPER && onSkip
            ? <button type="button" onClick={onSkip} style={{background:"none",border:"none",color:C.muted,fontSize:14,padding:"8px 0",textDecoration:"underline",textUnderlineOffset:3}}>Skip intro</button>
            : <div style={{fontSize:12,color:C.muted}}>{step+1}/{ONBOARD_SLIDES.length}</div>}
        </div>
        <div className="fade" key={step} style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"center",padding:"20px 0 80px"}}>
          {FLAT
            ? <div style={{marginBottom:22}}><Medallion size={44} /></div>
            : <div style={{fontSize:44,marginBottom:20}}>{s.icon}</div>}
          {PAPER ? null : FLAT
            ? <div style={{fontSize:15,color:C.turq,marginBottom:10,fontWeight:500}}>{s.label}</div>
            : <div style={{fontSize:12,letterSpacing:3,textTransform:"uppercase",color:C.goldText,marginBottom:12,fontWeight:500}}>{s.label}</div>}
          <h2 style={{fontFamily:Fd,fontSize:PAPER?"clamp(28px,5vw,40px)":"clamp(24px,5vw,38px)",fontWeight:PAPER?FW:300,lineHeight:PAPER?1.2:1.25,color:PAPER?C.green:C.text,marginBottom:24,textWrap:PAPER?"balance":undefined}}>{s.title}</h2>
          {!PAPER && <div style={{width:36,height:2,background:G_RULE,marginBottom:24}} />}
          <p style={{color:PAPER?C.text:C.muted,fontSize:PAPER?17:15,lineHeight:PAPER?1.65:1.9,fontWeight:400,marginBottom:32,whiteSpace:"pre-line",maxWidth:PAPER?"60ch":undefined}}>{s.body}</p>
          <button onClick={onNext} style={{alignSelf:"flex-start",background:FLAT?C.green:G_GREEN,color:C.cream,border:"none",borderRadius:FLAT?6:40,padding:"13px 28px",fontSize:14,fontWeight:500}}>{s.cta}</button>
        </div>
      </div>
    </div>
  );
}

// ─── AUTH ─────────────────────────────────────────────────────────────────────
function PrerequisiteScreen({user, prerequisite, setPrerequisite, prerequisiteDone, setPrerequisiteDone, onDone}) {
  const [draft, setDraft] = useState(prerequisite || "");
  const [saving, setSaving] = useState(false);

  const prompts = [
    "Is there something about your health, family situation, or past that has shaped previous connections?",
    "Is there a life circumstance that significantly affects your availability or lifestyle?",
    "Is there something you've been afraid to say early — that you wish you had said earlier?",
  ];

  const save = async () => {
    setSaving(true);
    await supabase.from("profiles").update({prerequisite: draft}).eq("id", user.id);
    setPrerequisite(draft);
    setPrerequisiteDone(!!draft.trim());
    setSaving(false);
    onDone();
  };

  return (
    <div style={{minHeight:"100vh",background:C.cream,padding:"40px 24px"}}>
      <div style={{maxWidth:560,margin:"0 auto"}}>
        <button onClick={onDone} style={{background:"none",border:"none",color:C.muted,fontSize:13,padding:0,marginBottom:32,cursor:"pointer"}}>← Back</button>
        <div style={{fontSize:12,letterSpacing:3,textTransform:"uppercase",color:C.goldText,marginBottom:12}}>Before We Go Further</div>
        <h1 id="prereq-title" style={{fontFamily:Fd,color:C.green,fontSize:32,fontWeight:300,marginBottom:8}}>What I want you to know first</h1>
        <p style={{color:C.muted,fontSize:14,lineHeight:1.8,marginBottom:32}}>Before someone invests time and heart getting to know you, is there anything they deserve to know first? This is not a confession — it is an act of respect. You are giving someone the dignity of an informed choice.</p>

        <div style={{background:C.soft,border:`1px solid ${C.parchment}`,borderRadius:12,padding:20,marginBottom:24}}>
          <div style={{fontSize:12,color:C.muted,marginBottom:12,fontWeight:500}}>Reflect on these before writing:</div>
          {prompts.map((p,i) => (
            <div key={i} style={{display:"flex",gap:10,marginBottom:10}}>
              <div style={{color:C.goldText,fontSize:14,flexShrink:0}}>◦</div>
              <div style={{color:C.text,fontSize:13,lineHeight:1.7}}>{p}</div>
            </div>
          ))}
        </div>

        <textarea
          aria-labelledby="prereq-title"
          aria-describedby="prereq-note"
          value={draft}
          onChange={e=>setDraft(e.target.value)}
          placeholder="Write in your own words, with whatever tone feels honest..."
          style={{width:"100%",minHeight:180,fontSize:14,lineHeight:1.8,padding:"16px",border:`1.5px solid ${C.parchment}`,borderRadius:12,background:C.white,color:C.text,resize:"vertical",boxSizing:"border-box",fontFamily:"inherit"}}
          onFocus={e=>e.target.style.borderColor=C.gold}
          onBlur={e=>e.target.style.borderColor=C.parchment}
        />
        <div id="prereq-note" style={{fontSize:12,color:C.muted,marginTop:8,marginBottom:24}}>This will appear at the top of your profile — before your story, before everything else.</div>

        <button onClick={save} disabled={saving} style={{width:"100%",background:G_GREEN,color:C.cream,border:"none",borderRadius:10,padding:"15px 24px",fontSize:14,fontWeight:500,cursor:"pointer"}}>
          {saving ? "Saving..." : draft.trim() ? "Save & return to profile →" : "Skip for now →"}
        </button>
      </div>
    </div>
  );
}

// ─── PARTNER CONNECTION ───────────────────────────────────────────────────────
// Charter-by-charter status for a connected couple, with the reveal ritual.
function CompareList({partnerFirst, onOpen, stage}) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    const { data, error } = await supabase.rpc("compare_status");
    if (error) setErr(error.message);
    setRows(data || []);
  };
  useEffect(() => { load(); }, []);

  const ready = async (id) => {
    setBusy(id); setErr("");
    const { error } = await supabase.rpc("mark_reveal_ready", { p_section: id });
    if (error) setErr(error.message);
    await load();
    setBusy(null);
  };

  const checkIn = async (id) => {
    if (!window.confirm(`Start a check-in? The charter reopens so you can both update anything that's changed, compare again, and re-sign. Your signed version is kept.`)) return;
    setBusy(id); setErr("");
    const { error } = await supabase.rpc("start_checkin", { p_section: id });
    if (error) setErr(error.message);
    await load();
    setBusy(null);
  };

  const byId = {};
  (rows || []).forEach(r => { byId[r.section_id] = r; });
  const list = CHARTERS.filter(c => byId[c.id]);

  return (
    <div style={{marginBottom:28}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:12}}>
        <SectionLabel>Compare together</SectionLabel>
        <button onClick={load} style={{background:"none",border:"none",color:C.muted,fontSize:12}}>↻ Refresh</button>
      </div>
      {rows === null && <div style={{color:C.muted,fontSize:13}}>Loading...</div>}
      {rows !== null && list.length === 0 && (
        <div style={{padding:20,background:C.soft,borderRadius:12,color:C.muted,fontSize:13,lineHeight:1.7}}>
          When either of you finishes a charter, it will appear here. Once you've both finished the same one, you can reveal it together.
        </div>
      )}
      {list.map(c => {
        const r = byId[c.id];
        const both = r.me_done && r.partner_done;
        const open = both && r.me_ready && r.partner_ready;
        let status, action = null;
        const checkin = !r.signed_at && r.last_signed_at;
        const opensAt = charterStage(c.id);
        const locked = stageRank(opensAt) > stageRank(stage);
        if (locked) status = `${stageInfo(opensAt).icon} Opens when you're both ${stageInfo(opensAt).short} — answers stay private until then`;
        else if (!r.me_done) status = `${partnerFirst} has finished this — complete it to compare`;
        else if (checkin && !(r.me_ready && r.partner_ready)) status = r.me_ready
          ? `🔄 Check-in · you're ready ✓ — waiting for ${partnerFirst}`
          : `🔄 Check-in · last signed ${fmtDate(r.last_signed_at)}. Update any answers that changed (Dashboard → Completed), then tap I'm ready`;
        else if (!r.partner_done) status = `Waiting for ${partnerFirst} to finish`;
        else if (open && r.signed_at) status = `🤝 Signed together · ${fmtDate(r.signed_at)} · next check-in around ${fmtDate(addMonths(r.signed_at, CHECKIN_MONTHS))}`;
        else if (open) status = "Revealed — settle differences and sign";
        else if (r.me_ready) status = `You're ready ✓ — waiting for ${partnerFirst}`;
        else status = r.partner_ready ? `${partnerFirst} is ready ✓ — tap when you are` : "You've both finished — reveal when you're ready";

        if (locked) action = null;
        else if (open && r.signed_at) action = (
          <div style={{display:"flex",gap:6,flexShrink:0}}>
            <button onClick={()=>onOpen(c.id)} style={{background:C.white,color:C.green,border:`1px solid ${C.parchment}`,borderRadius:8,padding:"9px 12px",fontSize:13}}>View</button>
            <button disabled={busy===c.id} onClick={()=>checkIn(c.id)} style={{background:G_GREEN,color:C.cream,border:"none",borderRadius:8,padding:"9px 12px",fontSize:13,whiteSpace:"nowrap"}}>Check in</button>
          </div>
        );
        else if (open) action = <button onClick={()=>onOpen(c.id)} style={{background:G_GOLD,color:C.onGold,border:"none",borderRadius:8,padding:"9px 16px",fontSize:13,fontWeight:600,whiteSpace:"nowrap"}}>Open →</button>;
        else if (both && !r.me_ready) action = <button disabled={busy===c.id} onClick={()=>ready(c.id)} style={{background:G_GREEN,color:C.cream,border:"none",borderRadius:8,padding:"9px 16px",fontSize:13,fontWeight:500,whiteSpace:"nowrap",opacity:busy===c.id?0.6:1}}>I'm ready</button>;

        return (
          <div key={c.id} style={{display:"flex",alignItems:"center",gap:14,padding:"14px 16px",background:locked?C.soft:C.white,border:`1px solid ${open&&!locked?C.gold:C.parchment}`,borderRadius:12,marginBottom:10,opacity:locked?0.75:1}}>
            <span style={{fontSize:20}}>{c.icon}</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontFamily:Fd,fontSize:16,fontWeight:600,color:C.text}}>{c.title}</div>
              <div style={{fontSize:12,color:open?C.goldText:C.muted,marginTop:2}}>{status}</div>
            </div>
            {action}
          </div>
        );
      })}
      <div style={{fontSize:12,color:C.muted,marginTop:8,lineHeight:1.6}}>Tip: tap "I'm ready" when you're sitting together, so you read the results side by side — not alone at midnight.</div>
      {err && <div style={{marginTop:12,fontSize:13,color:C.rose}}>{err}</div>}
    </div>
  );
}

// How a couple settles one difference: propose → partner confirms.
const RESOLUTION_LABELS = {
  agreed:     { icon: "🤝", label: "We agreed" },
  different:  { icon: "↔️", label: "Okay being different" },
  discussing: { icon: "💬", label: "Still discussing" },
};
const LOG_VERBS = {
  agreed: "proposed an agreement", different: "suggested okay being different",
  discussing: "wants to keep discussing", confirmed: "confirmed ✓",
};
const shortDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });

// The brief back-and-forth under a difference.
function DiscussionLog({ log, meId, partnerFirst }) {
  if (!log || !log.length) return null;
  return (
    <div style={{marginTop:10,paddingTop:10,borderTop:`1px dashed ${C.parchment}`}}>
      {log.map((e, i) => {
        const who = e.user_id === meId ? "You" : partnerFirst;
        return (
          <div key={i} style={{fontSize:12,color:C.muted,lineHeight:1.6,marginBottom:4}}>
            <span style={{color:C.text,fontWeight:500}}>{who}</span> {LOG_VERBS[e.action] || e.action}
            {e.action === "agreed" && e.agreed_text ? <> — <span style={{color:C.text}}>“{e.agreed_text}”</span></> : null}
            {e.message ? <> · <span style={{color:C.text,fontStyle:"italic"}}>“{e.message}”</span></> : null}
            <span style={{color:C.muted}}> · {shortDate(e.created_at)}</span>
          </div>
        );
      })}
    </div>
  );
}

// How a couple settles one difference: propose (with an optional message) → partner confirms.
function ResolutionControl({ res, log, meId, partnerFirst, locked, onSet, onConfirm }) {
  const [composing, setComposing] = useState(null);   // status being written
  const [text, setText] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const open = (status) => {
    setText(status === "agreed" ? (res?.agreed_text || "") : "");
    setMessage(""); setComposing(status);
  };
  const submit = async () => {
    setBusy(true);
    await onSet(composing, text.trim(), message.trim());
    setBusy(false); setComposing(null);
  };
  const confirm = async () => { setBusy(true); await onConfirm(); setBusy(false); };

  const chooser = (
    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
      {Object.entries(RESOLUTION_LABELS).map(([id, r]) => (
        <button key={id} disabled={busy} onClick={()=>open(id)} style={{fontSize:12,padding:"7px 12px",borderRadius:20,border:`1px solid ${composing===id?C.gold:C.parchment}`,background:C.white,color:C.text,opacity:busy?0.6:1}}>{r.icon} {r.label}</button>
      ))}
    </div>
  );

  const composer = composing && (
    <div style={{marginTop:10,padding:12,background:C.white,border:`1px solid ${C.gold}`,borderRadius:10}}>
      <div style={{fontSize:13,color:C.green,fontWeight:500,marginBottom:8}}>{RESOLUTION_LABELS[composing].icon} {RESOLUTION_LABELS[composing].label}</div>
      {composing === "agreed" && (
        <textarea aria-label="What did you agree on?" value={text} onChange={e=>setText(e.target.value)} rows={2} placeholder="What did you agree on? e.g. We'll decide big purchases together; one of us tracks the budget."
          style={{width:"100%",fontSize:13,lineHeight:1.6,padding:"10px 12px",border:`1px solid ${C.parchment}`,borderRadius:8,background:C.cream,color:C.text,marginBottom:8}} />
      )}
      <input aria-label={composing === "discussing" ? "What do you still want to talk about? (optional)" : `A short note for ${partnerFirst} (optional)`} value={message} onChange={e=>setMessage(e.target.value)} maxLength={500}
        placeholder={composing === "discussing" ? `What do you still want to talk about? (optional)` : `A short note for ${partnerFirst} (optional)`}
        style={{width:"100%",fontSize:13,padding:"10px 12px",border:`1px solid ${C.parchment}`,borderRadius:8,background:C.cream,color:C.text,marginBottom:10}} />
      <div style={{display:"flex",gap:8}}>
        <button disabled={busy || (composing === "agreed" && !text.trim())} onClick={submit}
          style={{background:G_GREEN,color:C.cream,border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,opacity:busy||(composing==="agreed"&&!text.trim())?0.5:1}}>{busy?"Sending...":"Send"}</button>
        <button onClick={()=>setComposing(null)} style={{background:"none",border:"none",color:C.muted,fontSize:13}}>Cancel</button>
      </div>
    </div>
  );

  const box = (children, accent) => (
    <div style={{marginTop:12,padding:"12px 14px",background:C.soft,border:`1px solid ${accent || C.parchment}`,borderRadius:10}}>
      {children}
      <DiscussionLog log={log} meId={meId} partnerFirst={partnerFirst} />
    </div>
  );

  if (!res) return locked ? null : box(<>
    <div style={{fontSize:12,color:C.muted,marginBottom:8}}>How will you settle this one?</div>
    {chooser}{composer}
  </>);

  const r = RESOLUTION_LABELS[res.status];
  const mine = res.proposed_by === meId;
  const summary = (
    <div style={{fontSize:13,color:C.green,fontWeight:500}}>
      {r.icon} {r.label}{res.status === "agreed" && res.agreed_text ? ":" : ""}
      {res.status === "agreed" && res.agreed_text && <span style={{fontWeight:300,color:C.text}}> “{res.agreed_text}”</span>}
    </div>
  );

  if (res.status === "discussing") return box(<>
    {summary}
    <div style={{fontSize:12,color:C.muted,marginTop:4}}>On both of your Talk Lists until you settle it.</div>
    {!locked && <div style={{marginTop:10}}>{chooser}{composer}</div>}
  </>, C.amberL);

  if (res.confirmed) return box(<>
    {summary}
    <div style={{fontSize:12,color:C.goldText,marginTop:4}}>✓ Confirmed by both of you</div>
    {!locked && !composing && <button onClick={()=>open("discussing")} style={{background:"none",border:"none",color:C.muted,fontSize:12,padding:"6px 0 0",textDecoration:"underline"}}>Reopen</button>}
    {!locked && composer}
  </>, C.gold);

  return box(<>
    {summary}
    <div style={{fontSize:12,color:C.muted,marginTop:4}}>{mine ? `Waiting for ${partnerFirst} to confirm` : `${partnerFirst} proposed this — do you agree?`}</div>
    {!locked && (
      <div style={{display:"flex",gap:8,marginTop:10,flexWrap:"wrap",alignItems:"center"}}>
        {!mine && <button disabled={busy} onClick={confirm} style={{background:G_GOLD,color:C.onGold,border:"none",borderRadius:8,padding:"8px 14px",fontSize:13,fontWeight:600}}>✓ Confirm</button>}
        <span style={{fontSize:12,color:C.muted}}>{mine ? "Change:" : "Or respond:"}</span>
        {chooser}
      </div>
    )}
    {!locked && composer}
  </>);
}

// Dealbreaker triage: how differences are ordered. No score — only counts.
const TRIAGE_GROUPS = [
  { id: "must", title: "🔴 Must resolve before the nikah", count: "to resolve",
    sub: "Life-shaping differences. Settle these together before you go further." },
  { id: "care", title: "🟡 Discuss with care", count: "to discuss",
    sub: "Real differences worth a calm conversation — they rarely end a match." },
  { id: "grow", title: "🌱 Grow into", count: "to grow into",
    sub: "Differences in taste and style that couples usually adapt to over time." },
];
const DIFF_OPENERS = {
  must: "You see this differently. Start with what each of you pictures day to day.",
  care: "You see this differently. Start by asking what's behind the other's answer.",
  grow: "A difference in style — worth a smile and a short conversation.",
};

// A faint watermark with the viewer's own name, behind a partner's answers.
const watermark = (name) => {
  const safe = String(name || "").replace(/[<>&'"]/g, "");
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='220' height='80'><text x='14' y='48' transform='rotate(-14 110 40)' font-family='sans-serif' font-size='12' fill='${tint(C.stone,0.10)}'>${safe}</text></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

function ComparisonScreen({user, sectionId, flags, onToggleFlag, onBack, onTalkList}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [signing, setSigning] = useState(false);
  const load = async () => {
    const { data, error } = await supabase.rpc("get_comparison", { p_section: sectionId });
    if (error) setErr(error.message); else { setErr(""); setData(data); }
  };
  useEffect(() => { load(); }, [sectionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setResolution = async (qid, status, text, message) => {
    const { error } = await supabase.rpc("set_resolution", { p_section: sectionId, p_question: qid, p_status: status, p_text: text || "", p_message: message || "" });
    if (error) setErr(error.message);
    await load();
  };
  const confirmResolution = async (qid) => {
    const { error } = await supabase.rpc("confirm_resolution", { p_section: sectionId, p_question: qid });
    if (error) setErr(error.message);
    await load();
  };
  const { notes, save, remove } = useNotes(user?.id, data?.partner_id, data?.partner_name);

  const charter = CHARTERS.find(c => c.id === sectionId);
  const partnerFirst = data?.partner_name?.split(" ")[0] || "Your partner";
  const items = !data ? [] : (charter?.qs || []).map(q => {
    const a = parseAns(data.me?.[q.id]);
    const b = parseAns(data.partner?.[q.id]);
    if (a == null && b == null) return null;
    return { q, a, b, group: classifyPair(q, a, b) };
  }).filter(Boolean);

  // Check-in: what changed since the last signing
  const baselineAt = data?.baseline_signed_at;
  const changes = !baselineAt ? [] : (charter?.qs || []).map(q => {
    const meThen = parseAns(data.baseline_me?.[q.id]), meNow = parseAns(data.me?.[q.id]);
    const pThen = parseAns(data.baseline_partner?.[q.id]), pNow = parseAns(data.partner?.[q.id]);
    const meChanged = !sameAns(meThen, meNow), pChanged = !isAlign(pNow) && !sameAns(pThen, pNow);
    return (meChanged || pChanged) ? { q, meThen, meNow, pThen, pNow, meChanged, pChanged } : null;
  }).filter(Boolean);
  const changedIds = new Set(changes.map(c => c.q.id));

  // Triage: a difference's level is its default, raised to "must" if either of
  // you privately marked it (the server never says who).
  const raised = new Set(data?.raised || []);
  const levelOf = (qid) => raised.has(qid) ? "must" : weightOf(sectionId, qid);
  const diffs = items.filter(i => i.group === "talk" || i.group === "discuss").map(i => ({ ...i, level: levelOf(i.q.id) }));
  const engaged = atLeastEngaged(data?.stage);

  // Differences that must be settled before signing ("grow into" is optional)
  const required = diffs.filter(i => i.level !== "grow").map(i => i.q.id);
  const resolutions = data?.resolutions || {};
  const settled = (qid) => { const r = resolutions[qid]; return r && r.confirmed && r.status !== "discussing"; };
  const openCount = required.filter(qid => !settled(qid)).length;
  const signedAt = data?.signed_at;
  const locked = !!signedAt;
  const sign = async () => {
    setSigning(true);
    const { error } = await supabase.rpc("sign_charter", { p_section: sectionId, p_required: required });
    if (error) setErr(error.message);
    await load();
    setSigning(false);
  };

  const wm = watermark(user?.name);
  const noteIn = (mine, q) => q && (mine ? data?.me_notes : data?.partner_notes)?.[q.id];
  const box = (who, val, mine, q) => (
    <div className={mine ? undefined : "partner-private"} onCopy={mine ? undefined : (e => e.preventDefault())}
      style={{flex:"1 1 220px",minWidth:0,padding:"12px 14px",background:mine?C.soft:C.white,backgroundImage:mine?undefined:wm,border:`1px solid ${C.parchment}`,borderRadius:10}}>
      <div style={{fontSize:12,letterSpacing:2,textTransform:"uppercase",color:mine?C.muted:C.goldText,marginBottom:6,fontWeight:600}}>{who}</div>
      {isAlign(val) ? (
        <div style={{fontSize:14,color:C.text,lineHeight:1.6}}>
          <div style={{fontSize:12,color:C.muted,marginBottom:4}}>🔒 Answered privately</div>
          <div style={{fontWeight:500,color:val.__align === "same" ? C.green : C.text}}>{ALIGN_TEXT[val.__align]}</div>
          {val.__align !== "same" && val.__align !== "unanswered" && <div style={{fontSize:13,color:C.muted,marginTop:4}}>Talk about it in person, gently.</div>}
        </div>
      ) : (
        <div style={{fontSize:14,color:C.text,lineHeight:1.7,fontWeight:400,whiteSpace:"pre-wrap"}}>{val == null ? <span style={{color:C.muted,fontStyle:"italic"}}>No answer</span> : formatAns(val)}</div>
      )}
      {noteIn(mine, q) && (
        <div style={{fontSize:13,color:C.text,lineHeight:1.7,fontWeight:400,fontStyle:"italic",whiteSpace:"pre-wrap",marginTop:8,paddingLeft:10,borderLeft:`2px solid ${C.parchment}`}}>“{noteIn(mine, q)}”</div>
      )}
      {mine && noteIn(mine, q) && PRIVATE_QUESTIONS.has(flagKey(sectionId, q.id)) && (
        <div style={{fontSize:12,color:C.muted,marginTop:4}}>🔒 Only you can see this note.</div>
      )}
      {mine && q && PRIVATE_QUESTIONS.has(flagKey(sectionId, q.id)) && (
        <div style={{fontSize:12,color:C.muted,marginTop:6}}>🔒 Your exact answer isn't shown to {partnerFirst} — only whether you see it the same way.</div>
      )}
      {mine && q && q.type !== "text" && onToggleFlag && !locked && (
        <button className="no-print" onClick={()=>onToggleFlag(sectionId, q.id)} style={{background:"none",border:"none",padding:"8px 0 0",fontSize:12,color:flags?.[flagKey(sectionId, q.id)]?C.goldText:C.muted}}>
          {flags?.[flagKey(sectionId, q.id)] ? "★ Really matters to me" : "☆ Really matters to me"}
        </button>
      )}
    </div>
  );
  const row = ({ q, a, b }, opener) => (
    <div key={q.id} style={{marginBottom:22,paddingBottom:18,borderBottom:`1px solid ${C.parchment}`}}>
      <div style={{fontSize:14,color:C.text,fontWeight:500,lineHeight:1.6,marginBottom:opener?4:10}}>
        {q.text}
        {changedIds.has(q.id) && <span style={{marginLeft:8,fontSize:12,letterSpacing:1,textTransform:"uppercase",color:C.amberL,border:`1px solid ${C.amberL}`,borderRadius:10,padding:"2px 7px",verticalAlign:"middle"}}>changed</span>}
      </div>
      {opener && <div style={{fontSize:12,color:C.muted,fontStyle:"italic",marginBottom:10}}>{opener}</div>}
      <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
        {box("You", a, true, q)}
        {box(partnerFirst, b, false, q)}
      </div>
      {opener && (
        <ResolutionControl res={resolutions[q.id]} log={data.log?.[q.id]} meId={data.me_id} partnerFirst={partnerFirst} locked={locked}
          onSet={(status, text, message) => setResolution(q.id, status, text, message)}
          onConfirm={() => confirmResolution(q.id)} />
      )}
      {b != null && !isAlign(b) && data.partner_id && (
        <NoteControl note={notes[`${sectionId}__${q.id}`]}
          onSave={(tag, body) => save(sectionId, q.id, b, tag, body)}
          onDelete={() => remove(sectionId, q.id)} />
      )}
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:C.cream,padding:"40px 24px 80px"}}>
      <div style={{maxWidth:720,margin:"0 auto"}} className="fade">
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:28}}>
          <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:13,padding:0}}>← Back</button>
          <button onClick={onTalkList} style={{background:"none",border:"none",color:C.goldText,fontSize:13,padding:0}}>📝 Talk list</button>
        </div>
        <div style={{fontSize:12,letterSpacing:3,textTransform:"uppercase",color:C.goldText,marginBottom:10}}>Compare together</div>
        <h1 style={{fontFamily:Fd,color:C.green,fontSize:32,fontWeight:300,marginBottom:8}}>{charter ? `${charter.icon} ${charter.title}` : sectionId}</h1>
        {err && <div style={{marginTop:16,padding:"12px 16px",background:"rgba(58,28,28,0.06)",borderRadius:10,color:C.rose,fontSize:13}}>{err}</div>}
        {!data && !err && <div style={{color:C.muted,fontSize:13}}>Loading...</div>}
        {data && (
          <>
            <p style={{color:C.muted,fontSize:14,lineHeight:1.8,marginBottom:20}}>
              You and {partnerFirst}, side by side. Differences aren't failures — they're the conversations worth having before the nikah. Add a private note under any of {partnerFirst}'s answers to bring it up later.
            </p>
            {baselineAt && !locked && (
              <div style={{marginBottom:32,padding:"18px 20px",background:C.white,border:`1.5px solid ${C.gold}`,borderRadius:14}}>
                <div style={{fontFamily:Fd,fontSize:22,color:C.green,fontWeight:600,marginBottom:4}}>🔄 What changed since you signed</div>
                <div style={{fontSize:12,color:C.muted,marginBottom:14}}>Last signed {fmtDate(baselineAt)}</div>
                {changes.length === 0 ? (
                  <div style={{fontSize:14,color:C.text,lineHeight:1.7}}>Nothing has changed. If that's still true for both of you, you can sign again below.</div>
                ) : changes.map(c => (
                  <div key={c.q.id} style={{marginBottom:14,paddingBottom:12,borderBottom:`1px solid ${C.parchment}`}}>
                    <div style={{fontSize:13,color:C.text,fontWeight:500,lineHeight:1.6,marginBottom:6}}>{c.q.text}</div>
                    {c.meChanged && <div style={{fontSize:13,color:C.muted,lineHeight:1.7}}>You: <s>{formatAns(c.meThen) || "—"}</s> → <span style={{color:C.green,fontWeight:500}}>{formatAns(c.meNow) || "—"}</span></div>}
                    {c.pChanged && <div className={engaged ? "partner-private" : "partner-private no-print"} style={{fontSize:13,color:C.muted,lineHeight:1.7}}>{partnerFirst}: <s>{formatAns(c.pThen) || "—"}</s> → <span style={{color:C.green,fontWeight:500}}>{formatAns(c.pNow) || "—"}</span></div>}
                  </div>
                ))}
              </div>
            )}
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:32}}>
              {TRIAGE_GROUPS.map(g => {
                const n = diffs.filter(i => i.level === g.id).length;
                return n ? <div key={g.id} style={{fontSize:12,padding:"6px 12px",borderRadius:20,background:C.white,border:`1px solid ${C.parchment}`,color:C.text}}>{g.title.split(" ")[0]} {n} {g.count}</div> : null;
              })}
              {COMPARE_GROUPS.filter(g => g.id === "align" || g.id === "read").map(g => {
                const n = items.filter(i => i.group === g.id).length;
                return n ? <div key={g.id} style={{fontSize:12,padding:"6px 12px",borderRadius:20,background:C.white,border:`1px solid ${C.parchment}`,color:C.text}}>{g.title.split(" ")[0]} {n} {g.id === "align" ? "aligned" : "to read"}</div> : null;
              })}
            </div>
            <div className={engaged ? undefined : "print-locked"}>
              <div className="print-only" style={{fontSize:12,color:C.muted,marginBottom:16}}>{partnerFirst}'s answers can be printed once you're both engaged.</div>
              {TRIAGE_GROUPS.map(g => {
                const group = diffs.filter(i => i.level === g.id);
                if (!group.length) return null;
                return (
                  <div key={g.id} style={{marginBottom:36}}>
                    <div style={{fontFamily:Fd,fontSize:22,color:C.green,fontWeight:600,marginBottom:2}}>{g.title}</div>
                    <div style={{fontSize:12,color:C.muted,marginBottom:16}}>{g.sub}{g.id === "grow" ? " Settling these is optional before signing." : ""}</div>
                    {group.map(i => row(i, DIFF_OPENERS[g.id]))}
                  </div>
                );
              })}
              {COMPARE_GROUPS.filter(g => g.id === "align" || g.id === "read").map(g => {
                const group = items.filter(i => i.group === g.id);
                if (!group.length) return null;
                return (
                  <div key={g.id} style={{marginBottom:36}}>
                    <div style={{fontFamily:Fd,fontSize:22,color:C.green,fontWeight:600,marginBottom:2}}>{g.title}</div>
                    <div style={{fontSize:12,color:C.muted,marginBottom:16}}>{g.sub}</div>
                    {group.map(i => row(i, null))}
                  </div>
                );
              })}
            </div>

            {/* ── Sign together ── */}
            <div style={{marginTop:12,padding:"26px 24px",borderRadius:16,background:locked?G_GREEN:C.white,border:locked?"none":`1.5px solid ${C.parchment}`,textAlign:"center"}}>
              {locked ? (
                <>
                  <div style={{fontSize:30,marginBottom:8}}>🤝</div>
                  <div style={{fontFamily:Fd,fontSize:24,color:C.cream,fontWeight:300,marginBottom:6}}>Signed together</div>
                  <div style={{fontSize:13,color:tint(C.cream,0.75)}}>{new Date(signedAt).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})} · saved for your next check-in</div>
                </>
              ) : (
                <>
                  <div style={{fontFamily:Fd,fontSize:24,color:C.green,fontWeight:300,marginBottom:8}}>Sign this charter together</div>
                  <p style={{fontSize:13,color:C.muted,lineHeight:1.8,maxWidth:460,margin:"0 auto 18px"}}>
                    Signing says: we've read each other honestly, and these are our agreements. It isn't a legal contract — it's a values commitment.
                  </p>
                  {openCount > 0 ? (
                    <div style={{fontSize:13,color:C.amber}}>{openCount} difference{openCount>1?"s":""} still to settle before you can sign</div>
                  ) : data.me_signed ? (
                    <div style={{fontSize:13,color:C.goldText}}>You've signed ✓ — waiting for {partnerFirst}</div>
                  ) : (
                    <>
                      {data.partner_signed && <div style={{fontSize:13,color:C.goldText,marginBottom:12}}>{partnerFirst} has signed ✓</div>}
                      <button disabled={signing} onClick={sign} style={{background:G_GOLD,color:C.onGold,border:"none",borderRadius:10,padding:"14px 28px",fontSize:14,fontWeight:600,opacity:signing?0.6:1}}>{signing?"Signing...":"Sign as "+(user?.name?.split(" ")[0]||"me")}</button>
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// All my private notes, gathered for the next conversation.
function TalkListScreen({user, partnership, onBack}) {
  // Before the engaged stage, a partner's answers stay out of copies and prints.
  const partnerId = partnership?.status === "active" ? partnership.partner_id : null;
  const partnerLocked = !!partnerId && !atLeastEngaged(partnership?.stage);
  const hideQuote = (subjectId) => partnerLocked && subjectId === partnerId;
  const [notes, setNotes] = useState(null);
  const [tab, setTab] = useState("open");
  const [copied, setCopied] = useState(false);

  const [discussions, setDiscussions] = useState([]);
  useEffect(() => {
    if (!user?.id) { setNotes([]); return; }
    supabase.from("notes").select("*").order("created_at", { ascending: true })
      .then(({ data }) => setNotes(data || []));
    supabase.rpc("open_discussions").then(({ data }) => setDiscussions(Array.isArray(data) ? data : []));
  }, [user?.id]);

  const toggle = async (n) => {
    setNotes(p => p.map(x => x.id === n.id ? { ...x, discussed: !x.discussed } : x));
    await supabase.from("notes").update({ discussed: !n.discussed }).eq("id", n.id);
  };
  const del = async (n) => {
    setNotes(p => p.filter(x => x.id !== n.id));
    await supabase.from("notes").delete().eq("id", n.id);
  };

  const shown = (notes || []).filter(n => tab === "open" ? !n.discussed : n.discussed);
  // Group: person → section → notes
  const groups = [];
  shown.forEach(n => {
    let p = groups.find(g => g.id === n.subject_id);
    if (!p) { p = { id: n.subject_id, name: n.subject_name || "Someone", sections: [] }; groups.push(p); }
    let s = p.sections.find(x => x.id === n.section_id);
    if (!s) { s = { id: n.section_id, title: n.section_title || n.section_id, notes: [] }; p.sections.push(s); }
    s.notes.push(n);
  });

  const asText = () => {
    const date = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    const open = tab === "open" && discussions.length ? [
      `Still open between us (${date})`, "",
      ...discussions.flatMap(d => {
        const q = QUESTION_INFO[`${d.section_id}__${d.question_id}`];
        const last = (d.log || []).filter(e => e.message).slice(-1)[0];
        return [
          `${SECTION_TITLES[d.section_id] || d.section_id}`,
          `• ${q?.text || d.question_id}`,
          `  Me: ${formatAns(parseAns(d.my_answer))}`,
          ...(partnerLocked || d.private ? [] : [`  ${d.partner_name?.split(" ")[0] || "Partner"}: ${formatAns(parseAns(d.partner_answer))}`]),
          ...(last ? [`  Latest: "${last.message}"`] : []),
          "",
        ];
      }),
    ].join("\n") : "";
    return [open, ...groups.map(p => {
      const lines = [`Talk list — with ${p.name} (${date})`, ""];
      p.sections.forEach(s => {
        lines.push(s.title);
        s.notes.forEach(n => {
          const t = tagInfo(n.tag);
          lines.push(`• ${n.question_text}`);
          if (n.answer_text && !hideQuote(n.subject_id)) lines.push(`  They said: "${n.answer_text}"`);
          lines.push(`  ${t.icon} ${t.label}${n.body ? ` — ${n.body}` : ""}`);
        });
        lines.push("");
      });
      return lines.join("\n");
    })].filter(Boolean).join("\n\n");
  };
  const copy = () => { navigator.clipboard.writeText(asText()); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  const openCount = (notes || []).filter(n => !n.discussed).length + discussions.length;
  const doneCount = (notes || []).filter(n => n.discussed).length;

  return (
    <div style={{minHeight:"100vh",background:C.cream,padding:"40px 24px 80px"}}>
      <div style={{maxWidth:680,margin:"0 auto"}} className="fade">
        <button onClick={onBack} className="no-print" style={{background:"none",border:"none",color:C.muted,fontSize:13,padding:0,marginBottom:32}}>← Back</button>
        <div style={{fontSize:12,letterSpacing:3,textTransform:"uppercase",color:C.goldText,marginBottom:10}}>Your next conversation</div>
        <h1 style={{fontFamily:Fd,color:C.green,fontSize:34,fontWeight:300,marginBottom:8}}>Talk List</h1>
        <p className="no-print" style={{color:C.muted,fontSize:14,lineHeight:1.8,marginBottom:24}}>
          Every note you've left on someone's answers, gathered in one place. Bring it to your next conversation — in person or on a call — and tick things off as you discuss them. Only you can see this.
        </p>

        <div className="no-print" style={{display:"flex",gap:8,marginBottom:24,flexWrap:"wrap"}}>
          {[["open", `To discuss (${openCount})`], ["done", `Discussed (${doneCount})`]].map(([id, label]) => (
            <button key={id} onClick={()=>setTab(id)} style={{fontSize:13,padding:"8px 16px",borderRadius:20,border:`1px solid ${tab===id?C.green:C.parchment}`,background:tab===id?C.green:C.white,color:tab===id?C.cream:C.text}}>{label}</button>
          ))}
          <div style={{flex:1}} />
          {(shown.length > 0 || (tab === "open" && discussions.length > 0)) && <>
            <button onClick={copy} style={{fontSize:13,padding:"8px 16px",borderRadius:20,border:`1px solid ${C.gold}`,background:C.white,color:C.green}}>{copied ? "✓ Copied" : "Copy as text"}</button>
            <button onClick={()=>window.print()} style={{fontSize:13,padding:"8px 16px",borderRadius:20,border:`1px solid ${C.gold}`,background:C.white,color:C.green}}>Print / PDF</button>
          </>}
        </div>

        {partnerLocked && (
          <div className="no-print" style={{fontSize:12,color:C.muted,marginTop:-12,marginBottom:20,lineHeight:1.6}}>
            🔒 Copies and prints leave out {partnership?.partner_name?.split(" ")[0] || "your partner"}'s answers until you're both engaged.
          </div>
        )}
        {notes === null && <div style={{color:C.muted,fontSize:13}}>Loading...</div>}
        {notes !== null && shown.length === 0 && !(tab === "open" && discussions.length) && (
          <div style={{padding:24,background:C.soft,borderRadius:12,color:C.muted,fontSize:14,lineHeight:1.8}}>
            {tab === "open"
              ? "Nothing to discuss yet. When you read someone's shared profile, or compare a charter with your partner, tap “＋ Add a note” under any answer — it will show up here."
              : "Nothing marked as discussed yet."}
          </div>
        )}

        {tab === "open" && discussions.length > 0 && (
          <div style={{marginBottom:36}}>
            <div style={{fontFamily:Fd,fontSize:24,color:C.green,fontWeight:600,marginBottom:4}}>💬 Still open between you</div>
            <div style={{fontSize:12,color:C.muted,marginBottom:14}}>Differences one of you marked “still discussing”. Talk them through, then settle them in the comparison.</div>
            {discussions.map(d => {
              const q = QUESTION_INFO[`${d.section_id}__${d.question_id}`];
              const pf = d.partner_name?.split(" ")[0] || "Partner";
              return (
                <div key={`${d.section_id}-${d.question_id}`} style={{padding:"14px 16px",background:C.white,border:`1px solid ${C.amberL}`,borderRadius:12,marginBottom:10}}>
                  <div style={{fontSize:12,letterSpacing:2,textTransform:"uppercase",color:C.muted,marginBottom:6}}>{SECTION_TITLES[d.section_id] || d.section_id}</div>
                  <div style={{fontSize:13,color:C.text,fontWeight:500,lineHeight:1.6,marginBottom:6}}>{q?.text || d.question_id}</div>
                  <div style={{fontSize:13,color:C.muted,lineHeight:1.7}}>You: <span style={{color:C.text}}>{formatAns(parseAns(d.my_answer))}</span></div>
                  <div className={partnerLocked ? "partner-private no-print" : "partner-private"} onCopy={e => e.preventDefault()} style={{fontSize:13,color:C.muted,lineHeight:1.7}}>{pf}: <span style={{color:C.text}}>{d.private ? "🔒 Answered privately — talk about it in person" : formatAns(parseAns(d.partner_answer))}</span></div>
                  {(d.log || []).length > 0 && (
                    <div style={{marginTop:8,paddingTop:8,borderTop:`1px dashed ${C.parchment}`}}>
                      {d.log.map((e, i) => (
                        <div key={i} style={{fontSize:12,color:C.muted,lineHeight:1.6}}>
                          <span style={{color:C.text,fontWeight:500}}>{e.mine ? "You" : pf}</span> {LOG_VERBS[e.action] || e.action}
                          {e.message ? <> · <span style={{color:C.text,fontStyle:"italic"}}>“{e.message}”</span></> : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {groups.map(p => (
          <div key={p.id} style={{marginBottom:36}}>
            <div style={{fontFamily:Fd,fontSize:24,color:C.green,fontWeight:600,marginBottom:14}}>With {p.name}</div>
            {p.sections.map(s => (
              <div key={s.id} style={{marginBottom:20}}>
                <div style={{fontSize:12,letterSpacing:2,textTransform:"uppercase",color:C.muted,marginBottom:10,fontWeight:500}}>{s.title}</div>
                {s.notes.map(n => {
                  const t = tagInfo(n.tag);
                  return (
                    <div key={n.id} style={{display:"flex",gap:12,padding:"14px 16px",background:C.white,border:`1px solid ${C.parchment}`,borderRadius:12,marginBottom:10}}>
                      <input type="checkbox" checked={n.discussed} onChange={()=>toggle(n)} className="no-print" style={{width:18,height:18,marginTop:2,accentColor:C.green,flexShrink:0}} />
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,color:C.text,fontWeight:500,lineHeight:1.6}}>{n.question_text}</div>
                        {n.answer_text && <div className={hideQuote(n.subject_id) ? "partner-private no-print" : undefined} style={{fontSize:13,color:C.muted,fontStyle:"italic",lineHeight:1.7,margin:"6px 0",paddingLeft:10,borderLeft:`2px solid ${C.parchment}`}}>"{n.answer_text}"</div>}
                        <div style={{fontSize:13,color:C.goldText,fontWeight:500}}>{t.icon} {t.label}</div>
                        {n.body && <div style={{fontSize:13,color:C.text,lineHeight:1.7,marginTop:4}}>{n.body}</div>}
                      </div>
                      <button onClick={()=>del(n)} className="no-print" title="Delete note" style={{background:"none",border:"none",color:C.muted,fontSize:16,alignSelf:"flex-start"}}>×</button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SAFETY: REPORT & BLOCK ───────────────────────────────────────────────────
const REPORT_REASONS = [
  { id: "inappropriate", label: "Inappropriate content" },
  { id: "harassment",    label: "Harassment or pressure" },
  { id: "fake",          label: "Fake or misleading" },
  { id: "other",         label: "Something else" },
];
// ─── SCREEN CHANGES ───────────────────────────────────────────────────────────
const SCREEN_TITLES = {
  onboard: "Welcome", auth: "Sign in", resetPassword: "Choose a new password", account: "Account",
  updates: "Updates", reframe: "Before you begin", finished: "Section complete", survey: "Answering",
  dashboard: "Your journey", partner: "Your partner", compare: "Compare together", talk: "Talk list",
  summary: "Nikah summary", journal: "Journal", familyLinks: "Family links", yearOne: "Your first year",
  family: "Shared with you", publicProfile: "Profile", prerequisite: "What I want you to know first",
  witness: "Character witness", profile: "Your profile",
};
// Move focus to the first heading on the page (without scrolling or showing a
// ring), so a screen reader announces where the person has landed.
function focusViewHeading() {
  const h = document.querySelector("h1, h2");
  if (!h) return;
  h.setAttribute("tabindex", "-1");
  h.setAttribute("data-view-heading", "");
  h.focus({ preventScroll: true });
}

// ─── MODAL ────────────────────────────────────────────────────────────────────
// Shared wrapper for the bottom-sheet dialogs. It keeps their look and adds
// what a dialog needs: focus moves in when it opens, Tab stays inside, Escape
// or a tap outside closes it, the page behind doesn't scroll, and focus goes
// back to whatever opened it.
const FOCUSABLE = 'button:not([disabled]),[href],input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';
function Modal({ label, labelledBy, onClose, panelStyle, children }) {
  const panel = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const opener = document.activeElement;
    const el = panel.current;
    el.focus({ preventScroll: true });
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); close.current(); return; }
      if (e.key !== "Tab") return;
      const items = [...el.querySelectorAll(FOCUSABLE)].filter(x => x.offsetParent !== null);
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === el)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      if (opener && document.contains(opener) && opener.focus) opener.focus({ preventScroll: true });
    };
  }, []);
  return (
    <div className="modal-overlay" onClick={() => close.current()}>
      <div ref={panel} role="dialog" aria-modal="true" aria-label={label} aria-labelledby={labelledBy} tabIndex={-1}
        className="modal-panel" onClick={e => e.stopPropagation()} style={panelStyle}>
        {children}
      </div>
    </div>
  );
}

function ReportDialog({ reportedId, name, context, onClose }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setBusy(true); setErr("");
    const { data: s } = await supabase.auth.getSession();
    const { error } = await supabase.from("reports").insert({
      reporter_id: s.session?.user?.id, reported_id: reportedId, reason, details: details.trim(), context,
    });
    setBusy(false);
    if (error) setErr("Couldn't send the report — please try again"); else setDone(true);
  };
  return (
    <Modal labelledBy="report-title" onClose={onClose} panelStyle={{background:C.white,borderRadius:"20px 20px 0 0",padding:"28px 24px 32px",maxWidth:480,width:"100%"}}>
        {done ? (
          <>
            <div id="report-title" style={{fontFamily:Fd,fontSize:24,color:C.green,marginBottom:10}}>Thank you</div>
            <p style={{fontSize:14,color:C.muted,lineHeight:1.8,marginBottom:20}}>Your report was sent privately. {name ? `${name} won't` : "They won't"} be told who reported. You can also block them so they can't reach you here.</p>
            <button onClick={onClose} style={{width:"100%",background:C.green,color:C.cream,border:"none",borderRadius:10,padding:"13px",fontSize:14}}>Close</button>
          </>
        ) : (
          <>
            <div id="report-title" style={{fontFamily:Fd,fontSize:24,color:C.green,marginBottom:6}}>Report {name || "this person"}</div>
            <p style={{fontSize:13,color:C.muted,lineHeight:1.7,marginBottom:16}}>Reports are private and reviewed by the Mithaq team.</p>
            {REPORT_REASONS.map(r => (
              <label key={r.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",fontSize:14,color:C.text,cursor:"pointer"}}>
                <input type="radio" name="reason" checked={reason===r.id} onChange={()=>setReason(r.id)} style={{accentColor:C.green}} /> {r.label}
              </label>
            ))}
            <textarea aria-label="Anything else we should know? (optional)" value={details} onChange={e=>setDetails(e.target.value)} maxLength={1000} rows={3} placeholder="Anything else we should know? (optional)"
              style={{width:"100%",fontSize:13,padding:"10px 12px",border:`1px solid ${C.parchment}`,borderRadius:8,margin:"12px 0 16px",lineHeight:1.6}} />
            {err && <div style={{fontSize:13,color:C.rose,marginBottom:10}}>{err}</div>}
            <div style={{display:"flex",gap:10}}>
              <button disabled={!reason || busy} onClick={submit} style={{flex:1,background:C.rose,color:C.cream,border:"none",borderRadius:10,padding:"13px",fontSize:14,opacity:!reason||busy?0.5:1}}>{busy?"Sending...":"Send report"}</button>
              <button onClick={onClose} style={{flex:1,background:"none",border:`1px solid ${C.parchment}`,borderRadius:10,padding:"13px",fontSize:14,color:C.text}}>Cancel</button>
            </div>
          </>
        )}
    </Modal>
  );
}

// "Block · Report" links. onBlocked runs after a successful block.
function SafetyLinks({ userId, name, context, onBlocked, dark }) {
  const [reporting, setReporting] = useState(false);
  const [busy, setBusy] = useState(false);
  const block = async () => {
    if (!window.confirm(`Block ${name || "this person"}? They won't be able to see your profile or connect with you, and any connection between you ends. You can unblock later from your Partner screen.`)) return;
    setBusy(true);
    const { error } = await supabase.rpc("block_user", { p_user: userId });
    setBusy(false);
    if (error) alert(error.message); else onBlocked && onBlocked();
  };
  const link = {background:"none",border:"none",color:dark?tint(C.sand,0.75):C.muted,fontSize:12,textDecoration:"underline",textUnderlineOffset:3,padding:"4px 6px"};
  return (
    <span className="no-print">
      <button disabled={busy} onClick={block} style={link}>Block</button>
      <button onClick={()=>setReporting(true)} style={link}>Report</button>
      {reporting && <ReportDialog reportedId={userId} name={name} context={context} onClose={()=>setReporting(false)} />}
    </span>
  );
}

// People I've blocked, with unblock.
function BlockedList() {
  const [list, setList] = useState([]);
  const load = () => supabase.rpc("list_blocks").then(({ data }) => setList(Array.isArray(data) ? data : []));
  useEffect(() => { load(); }, []);
  if (!list.length) return null;
  return (
    <div style={{marginTop:28}}>
      <SectionLabel>Blocked</SectionLabel>
      {list.map(b => (
        <div key={b.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:C.white,border:`1px solid ${C.parchment}`,borderRadius:10,marginBottom:8}}>
          <span style={{fontSize:13,color:C.text}}>{b.name || "Someone"}</span>
          <button onClick={async()=>{await supabase.rpc("unblock_user",{p_user:b.id}); load();}} style={{background:"none",border:"none",color:C.muted,fontSize:12,textDecoration:"underline"}}>Unblock</button>
        </div>
      ))}
    </div>
  );
}

// ─── FAMILY / WALI VIEW ───────────────────────────────────────────────────────
const RES_TEXT = (r) => !r ? null
  : r.status === "agreed" ? `🤝 Agreed${r.confirmed ? "" : " (awaiting confirmation)"}: “${r.agreed_text}”`
  : r.status === "different" ? `↔️ Okay being different${r.confirmed ? "" : " (awaiting confirmation)"}`
  : "💬 Still discussing";

// Older joint family links (both partners approved). New links are personal —
// this only lists existing joint links so they can be approved or turned off.
function FamilyShares({partnerFirst}) {
  const [shares, setShares] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(null);
  const load = async () => {
    const { data } = await supabase.rpc("list_family_shares");
    setShares(Array.isArray(data) ? data : []);
  };
  useEffect(() => { load(); }, []);
  const run = async (fn) => {
    setBusy(true); setErr("");
    const { error } = await fn();
    if (error) setErr(error.message);
    await load(); setBusy(false);
  };
  const copy = (sh) => {
    navigator.clipboard.writeText(`${window.location.origin}/family/${sh.token}`);
    setCopied(sh.id); setTimeout(() => setCopied(null), 1800);
  };
  if (!shares || !shares.length) return null;
  const small = {fontSize:12,padding:"7px 12px",borderRadius:8,border:`1px solid ${C.parchment}`,background:C.white,color:C.green};
  return (
    <div style={{marginBottom:28}}>
      <SectionLabel>Joint family links</SectionLabel>
      <div style={{fontSize:13,color:C.muted,lineHeight:1.7,marginBottom:14}}>
        Links you made together earlier. New family links are personal — each of you shares your own answers from Family on your dashboard.
      </div>
      {shares.map(sh => (
        <div key={sh.id} style={{padding:"14px 16px",background:C.white,border:`1px solid ${C.parchment}`,borderRadius:12,marginBottom:10}}>
          <div style={{fontFamily:Fd,fontSize:16,color:C.text}}>{sh.label || "Family link"}</div>
          <div style={{fontSize:12,color:C.muted,margin:"4px 0 10px"}}>{(sh.sections || []).map(id => SECTION_TITLES[id] || id).join(" · ")}</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            {sh.approved
              ? <button onClick={()=>copy(sh)} style={small}>{copied===sh.id ? "✓ Copied" : "Copy link"}</button>
              : !sh.created_by_me
                ? <button disabled={busy} onClick={()=>run(()=>supabase.rpc("approve_family_share", { p_id: sh.id }))} style={small}>Approve</button>
                : <span style={{fontSize:12,color:C.muted}}>Waiting for {partnerFirst} to approve</span>}
            <button disabled={busy} onClick={()=>{ if (window.confirm("Turn off this link? Anyone who has it will no longer be able to open it.")) run(()=>supabase.rpc("revoke_family_share", { p_id: sh.id })); }}
              style={{background:"none",border:"none",color:C.muted,fontSize:12,textDecoration:"underline",marginLeft:"auto"}}>Turn off</button>
          </div>
        </div>
      ))}
      {err && <div style={{fontSize:13,color:C.rose}}>{err}</div>}
    </div>
  );
}

// ─── FAMILY (personal links) ──────────────────────────────────────────────────
// Each person invites their own family and chooses what they see. Only your
// own answers are ever shown. Nothing here is required.
function FamilyLinksScreen({ user, done, storyDone, growthDone, onBack }) {
  const [links, setLinks] = useState(null);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(null);
  const load = () => supabase.from("family_links").select("*").is("revoked_at", null).order("created_at", { ascending: false })
    .then(({ data }) => setLinks(data || []));
  useEffect(() => { if (user?.id) load(); else setLinks([]); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const choices = [
    ...(storyDone ? ["story"] : []), ...(growthDone ? ["growth"] : []),
    ...CHARTERS.filter(c => done.includes(c.id) && !["physical", "unspoken"].includes(c.id)).map(c => c.id),
  ];
  const create = async () => {
    setBusy(true); setErr("");
    const { error } = await supabase.rpc("create_family_link", { p_label: label, p_sections: picked });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    setCreating(false); setLabel(""); setPicked([]); load();
  };
  const revoke = async (l) => {
    if (!window.confirm(`Turn off "${l.label || "this link"}"? Anyone who has it will no longer be able to open it.`)) return;
    await supabase.rpc("revoke_family_link", { p_id: l.id }); load();
  };
  const copy = (l) => {
    navigator.clipboard.writeText(`${window.location.origin}/family/${l.token}`);
    setCopied(l.id); setTimeout(() => setCopied(null), 1800);
  };
  const title = (id) => SECTION_TITLES[id] || id;
  const btn = {background:FLAT?C.green:G_GREEN,color:C.cream,border:"none",borderRadius:FLAT?6:10,padding:"11px 18px",fontSize:14};

  return (
    <div style={{minHeight:"100vh",background:C.cream,padding:"40px 24px 80px"}}>
      <div style={{maxWidth:620,margin:"0 auto"}} className="fade">
        <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:13,padding:0,marginBottom:32}}>← Back</button>
        <h1 style={{fontFamily:Fd,color:C.green,fontSize:34,fontWeight:FW,marginBottom:10}}>Family</h1>
        <p style={{color:C.muted,fontSize:15,lineHeight:1.8,marginBottom:10}}>
          Every family involves itself differently. If you'd like a parent, wali or someone you trust to read your reflections, make a link just for them.
        </p>
        <ul style={{color:C.muted,fontSize:14,lineHeight:1.8,paddingLeft:20,marginBottom:28}}>
          <li>Only <b>your own</b> answers are shown, on the parts you choose. Never your partner's.</li>
          <li>Attraction, Unspoken Realities and your most sensitive answers are never included.</li>
          <li>You can turn a link off at any time. This is entirely optional.</li>
        </ul>

        {!user?.id && <div style={{padding:20,background:C.soft,borderRadius:12,color:C.muted,fontSize:14}}>Create an account to share with family.</div>}

        {(links || []).map(l => (
          <div key={l.id} style={{padding:"16px 18px",background:C.white,border:`1px solid ${C.parchment}`,borderRadius:FLAT?8:12,marginBottom:10}}>
            <div style={{fontFamily:Fd,fontSize:19,color:C.green}}>{l.label || "Family link"}</div>
            <div style={{fontSize:13,color:C.muted,margin:"4px 0 12px",lineHeight:1.6}}>{(l.sections || []).map(title).join(" · ")}</div>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <button onClick={()=>copy(l)} style={{fontSize:13,padding:"8px 14px",borderRadius:FLAT?6:8,border:`1px solid ${C.gold}`,background:C.white,color:C.green}}>{copied === l.id ? "✓ Copied" : "Copy link"}</button>
              <button onClick={()=>revoke(l)} style={{background:"none",border:"none",color:C.muted,fontSize:13,textDecoration:"underline",marginLeft:"auto"}}>Turn off</button>
            </div>
          </div>
        ))}

        {user?.id && !creating && (
          <button onClick={()=>setCreating(true)} disabled={!choices.length} style={{...btn,marginTop:8,opacity:choices.length?1:0.5}}>New family link</button>
        )}
        {user?.id && !choices.length && <div style={{fontSize:13,color:C.muted,marginTop:10}}>Finish your story or a charter first — then you can share it.</div>}
        {creating && (
          <div style={{marginTop:8,padding:18,background:C.white,border:`1px solid ${C.gold}`,borderRadius:FLAT?8:12}}>
            <label htmlFor="family-link-label" style={{display:"block",fontSize:13,color:C.text,marginBottom:6}}>Who is it for?</label>
            <input id="family-link-label" value={label} onChange={e=>setLabel(e.target.value)} maxLength={80} placeholder="e.g. For my mother"
              style={{width:"100%",fontSize:14,padding:"10px 12px",border:`1px solid ${C.parchment}`,borderRadius:8,background:C.cream,marginBottom:14}} />
            <div style={{fontSize:13,color:C.text,marginBottom:6}}>What can they read?</div>
            {choices.map(id => (
              <label key={id} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 0",fontSize:14,color:C.text,cursor:"pointer"}}>
                <input type="checkbox" checked={picked.includes(id)} onChange={()=>setPicked(p => p.includes(id) ? p.filter(x=>x!==id) : [...p, id])} style={{width:17,height:17,accentColor:C.green}} />
                {title(id)}
              </label>
            ))}
            <div style={{display:"flex",gap:10,marginTop:14}}>
              <button disabled={busy || !picked.length} onClick={create} style={{...btn,opacity:busy||!picked.length?0.5:1}}>{busy ? "Creating..." : "Create link"}</button>
              <button onClick={()=>setCreating(false)} style={{background:"none",border:"none",color:C.muted,fontSize:14}}>Cancel</button>
            </div>
          </div>
        )}
        {err && <div style={{fontSize:13,color:C.rose,marginTop:12}}>{err}</div>}
      </div>
    </div>
  );
}

// What a wali / family member sees (no account needed).
function FamilyView({token}) {
  const [data, setData] = useState(undefined);
  const [committed, setCommitted] = useState(false);
  useEffect(() => {
    supabase.rpc("get_family_view", { p_token: token }).then(({ data }) => setData(data || null));
  }, [token]);

  const shell = (children) => (
    <div style={{minHeight:"100vh",background:C.night,padding:"40px 24px"}}><div style={{maxWidth:680,margin:"0 auto"}}>{children}</div></div>
  );
  const cream = C.cream, sand = C.sand;
  if (data === undefined) return shell(<div style={{color:sand,fontFamily:"Georgia,serif"}}>Loading...</div>);
  if (!data) return shell(<div style={{color:sand,fontFamily:"Georgia,serif",lineHeight:1.8}}>This link isn't available. It may have been turned off by the couple.</div>);

  const personal = data.kind === "personal";
  const a = personal ? (data.name?.split(" ")[0] || "Them") : (data.a_name?.split(" ")[0] || "Partner 1");
  const b = data.b_name?.split(" ")[0] || "Partner 2";
  const names = personal ? data.name : `${data.a_name} & ${data.b_name}`;

  if (!committed) return shell(
    <div style={{background:"rgba(255,255,255,0.04)",border:`1px solid ${tint(C.sand,0.2)}`,borderRadius:16,padding:36}}>
      <div style={{fontFamily:"Georgia,serif",color:sand,fontSize:12,letterSpacing:4,textTransform:"uppercase",marginBottom:16}}>Shared with you in trust</div>
      <h2 style={{fontFamily:"Georgia,serif",color:cream,fontSize:26,fontWeight:300,marginBottom:16}}>{names}</h2>
      <div style={{color:tint(C.cream,0.75),fontSize:14,lineHeight:1.8,marginBottom:28}}>
        <p style={{marginBottom:12}}>{personal ? `${a} chose to share these reflections with you` : "Both of them chose to share these reflections with you"}{data.label ? ` (${data.label})` : ""}. By continuing, you commit to:</p>
        <ul style={{paddingLeft:20}}>
          <li style={{marginBottom:8}}>Keep what you read between you and them</li>
          <li style={{marginBottom:8}}>Offer counsel, not pressure</li>
          <li style={{marginBottom:8}}>Remember their answers may grow and change</li>
        </ul>
      </div>
      <button onClick={()=>setCommitted(true)} style={{width:"100%",background:G_LEAF,color:cream,border:"none",borderRadius:8,padding:"14px 24px",fontSize:14,fontWeight:500}}>I understand and I commit →</button>
    </div>
  );

  return shell(<>
    <div style={{fontFamily:"Georgia,serif",color:sand,fontSize:12,letterSpacing:4,textTransform:"uppercase",marginBottom:8}}>Mithaq · Family view</div>
    <h1 style={{fontFamily:"Georgia,serif",color:cream,fontSize:32,fontWeight:300,marginBottom:6}}>{names}</h1>
    <p style={{color:tint(C.cream,0.75),fontSize:13,marginBottom:36}}>{personal ? `Shared by ${a}` : "Shared with the consent of both"}{data.label ? ` · ${data.label}` : ""}</p>
    {(data.charters || []).map(ch => {
      const charter = CHARTERS.find(c => c.id === ch.section_id);
      const qs = ch.section_id === "story" ? STORY_QS : ch.section_id === "growth" ? GROWTH_QS : (charter?.qs || []);
      return (
        <div key={ch.section_id} style={{marginBottom:36,padding:"20px 22px",background:"rgba(255,255,255,0.04)",border:`1px solid ${tint(C.sand,0.15)}`,borderRadius:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",flexWrap:"wrap",gap:8,marginBottom:18}}>
            <div style={{fontFamily:"Georgia,serif",color:cream,fontSize:20}}>{SECTION_TITLES[ch.section_id] || ch.section_id}</div>
            {ch.signed_at && <div style={{fontSize:12,color:C.goldL}}>🤝 Signed together · {fmtDate(ch.signed_at)}</div>}
          </div>
          {qs.map(q => {
            const av = parseAns(ch.a?.[q.id]), bv = personal ? null : parseAns(ch.b?.[q.id]);
            if (av == null && bv == null) return null;
            const res = RES_TEXT(ch.resolutions?.[q.id]);
            return (
              <div key={q.id} style={{marginBottom:18,paddingBottom:14,borderBottom:`1px solid ${tint(C.sand,0.1)}`}}>
                <div style={{color:tint(C.sand,0.8),fontSize:13,marginBottom:8,lineHeight:1.6}}>{q.text}</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
                  {(personal ? [[a, av]] : [[a, av], [b, bv]]).map(([who, v]) => (
                    <div key={who} style={{flex:"1 1 220px",minWidth:0,padding:"10px 12px",background:"rgba(0,0,0,0.15)",borderRadius:8}}>
                      <div style={{fontSize:12,letterSpacing:2,textTransform:"uppercase",color:sand,marginBottom:4}}>{who}</div>
                      <div style={{color:tint(C.cream,0.85),fontSize:13,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{v == null ? "—" : formatAns(v)}</div>
                    </div>
                  ))}
                </div>
                {res && <div style={{marginTop:8,fontSize:13,color:C.goldL,lineHeight:1.6}}>{res}</div>}
              </div>
            );
          })}
        </div>
      );
    })}
    <div style={{textAlign:"center",color:tint(C.cream,0.75),fontSize:12,marginTop:24}}>Read-only · {personal ? `${a} can turn this link off at any time` : "Either of them can turn this link off at any time"}</div>
  </>);
}

// The pledge each person takes before connecting.
function AmanahPledge({ checked, onChange }) {
  return (
    <div style={{background:C.soft,border:`1.5px solid ${checked?C.gold:C.parchment}`,borderRadius:14,padding:"20px 20px 16px",marginBottom:18}}>
      <div style={{fontFamily:Fd,fontSize:22,color:C.green,fontWeight:600,marginBottom:6}}>🤲 {AMANAH_PLEDGE.title}</div>
      <div style={{fontSize:13,color:C.muted,lineHeight:1.7,marginBottom:12}}>{AMANAH_PLEDGE.body}</div>
      {AMANAH_PLEDGE.promises.map((p, i) => (
        <div key={i} style={{fontSize:14,color:C.text,lineHeight:1.7,marginBottom:6,paddingLeft:14,borderLeft:`2px solid ${C.gold}`}}>{p}</div>
      ))}
      <div style={{fontFamily:Fd,fontSize:14,color:C.goldText,fontStyle:"italic",margin:"12px 0"}}>{AMANAH_PLEDGE.verse}</div>
      <label style={{display:"flex",alignItems:"center",gap:10,fontSize:14,color:C.green,fontWeight:500,cursor:"pointer"}}>
        <input type="checkbox" checked={checked} onChange={e=>onChange(e.target.checked)} style={{width:18,height:18,accentColor:C.green}} />
        {AMANAH_PLEDGE.check}
      </label>
    </div>
  );
}

// Where the couple is, and moving forward together.
function StageCard({ partnership, partnerFirst, onChanged, onJournal, onYearOne }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [nikahDate, setNikahDate] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const cur = stageRank(partnership.stage);
  const next = STAGES[cur];   // undefined at the last stage
  const run = async (fn) => {
    setBusy(true); setErr("");
    const { error } = await fn();
    if (error) setErr(error.message);
    await onChanged(); setBusy(false);
  };
  const btn = {borderRadius:8,padding:"9px 14px",fontSize:13,border:"none"};
  return (
    <div style={{background:C.white,border:`1px solid ${C.parchment}`,borderRadius:14,padding:20,marginBottom:28}}>
      <SectionLabel>Where you are together</SectionLabel>
      <div style={{display:"flex",gap:6,marginBottom:14}}>
        {STAGES.map((s, i) => (
          <div key={s.id} style={{flex:1,textAlign:"center"}}>
            <div style={{height:4,borderRadius:2,background:i < cur ? G_GOLD90 : C.parchment,marginBottom:6}} />
            <div style={{fontSize:12,color:i === cur - 1 ? C.green : C.muted,fontWeight:i === cur - 1 ? 600 : 400}}>{s.icon} {s.label}</div>
          </div>
        ))}
      </div>
      <div style={{fontSize:13,color:C.muted,lineHeight:1.7,marginBottom:next?14:0}}>
        Open now: {STAGES.slice(0, cur).map(s => s.sub.split(".")[0]).join(" · ")}.
      </div>
      {next && !partnership.stage_proposed && (
        <>
          <button disabled={busy} onClick={()=>run(()=>supabase.rpc("propose_next_stage"))}
            style={{...btn,background:G_GREEN,color:C.cream,opacity:busy?0.6:1}}>Ready to go deeper? Suggest “{next.label}”</button>
          <div style={{fontSize:12,color:C.muted,marginTop:8,lineHeight:1.6}}>{next.sub} {partnerFirst} will be asked too — it only changes when you both agree.</div>
        </>
      )}
      {next && partnership.stage_proposed === "me" && (
        <div style={{fontSize:13,color:C.text,lineHeight:1.7}}>
          You suggested moving to <b>{next.label}</b> — waiting for {partnerFirst}.
          <button disabled={busy} onClick={()=>run(()=>supabase.rpc("respond_next_stage", { p_accept: false }))} style={{background:"none",border:"none",color:C.muted,fontSize:12,textDecoration:"underline",marginLeft:8}}>Withdraw</button>
        </div>
      )}
      {next && partnership.stage_proposed === "partner" && (
        <div style={{padding:14,background:C.soft,border:`1px solid ${C.gold}`,borderRadius:10}}>
          <div style={{fontSize:14,color:C.green,fontWeight:500,marginBottom:6}}>{partnerFirst} is ready to move to {next.icon} {next.label}. Are you?</div>
          <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginBottom:6}}>{next.sub} There's no moving back to an earlier stage — if this isn't working, ending the connection is always open to you.</div>
          <div style={{fontSize:12,color:C.muted,lineHeight:1.7,marginBottom:12,fontStyle:"italic"}}>{ISTIKHARA_NOTE} <button onClick={onJournal} style={{background:"none",border:"none",color:C.goldText,fontSize:12,padding:0,textDecoration:"underline"}}>Open your journal</button></div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button disabled={busy} onClick={()=>run(()=>supabase.rpc("respond_next_stage", { p_accept: true }))} style={{...btn,background:G_GOLD,color:C.onGold,fontWeight:600}}>Yes, I'm ready</button>
            <button disabled={busy} onClick={()=>run(()=>supabase.rpc("respond_next_stage", { p_accept: false }))} style={{...btn,background:"none",border:`1px solid ${C.parchment}`,color:C.text}}>Not yet</button>
          </div>
        </div>
      )}
      {partnership.stage === "engaged" && !partnership.stage_proposed && (
        <div style={{marginTop:6,paddingTop:14,borderTop:`1px solid ${C.parchment}`}}>
          <div style={{fontSize:14,color:C.green,fontWeight:500,marginBottom:4}}>Has your nikah taken place?</div>
          <div style={{fontSize:12,color:C.muted,lineHeight:1.6,marginBottom:10}}>Confirm it together to open your first-year check-ins — short, private conversations at 1, 3, 6 and 12 months.</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            <input type="date" value={nikahDate} max={today} onChange={e=>setNikahDate(e.target.value)} aria-label="Date of your nikah"
              style={{fontSize:14,padding:"8px 10px",border:`1px solid ${C.parchment}`,borderRadius:8,background:C.cream,color:C.text}} />
            <button disabled={busy || !nikahDate} onClick={()=>run(()=>supabase.rpc("propose_next_stage", { p_nikah_date: nikahDate }))}
              style={{...btn,background:G_GREEN,color:C.cream,opacity:busy||!nikahDate?0.5:1}}>Our nikah took place</button>
          </div>
        </div>
      )}
      {partnership.stage === "engaged" && partnership.stage_proposed === "me" && (
        <div style={{fontSize:13,color:C.text,lineHeight:1.7}}>
          You confirmed your nikah on {fmtDate(partnership.stage_proposed_date)} — waiting for {partnerFirst}.
          <button disabled={busy} onClick={()=>run(()=>supabase.rpc("respond_next_stage", { p_accept: false }))} style={{background:"none",border:"none",color:C.muted,fontSize:12,textDecoration:"underline",marginLeft:8}}>Withdraw</button>
        </div>
      )}
      {partnership.stage === "engaged" && partnership.stage_proposed === "partner" && (
        <div style={{padding:14,background:C.soft,border:`1px solid ${C.gold}`,borderRadius:10}}>
          <div style={{fontSize:14,color:C.green,fontWeight:500,marginBottom:10}}>{partnerFirst} confirmed your nikah took place on {fmtDate(partnership.stage_proposed_date)}. Is that right?</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button disabled={busy} onClick={()=>run(()=>supabase.rpc("respond_next_stage", { p_accept: true }))} style={{...btn,background:G_GOLD,color:C.onGold,fontWeight:600}}>Yes, confirm</button>
            <button disabled={busy} onClick={()=>run(()=>supabase.rpc("respond_next_stage", { p_accept: false }))} style={{...btn,background:"none",border:`1px solid ${C.parchment}`,color:C.text}}>Not yet</button>
          </div>
        </div>
      )}
      {partnership.stage === "married" && (
        <div style={{marginTop:6,paddingTop:14,borderTop:`1px solid ${C.parchment}`}}>
          <div lang="ar" dir="rtl" style={{fontFamily:Fa,fontSize:20,color:C.goldText,marginBottom:4}}>بارك الله لكما وبارك عليكما وجمع بينكما في خير</div>
          <div style={{fontSize:13,color:C.muted,lineHeight:1.6,marginBottom:12}}>May Allah bless you both, and bring you together in goodness. Married since {fmtDate(partnership.nikah_date)}.</div>
          <button onClick={onYearOne} style={{...btn,background:G_GREEN,color:C.cream}}>Your first year together</button>
        </div>
      )}
      {err && <div style={{marginTop:10,fontSize:13,color:C.rose}}>{err}</div>}
    </div>
  );
}

function PartnerScreen({user, partnership, onChanged, onBack, onCompare, onSummary, onJournal, onYearOne}) {
  const invitedCode = getJoinCode();
  const [code, setCode] = useState(invitedCode || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [pledged, setPledged] = useState(false);
  const [justEnded, setJustEnded] = useState(false);

  const status = partnership?.status;
  const [inviter, setInviter] = useState(null);
  useEffect(() => {
    if (!invitedCode || !user?.id) return;
    supabase.rpc("invite_info", { p_code: invitedCode }).then(({ data }) => setInviter(data || null));
  }, [invitedCode, user?.id]);
  // Already connected? A leftover invite link shouldn't keep redirecting here.
  useEffect(() => { if (status === "active" && invitedCode) clearJoinCode(); }, [status, invitedCode]);
  const inviteLink = partnership?.invite_code ? `${window.location.origin}/join/${partnership.invite_code}` : "";
  const partnerFirst = partnership?.partner_name?.split(" ")[0] || "your partner";

  const run = async (fn) => {
    setBusy(true); setError("");
    try {
      const { error: err } = await fn();
      if (err) setError(err.message || "Something went wrong");
      else await onChanged();
    } catch (e) {
      setError("Something went wrong — please try again");
    }
    setBusy(false);
  };

  const createInvite = () => run(() => supabase.rpc("create_partner_invite", { p_pledged: pledged }));
  const accept = () => run(async () => {
    const res = await supabase.rpc("accept_partner_invite", { p_code: code.trim(), p_pledged: pledged });
    if (!res.error) clearJoinCode();
    return res;
  });
  const endIt = () => run(async () => {
    const wasActive = status === "active";
    const res = await supabase.rpc("end_partnership");
    setConfirmEnd(false);
    if (!res.error && wasActive) setJustEnded(true);
    return res;
  });
  const decline = () => { clearJoinCode(); setCode(""); onBack(); };
  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  };

  const btnPrimary = {width:"100%",background:G_GREEN,color:C.cream,border:"none",borderRadius:10,padding:"15px 24px",fontSize:14,fontWeight:500,opacity:busy?0.6:1};
  const btnGold = {...btnPrimary,background:G_GOLD,color:C.onGold,fontWeight:600};
  const btnQuiet = {background:"none",border:"none",color:C.muted,fontSize:13,padding:"10px 0",textDecoration:"underline",textUnderlineOffset:3};
  const panel = {background:C.white,border:`1px solid ${C.parchment}`,borderRadius:14,padding:22,marginBottom:18};

  return (
    <div style={{minHeight:"100vh",background:C.cream,padding:"40px 24px"}}>
      <div style={{maxWidth:560,margin:"0 auto"}} className="fade">
        <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:13,padding:0,marginBottom:32}}>← Back</button>
        <div style={{fontSize:12,letterSpacing:3,textTransform:"uppercase",color:C.goldText,marginBottom:12}}>Your Partner</div>

        {!user?.id && (
          <>
            <h1 style={{fontFamily:Fd,color:C.green,fontSize:32,fontWeight:300,marginBottom:12}}>Create an account first</h1>
            <p style={{color:C.muted,fontSize:14,lineHeight:1.8}}>Connecting with a partner needs a real account, so your answers stay yours. Sign out of the demo and create one.</p>
          </>
        )}

        {/* ── Connected ── */}
        {user?.id && status === "active" && (
          <>
            <h1 style={{fontFamily:Fd,color:C.green,fontSize:32,fontWeight:300,marginBottom:12}}>You're connected with {partnerFirst}</h1>
            <p style={{color:C.muted,fontSize:14,lineHeight:1.8,marginBottom:28}}>
              Your accounts are linked{partnership.accepted_at ? ` since ${new Date(partnership.accepted_at).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}` : ""}. Each of you still answers privately — nothing is shared until you both choose to compare a charter.
            </p>
            <StageCard partnership={partnership} partnerFirst={partnerFirst} onChanged={onChanged} onJournal={onJournal} onYearOne={onYearOne} />
            {atLeastEngaged(partnership.stage) && (
              <button type="button" className="card btn-card" onClick={onSummary} style={{display:"flex",alignItems:"center",gap:14,padding:"16px 18px",background:G_GREEN,borderRadius:14,marginBottom:28,cursor:"pointer"}}>
                <span aria-hidden="true" style={{fontSize:22}}>🕌</span>
                <div style={{flex:1}}>
                  <div style={{fontFamily:Fd,fontSize:18,color:C.cream,fontWeight:600}}>Your nikah summary</div>
                  <div style={{fontSize:12,color:tint(C.cream,0.75)}}>A one-page summary of your agreements to bring to your imam</div>
                </div>
                <span aria-hidden="true" style={{color:C.goldL,fontSize:18}}>→</span>
              </button>
            )}
            <CompareList partnerFirst={partnerFirst} onOpen={onCompare} stage={partnership.stage} />
            <FamilyShares partnerFirst={partnerFirst} />
            {!confirmEnd ? (
              <div>
                <button onClick={()=>setConfirmEnd(true)} style={btnQuiet}>End this connection</button>
                <span style={{marginLeft:8}}><SafetyLinks userId={partnership.partner_id} name={partnerFirst} context="partner" onBlocked={onChanged} /></span>
              </div>
            ) : (
              <div style={panel}>
                <div style={{fontFamily:Fd,fontSize:22,color:C.green,marginBottom:8}}>End your connection with {partnerFirst}?</div>
                <div style={{fontSize:14,color:C.text,lineHeight:1.75,marginBottom:12}}>You don't need {partnerFirst}'s agreement, and you don't need to give a reason. When you end it:</div>
                <ul style={{fontSize:13,color:C.muted,lineHeight:1.8,paddingLeft:20,marginBottom:12}}>
                  <li>Your shared space closes, and you both lose access to each other's answers.</li>
                  <li>Everything you shared is deleted: reveals, agreements, discussions, signatures, family links and the nikah summary.</li>
                  <li>Your private notes about each other are deleted too.</li>
                  <li>{partnerFirst} sees one gentle message — no reason, no blame.</li>
                  <li>Your own answers stay with you.</li>
                </ul>
                <div style={{fontSize:12,color:C.muted,fontStyle:"italic",lineHeight:1.7,marginBottom:16}}>This can't be undone. Whether to tell your own family is up to you — Mithaq never contacts anyone.</div>
                <div style={{display:"flex",gap:10}}>
                  <button disabled={busy} onClick={endIt} style={{...btnPrimary,background:C.rose}}>End the connection</button>
                  <button onClick={()=>setConfirmEnd(false)} style={{...btnPrimary,background:"none",color:C.text,border:`1px solid ${C.parchment}`}}>Cancel</button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Just ended ── */}
        {user?.id && justEnded && status !== "active" && (
          <div style={{...panel,background:C.soft,marginBottom:28}}>
            <div style={{fontFamily:Fd,fontSize:24,color:C.green,marginBottom:8}}>The connection has ended</div>
            <p style={{fontSize:14,color:C.text,lineHeight:1.8,marginBottom:8}}>Everything the two of you shared has been deleted. Your own answers are still yours.</p>
            <p style={{fontFamily:Fd,fontSize:16,color:C.goldText,fontStyle:"italic",lineHeight:1.6,marginBottom:16}}>May Allah write what is best for both of you.</p>
            <p style={{fontSize:13,color:C.muted,lineHeight:1.7,marginBottom:14}}>Before you connect with someone new, it's worth reading your answers again and updating anything that has changed.</p>
            <button onClick={onBack} style={{...btnPrimary,width:"auto",padding:"11px 18px"}}>Review my answers</button>
          </div>
        )}

        {/* ── Invite sent, waiting ── */}
        {user?.id && status === "pending" && !invitedCode && (
          <>
            <h1 style={{fontFamily:Fd,color:C.green,fontSize:32,fontWeight:300,marginBottom:12}}>Waiting for your partner</h1>
            <p style={{color:C.muted,fontSize:14,lineHeight:1.8,marginBottom:28}}>Send this link to the person you're getting to know. When they open it and sign in, your accounts will be linked.</p>
            <div style={{...panel,textAlign:"center"}}>
              <div style={{fontSize:12,letterSpacing:2,textTransform:"uppercase",color:C.muted,marginBottom:8}}>Invite code</div>
              <div style={{fontFamily:Fd,fontSize:38,color:C.green,letterSpacing:6,fontWeight:600,marginBottom:6}}>{partnership.invite_code}</div>
              <div style={{fontSize:12,color:C.muted,wordBreak:"break-all"}}>{inviteLink}</div>
            </div>
            <button onClick={copyLink} style={btnGold}>{copied ? "✓ Copied!" : "Copy invite link"}</button>
            <div style={{textAlign:"center",marginTop:8}}>
              <button disabled={busy} onClick={endIt} style={btnQuiet}>Cancel this invite</button>
            </div>
          </>
        )}

        {/* ── Invited by someone, or entering a code ── */}
        {user?.id && status !== "active" && (invitedCode || status !== "pending") && (
          <>
            {invitedCode ? (
              <>
                <h1 style={{fontFamily:Fd,color:C.green,fontSize:32,fontWeight:300,marginBottom:12}}>{inviter?.inviter_name ? `${inviter.inviter_name.split(" ")[0]} invited you` : "You've been invited"}</h1>
                <p style={{color:C.muted,fontSize:14,lineHeight:1.8,marginBottom:28}}>Someone wants to link their Mithaq account with yours so you can compare charters together. Your answers stay private — nothing is shared until you both choose to compare.</p>
                <AmanahPledge checked={pledged} onChange={setPledged} />
              </>
            ) : (
              <>
                <h1 style={{fontFamily:Fd,color:C.green,fontSize:32,fontWeight:300,marginBottom:12}}>Connect with your partner</h1>
                <p style={{color:C.muted,fontSize:14,lineHeight:1.8,marginBottom:28}}>Link your account with the person you're getting to know. You'll each answer privately, then compare charters side by side when you're both ready.</p>
                <AmanahPledge checked={pledged} onChange={setPledged} />
                <button disabled={busy || !pledged} onClick={createInvite} style={{...btnPrimary,marginBottom:28,opacity:busy||!pledged?0.5:1}}>{busy ? "Creating..." : "Create invite link"}</button>
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
                  <div style={{flex:1,height:1,background:C.parchment}} />
                  <div style={{fontSize:12,color:C.muted,letterSpacing:2,textTransform:"uppercase"}}>or</div>
                  <div style={{flex:1,height:1,background:C.parchment}} />
                </div>
              </>
            )}
            <div style={panel}>
              <div id="partner-code-label" style={{fontSize:12,color:C.muted,marginBottom:10,fontWeight:500}}>{invitedCode ? "Invite code" : "Have a code from your partner?"}</div>
              <input aria-labelledby="partner-code-label" autoComplete="off" autoCapitalize="characters" spellCheck={false} value={code} onChange={e=>setCode(e.target.value.toUpperCase())} placeholder="e.g. 7F3A9C21" maxLength={12}
                style={{width:"100%",fontSize:20,letterSpacing:4,padding:"12px 14px",border:`1.5px solid ${C.parchment}`,borderRadius:10,background:C.cream,color:C.green,marginBottom:14,textAlign:"center"}} />
              <button disabled={busy || !code.trim() || !pledged} onClick={accept} style={{...btnGold,opacity:busy||!code.trim()||!pledged?0.5:1}}>{busy ? "Connecting..." : "Accept & connect"}</button>
              {!pledged && <div style={{fontSize:12,color:C.muted,textAlign:"center",marginTop:8}}>Take the amanah pledge above to connect.</div>}
            </div>
            {invitedCode && (
              <div style={{textAlign:"center"}}>
                <button onClick={decline} style={btnQuiet}>Not now</button>
                {inviter?.inviter_id && <div style={{marginTop:4}}><SafetyLinks userId={inviter.inviter_id} name={inviter.inviter_name?.split(" ")[0]} context="invite" onBlocked={()=>{clearJoinCode();onBack();}} /></div>}
              </div>
            )}
          </>
        )}

        {user?.id && <BlockedList />}
        {error && <div style={{marginTop:16,padding:"12px 16px",background:"rgba(58,28,28,0.06)",border:"1px solid rgba(58,28,28,0.15)",borderRadius:10,color:C.rose,fontSize:13}}>{error}</div>}
      </div>
    </div>
  );
}

// ─── NIKAH SUMMARY ────────────────────────────────────────────────────────────
// One page of agreements on practical topics, built by the server from what the
// couple settled or answered the same way. Both approve the exact version on
// screen before either can download it. There is no share link.
function NikahSummaryScreen({ onBack }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const { data, error } = await supabase.rpc("get_nikah_summary");
    if (error) setErr(error.message); else { setErr(""); setData(data); }
  };
  useEffect(() => { load(); }, []);
  const approve = async () => {
    setBusy(true);
    const { error } = await supabase.rpc("approve_nikah_summary", { p_version: data.version });
    if (error) setErr(error.message);
    await load(); setBusy(false);
  };

  const me = data?.me_name?.split(" ")[0] || "Me";
  const partner = data?.partner_name?.split(" ")[0] || "My partner";
  const bothApproved = data?.me_approved && data?.partner_approved;
  const items = (data?.items || []).filter(i => SUMMARY_TOPICS[`${i.section_id}__${i.question_id}`]);
  const line = (i) => {
    if (i.kind === "agreed") return <>We agreed: <span style={{color:C.text}}>“{i.value}”</span></>;
    if (i.kind === "different") return <>We've agreed it's okay for us to see this differently.</>;
    return <>We both chose: <span style={{color:C.text}}>{formatAns(i.value)}</span></>;
  };
  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  return (
    <div style={{minHeight:"100vh",background:C.cream,padding:"40px 24px 80px"}}>
      <div style={{maxWidth:680,margin:"0 auto"}} className="fade">
        <button onClick={onBack} className="no-print" style={{background:"none",border:"none",color:C.muted,fontSize:13,padding:0,marginBottom:32}}>← Back</button>
        {err && <div style={{padding:"12px 16px",background:"rgba(58,28,28,0.06)",borderRadius:10,color:C.rose,fontSize:13,marginBottom:16}}>{err}</div>}
        {!data && !err && <div style={{color:C.muted,fontSize:13}}>Loading...</div>}
        {data && (
          <>
            <div style={{background:C.white,border:`1px solid ${C.parchment}`,borderRadius:16,padding:"32px 28px"}}>
              <div style={{fontSize:12,letterSpacing:3,textTransform:"uppercase",color:C.goldText,marginBottom:10}}>Mithaq · Nikah summary</div>
              <h1 style={{fontFamily:Fd,color:C.green,fontSize:34,fontWeight:300,marginBottom:6}}>{data.me_name} & {data.partner_name}</h1>
              <div style={{fontSize:13,color:C.muted,marginBottom:6}}>{today}</div>
              <div style={{fontSize:13,color:C.muted,lineHeight:1.7,marginBottom:28}}>What we have agreed on so far, to discuss with whoever conducts our nikah.</div>

              {items.length === 0 && (
                <div style={{fontSize:14,color:C.muted,lineHeight:1.8}}>Nothing here yet. As you settle differences and answer practical questions the same way, your agreements will appear on this page.</div>
              )}
              {SUMMARY_GROUPS.map(g => {
                const gi = items.filter(i => SUMMARY_TOPICS[`${i.section_id}__${i.question_id}`].group === g.id);
                if (!gi.length) return null;
                return (
                  <div key={g.id} style={{marginBottom:24}}>
                    <div style={{fontFamily:Fd,fontSize:20,color:C.green,fontWeight:600,marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${C.parchment}`}}>{g.title}</div>
                    {gi.map(i => (
                      <div key={`${i.section_id}-${i.question_id}`} style={{marginBottom:12}}>
                        <div style={{fontSize:13,color:C.text,fontWeight:500}}>{SUMMARY_TOPICS[`${i.section_id}__${i.question_id}`].label}</div>
                        <div style={{fontSize:13,color:C.muted,lineHeight:1.7}}>{line(i)}</div>
                      </div>
                    ))}
                  </div>
                );
              })}

              <div style={{marginTop:28,paddingTop:16,borderTop:`1px solid ${C.parchment}`,fontSize:12,color:C.muted,lineHeight:1.8}}>
                This summary is not a religious ruling or a legal contract. Scholars and madhhabs differ on which conditions in a nikah are binding — please discuss it with your imam. Whether a nikah contract can be enforced in a U.S. court depends on your state; speak with a lawyer about anything you want legally enforceable.
              </div>
            </div>

            <div className="no-print" style={{marginTop:20}}>
              {(data.open_count > 0 || (data.not_revealed || []).length > 0) && (
                <div style={{fontSize:13,color:C.muted,lineHeight:1.7,marginBottom:16}}>
                  {data.open_count > 0 && <>{data.open_count} practical topic{data.open_count > 1 ? "s are" : " is"} still open between you. Settle {data.open_count > 1 ? "them" : "it"} in your comparisons and {data.open_count > 1 ? "they'll" : "it'll"} appear here. </>}
                  {(data.not_revealed || []).length > 0 && <>Not revealed together yet: {(data.not_revealed || []).map(id => SECTION_TITLES[id] || id).join(", ")}.</>}
                </div>
              )}
              <div style={{fontSize:13,color:C.muted,marginBottom:12}}>
                Intimacy, health, debts, legal status and your private reflections never appear on this page.
              </div>
              <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"center",marginBottom:12}}>
                <span style={{fontSize:13,color:data.me_approved?C.goldText:C.muted}}>{data.me_approved ? `✓ ${me} approved` : `${me} hasn't approved yet`}</span>
                <span style={{fontSize:13,color:data.partner_approved?C.goldText:C.muted}}>{data.partner_approved ? `✓ ${partner} approved` : `${partner} hasn't approved yet`}</span>
              </div>
              {!data.me_approved && (
                <button disabled={busy || items.length === 0} onClick={approve} style={{background:G_GREEN,color:C.cream,border:"none",borderRadius:10,padding:"13px 22px",fontSize:14,marginRight:10,opacity:busy||!items.length?0.5:1}}>{busy ? "Saving..." : "I approve this version"}</button>
              )}
              <button disabled={!bothApproved} onClick={()=>window.print()} style={{background:bothApproved?G_GOLD:C.parchment,color:bothApproved?C.onGold:C.muted,border:"none",borderRadius:10,padding:"13px 22px",fontSize:14,fontWeight:600,cursor:bothApproved?"pointer":"not-allowed"}}>Download PDF</button>
              <div style={{fontSize:12,color:C.muted,marginTop:10,lineHeight:1.6}}>
                {bothApproved ? "Choose “Save as PDF” in the print window." : "Download opens once you've both approved this exact version. Any later change needs both approvals again."}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── UPDATES ──────────────────────────────────────────────────────────────────
// What's new. Updates never contain anyone's answers.
const PERIOD_NAME = { 1: "one-month", 3: "three-month", 6: "six-month", 12: "one-year" };
function describeUpdate(n, partnerFirst) {
  const P = partnerFirst || "Your partner";
  const T = n.section_id ? (CHARTERS.find(c => c.id === n.section_id)?.title || n.section_id) : "";
  const period = PERIOD_NAME[n.data?.period] || "";
  switch (n.kind) {
    case "partner_joined":      return { text: `${P} accepted your invite. You're connected.`, go: "partner" };
    case "partner_finished":    return { text: `${P} finished ${T}. When you've finished it too, you can compare it together.`, go: "dashboard" };
    case "both_finished":       return { text: `You've both finished ${T}. Reveal it together when you're ready.`, go: "partner" };
    case "partner_ready":       return { text: `${P} is ready to reveal ${T}.`, go: "partner" };
    case "revealed":            return { text: `${T} is revealed — you can compare it now.`, go: "compare" };
    case "resolution_proposed": return { text: `${P} suggested how to settle something in ${T}.`, go: "compare" };
    case "partner_signed":      return { text: `${P} signed ${T}.`, go: "compare" };
    case "signed":              return { text: `You've both signed ${T}.`, go: "compare" };
    case "stage_proposed":      return n.data?.from === "engaged"
                                  ? { text: `${P} confirmed your nikah took place. Confirm it too to open your first-year check-ins.`, go: "partner" }
                                  : { text: `${P} is ready to go deeper together.`, go: "partner" };
    case "stage_changed":       return { text: n.data?.stage === "married" ? "Your nikah is confirmed. Your first-year check-ins are open." : `You're now ${stageInfo(n.data?.stage).short}. New charters are open to compare.`, go: "partner" };
    case "summary_approved":    return { text: `${P} approved your nikah summary.`, go: "summary" };
    case "checkin_open":        return { text: `Your ${period} check-in is open.`, go: "yearOne" };
    case "checkin_partner_done":return { text: `${P} answered the ${period} check-in.`, go: "yearOne" };
    case "connection_ended":    return { text: "A connection of yours has ended. Your own answers are still yours.", go: "dashboard" };
    default:                    return { text: "Something new is waiting for you.", go: "dashboard" };
  }
}
const ago = (d) => {
  const m = Math.round((Date.now() - new Date(d)) / 60000);
  if (m < 60) return m <= 1 ? "just now" : `${m} min ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h} h ago`;
  return fmtDate(d);
};

function UpdatesScreen({ partnership, onBack, onGo }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    supabase.rpc("get_notifications").then(({ data }) => {
      setItems(data?.items || []);
      if (data?.unread) supabase.rpc("mark_notifications_read");
    });
  }, []);
  const pf = partnership?.partner_name?.split(" ")[0];
  return (
    <div style={{minHeight:"100vh",background:C.cream,padding:"40px 24px 80px"}}>
      <div style={{maxWidth:600,margin:"0 auto"}} className="fade">
        <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:13,padding:0,marginBottom:28}}>← Back</button>
        <h1 style={{fontFamily:Fd,color:C.green,fontSize:34,fontWeight:FW,marginBottom:22}}>Updates</h1>
        {items === null && <div style={{color:C.muted,fontSize:14}}>Loading...</div>}
        {items && !items.length && <div style={{padding:20,background:C.soft,borderRadius:12,color:C.muted,fontSize:14,lineHeight:1.7}}>Nothing yet. When your partner joins, finishes a charter or a check-in opens, you'll see it here.</div>}
        {(items || []).map(n => {
          const d = describeUpdate(n, pf);
          return (
            <button key={n.id} onClick={()=>onGo(d.go, n.section_id)}
              style={{display:"flex",gap:12,alignItems:"flex-start",width:"100%",textAlign:"left",background:n.read?C.white:C.soft,border:`1px solid ${n.read?C.parchment:C.gold}`,borderRadius:FLAT?8:12,padding:"14px 16px",marginBottom:10}}>
              {FLAT ? <Star size={12} fill={n.read ? C.parchment : C.gold} style={{marginTop:5}} /> : <span style={{width:8,height:8,borderRadius:"50%",background:n.read?C.parchment:C.gold,marginTop:7,flexShrink:0}} />}
              <span style={{flex:1}}>
                <span style={{display:"block",fontSize:15,color:C.text,lineHeight:1.55}}>{d.text}</span>
                <span style={{display:"block",fontSize:12,color:C.muted,marginTop:4}}>{ago(n.created_at)}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── YEAR ONE ─────────────────────────────────────────────────────────────────
// Short check-ins at 1, 3, 6 and 12 months. Each answers privately; answers are
// shared once both have answered. Topics are a flag, not a story — and each
// comes with a gentle way to start the conversation.
const CHECKIN_TOPICS = [
  { id: "time",          label: "Time together",          opener: "I'd love for us to have more time that's just ours. Can we think about what that could look like?" },
  { id: "closeness",     label: "Closeness and intimacy", opener: "I want us to feel close in every way. Can we find a quiet time to talk about what helps each of us feel that?" },
  { id: "money",         label: "Money",                  opener: "Can we sit down soon and look at how money is going for us — no blame, just so we're on the same page?" },
  { id: "family",        label: "Family and in-laws",     opener: "There's something about our families I'd like us to talk through together, so we're a team on it." },
  { id: "faith",         label: "Faith and practice",     opener: "I'd like us to grow in our deen together. What's one thing we could do side by side?" },
  { id: "home",          label: "Home and chores",        opener: "Can we look at how we're sharing things at home? I want it to feel fair to both of us." },
  { id: "work",          label: "Work and stress",        opener: "Work has been weighing on one of us. How can we support each other better right now?" },
  { id: "communication", label: "How we talk to each other", opener: "I want us to feel heard by each other. Can we talk about how we handle hard moments?" },
];
const topicInfo = (id) => CHECKIN_TOPICS.find(t => t.id === id);
const PERIOD_LABEL = { 1: "One month", 3: "Three months", 6: "Six months", 12: "One year" };

function YearOneScreen({ onBack }) {
  const [data, setData] = useState(undefined);
  const [openPeriod, setOpenPeriod] = useState(null);
  const [form, setForm] = useState({ well: "", more: "", topics: [], private_note: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = async () => {
    const { data, error } = await supabase.rpc("get_year_one");
    if (error) setErr(error.message);
    setData(data || null);
  };
  useEffect(() => { load(); }, []);
  const begin = (p) => {
    const m = p.mine || {};
    setForm({ well: m.well || "", more: m.more || "", topics: m.topics || [], private_note: m.private_note || "" });
    setOpenPeriod(p.period); setErr("");
  };
  const submit = async () => {
    setBusy(true); setErr("");
    const { error } = await supabase.rpc("submit_checkin", { p_period: openPeriod, p_answers: form });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    setOpenPeriod(null); load();
  };
  const toggleTopic = (id) => setForm(f => ({ ...f, topics: f.topics.includes(id) ? f.topics.filter(x => x !== id) : [...f.topics, id] }));
  const spouse = data?.partner_name?.split(" ")[0] || "your spouse";
  const field = {width:"100%",fontSize:14,lineHeight:1.7,padding:"10px 12px",border:`1px solid ${C.parchment}`,borderRadius:8,background:C.cream,color:C.text,marginBottom:14};
  const btn = {background:FLAT?C.green:G_GREEN,color:C.cream,border:"none",borderRadius:FLAT?6:10,padding:"11px 18px",fontSize:14};

  const shared = (p) => {
    const me = p.mine || {}, them = p.partner || {};
    const topics = [...new Set([...(me.topics || []), ...(them.topics || [])])];
    const who = (id) => {
      const a = (me.topics || []).includes(id), b = (them.topics || []).includes(id);
      return a && b ? "You both" : a ? "You" : spouse;
    };
    return (
      <div>
        {[["What's going well", "well"], ["Something I'd like more of", "more"]].map(([label, k]) => (
          <div key={k} style={{marginBottom:14}}>
            <div style={{fontSize:13,color:C.muted,marginBottom:6}}>{label}</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
              {[["You", me[k]], [spouse, them[k]]].map(([w, v]) => (
                <div key={w} style={{flex:"1 1 220px",padding:"10px 12px",background:C.soft,borderRadius:8,fontSize:14,color:C.text,lineHeight:1.6}}>
                  <div style={{fontSize:12,color:C.muted,marginBottom:2}}>{w}</div>{v || <span style={{color:C.muted}}>—</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
        {topics.length > 0 && (
          <div style={{marginTop:6}}>
            <div style={{fontSize:13,color:C.muted,marginBottom:8}}>Worth a conversation</div>
            {topics.map(id => (
              <div key={id} style={{padding:"12px 14px",border:`1px solid ${C.gold}`,borderRadius:8,marginBottom:8}}>
                <div style={{fontSize:14,color:C.green,fontWeight:500}}>{topicInfo(id)?.label} <span style={{color:C.muted,fontWeight:400}}>· {who(id)}</span></div>
                <div style={{fontSize:13,color:C.muted,lineHeight:1.6,marginTop:4}}>A gentle way to start: <i>“{topicInfo(id)?.opener}”</i></div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{minHeight:"100vh",background:C.cream,padding:"40px 24px 80px"}}>
      <div style={{maxWidth:640,margin:"0 auto"}} className="fade">
        <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:13,padding:0,marginBottom:32}}>← Back</button>
        <h1 style={{fontFamily:Fd,color:C.green,fontSize:34,fontWeight:FW,marginBottom:10}}>Your first year together</h1>
        <p style={{color:C.muted,fontSize:15,lineHeight:1.8,marginBottom:28}}>
          Small disappointments that go unsaid can grow and spread into everything else. These short check-ins give you a regular, gentle moment to say what's going well and what needs a conversation — before it grows. You each answer privately; nothing is shared until you've both answered.
        </p>
        {data === undefined && <div style={{color:C.muted,fontSize:13}}>Loading...</div>}
        {data === null && <div style={{padding:20,background:C.soft,borderRadius:12,color:C.muted,fontSize:14,lineHeight:1.7}}>Check-ins open once you've both confirmed your nikah, from your Partner page.</div>}
        {data && (data.periods || []).map(p => {
          const mineDone = !!p.mine, both = mineDone && p.partner_done;
          const editing = openPeriod === p.period;
          return (
            <div key={p.period} style={{background:C.white,border:`1px solid ${p.open && !both ? C.gold : C.parchment}`,borderRadius:FLAT?8:14,padding:"18px 20px",marginBottom:14,opacity:p.open?1:0.6}}>
              <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap",alignItems:"baseline",marginBottom:both||editing?14:0}}>
                <div style={{fontFamily:Fd,fontSize:21,color:C.green}}>{PERIOD_LABEL[p.period]}</div>
                <div style={{fontSize:13,color:C.muted}}>
                  {!p.open ? `Opens around ${fmtDate(p.due_on)}`
                    : both ? "Shared with each other"
                    : mineDone ? `You've answered · waiting for ${spouse}`
                    : p.partner_done ? `${spouse} has answered · your turn` : "Ready when you are"}
                </div>
              </div>
              {p.open && !mineDone && !editing && <button onClick={()=>begin(p)} style={{...btn,marginTop:12}}>Start this check-in</button>}
              {p.open && mineDone && !both && !editing && <button onClick={()=>begin(p)} style={{background:"none",border:"none",color:C.muted,fontSize:13,textDecoration:"underline",padding:"10px 0 0"}}>Edit my answers</button>}
              {editing && (
                <div>
                  <label htmlFor="checkin-well" style={{display:"block",fontSize:14,color:C.text,marginBottom:6}}>What's going well between you?</label>
                  <textarea id="checkin-well" rows={2} value={form.well} maxLength={1000} onChange={e=>setForm(f=>({...f,well:e.target.value}))} style={field} />
                  <label htmlFor="checkin-more" style={{display:"block",fontSize:14,color:C.text,marginBottom:6}}>Something you'd like more of</label>
                  <textarea id="checkin-more" rows={2} value={form.more} maxLength={1000} onChange={e=>setForm(f=>({...f,more:e.target.value}))} style={field} />
                  <div style={{fontSize:14,color:C.text,marginBottom:4}}>Is there anything you'd like to talk about?</div>
                  <div style={{fontSize:12,color:C.muted,marginBottom:8}}>Only the topic is shared — not a story — along with a gentle way to start the conversation.</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:16}}>
                    {CHECKIN_TOPICS.map(t => (
                      <button key={t.id} onClick={()=>toggleTopic(t.id)} aria-pressed={form.topics.includes(t.id)}
                        style={{fontSize:13,padding:"7px 12px",borderRadius:20,border:`1px solid ${form.topics.includes(t.id)?C.gold:C.parchment}`,background:form.topics.includes(t.id)?C.soft:C.white,color:C.text}}>{t.label}</button>
                    ))}
                  </div>
                  <label htmlFor="checkin-private" style={{display:"block",fontSize:14,color:C.text,marginBottom:4}}>A note just for you <span style={{color:C.muted,fontSize:12}}>(never shared)</span></label>
                  <textarea id="checkin-private" rows={2} value={form.private_note} maxLength={2000} onChange={e=>setForm(f=>({...f,private_note:e.target.value}))} placeholder="Anything you're still finding words for." style={field} />
                  <div style={{display:"flex",gap:10}}>
                    <button disabled={busy || (!form.well.trim() && !form.more.trim() && !form.topics.length)} onClick={submit} style={{...btn,opacity:busy?0.6:1}}>{busy ? "Saving..." : "Save my check-in"}</button>
                    <button onClick={()=>setOpenPeriod(null)} style={{background:"none",border:"none",color:C.muted,fontSize:14}}>Cancel</button>
                  </div>
                </div>
              )}
              {both && !editing && shared(p)}
            </div>
          );
        })}
        {err && <div style={{fontSize:13,color:C.rose,marginTop:10}}>{err}</div>}
      </div>
    </div>
  );
}

// ─── ISTIKHARA & REFLECTION JOURNAL ───────────────────────────────────────────
// Entirely private. Never shared, never part of a connection, kept if one ends.
function JournalScreen({ user, onBack }) {
  const [entries, setEntries] = useState(null);
  const [kind, setKind] = useState("reflection");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = () => supabase.from("journal_entries").select("*").order("created_at", { ascending: false })
    .then(({ data }) => setEntries(data || []));
  useEffect(() => { if (user?.id) load(); else setEntries([]); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    setBusy(true); setErr("");
    const { error } = await supabase.from("journal_entries").insert({ user_id: user.id, kind, body: body.trim() });
    setBusy(false);
    if (error) setErr("Couldn't save — please try again"); else { setBody(""); load(); }
  };
  const del = async (e) => {
    if (!window.confirm("Delete this entry?")) return;
    setEntries(p => p.filter(x => x.id !== e.id));
    await supabase.from("journal_entries").delete().eq("id", e.id);
  };
  const kindOf = (id) => JOURNAL_KINDS.find(k => k.id === id) || JOURNAL_KINDS[0];

  return (
    <div style={{minHeight:"100vh",background:C.cream,padding:"40px 24px 80px"}}>
      <div style={{maxWidth:640,margin:"0 auto"}} className="fade">
        <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:13,padding:0,marginBottom:32}}>← Back</button>
        <div style={{fontSize:12,letterSpacing:3,textTransform:"uppercase",color:C.goldText,marginBottom:10}}>Only you can read this</div>
        <h1 style={{fontFamily:Fd,color:C.green,fontSize:34,fontWeight:300,marginBottom:8}}>Your journal</h1>
        <p style={{color:C.muted,fontSize:14,lineHeight:1.8,marginBottom:10}}>A quiet place for impressions, du'as and istikhara along the way. Nothing here is ever shared with anyone, and it stays with you even if a connection ends.</p>
        <p style={{color:C.muted,fontSize:13,lineHeight:1.8,marginBottom:24,fontStyle:"italic"}}>{ISTIKHARA_NOTE}</p>

        {!user?.id ? (
          <div style={{padding:20,background:C.soft,borderRadius:12,color:C.muted,fontSize:14}}>Create an account to keep a journal.</div>
        ) : (
          <div style={{padding:16,background:C.white,border:`1px solid ${C.parchment}`,borderRadius:12,marginBottom:28}}>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
              {JOURNAL_KINDS.map(k => (
                <button key={k.id} onClick={()=>setKind(k.id)} style={{fontSize:12,padding:"6px 12px",borderRadius:20,border:`1px solid ${kind===k.id?C.gold:C.parchment}`,background:kind===k.id?tint(C.gold,0.1):C.white,color:kind===k.id?C.green:C.text}}>{k.icon} {k.label}</button>
              ))}
            </div>
            <textarea aria-label={`Journal entry: ${(JOURNAL_KINDS.find(k=>k.id===kind)||{}).label || "reflection"}`} value={body} onChange={e=>setBody(e.target.value)} rows={4} maxLength={5000}
              placeholder={kind === "istikhara" ? "What did you pray about? How did you feel afterwards?" : kind === "dua" ? "A du'a you want to remember…" : "What are you noticing, feeling, or wondering?"}
              style={{width:"100%",fontSize:14,lineHeight:1.7,padding:"12px 14px",border:`1px solid ${C.parchment}`,borderRadius:8,background:C.cream,color:C.text,marginBottom:10}} />
            <button disabled={busy || !body.trim()} onClick={add} style={{background:G_GREEN,color:C.cream,border:"none",borderRadius:8,padding:"9px 18px",fontSize:13,opacity:busy||!body.trim()?0.5:1}}>{busy ? "Saving..." : "Save entry"}</button>
            {err && <div style={{fontSize:12,color:C.rose,marginTop:8}}>{err}</div>}
          </div>
        )}

        {entries === null && <div style={{color:C.muted,fontSize:13}}>Loading...</div>}
        {(entries || []).map(e => {
          const k = kindOf(e.kind);
          return (
            <div key={e.id} style={{padding:"14px 16px",background:C.white,border:`1px solid ${C.parchment}`,borderRadius:12,marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
                <span style={{fontSize:12,color:C.goldText,fontWeight:500}}>{k.icon} {k.label} · <span style={{color:C.muted,fontWeight:300}}>{fmtDate(e.created_at)}</span></span>
                <button onClick={()=>del(e)} title="Delete entry" style={{background:"none",border:"none",color:C.muted,fontSize:16}}>×</button>
              </div>
              <div style={{fontSize:14,color:C.text,lineHeight:1.75,whiteSpace:"pre-wrap"}}>{e.body}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PublicProfile({userId, currentUserId}) {
  const PRIVATE_SECTIONS = ["unspoken", "physical", "financial"];
  const STORY_QS_MAP = Object.fromEntries(STORY_QS.map(q => [q.id, q.text]));
  const GROWTH_QS_MAP = Object.fromEntries(GROWTH_QS.map(q => [q.id, q.text]));
  // Who is looking? (profile links open outside the main app flow)
  const [viewerId, setViewerId] = useState(currentUserId || null);
  useEffect(() => {
    if (currentUserId) return;
    supabase.auth.getSession().then(({ data }) => setViewerId(data.session?.user?.id || null));
  }, [currentUserId]);
  const [profile, setProfile] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [committed, setCommitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [storyAnswers, setStoryAnswers] = useState({});
  const [growthAnswers, setGrowthAnswers] = useState({});
  const [prerequisite, setPrerequisite] = useState("");
  const [charterAnswers, setCharterAnswers] = useState({});
  const [expandedCharter, setExpandedCharter] = useState(null);
  const [ownerId, setOwnerId] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const { notes, save: saveNote, remove: removeNote } = useNotes(viewerId, ownerId, profile?.name);
  useEffect(() => {
    async function load() {
      // Only receives what this person chose to share — filtered by the database
      const {data: pub} = await supabase.rpc("get_shared_profile", {p_token: userId});
      setOwnerId(pub?.owner_id || null);
      const prof = pub ? {name: pub.name, prerequisite: pub.prerequisite, share_settings: pub.share_settings} : null;
      const ans = pub?.answers || [];
      setProfile(prof);
      setCompleted(pub?.completed || []);
      if (prof?.prerequisite) setPrerequisite(prof.prerequisite);
      if (ans) {
        const sMap = {};
        const gMap = {};
        const allMap = {};
        ans.forEach(r => {
          const val = (() => { try { return JSON.parse(r.answer_value); } catch { return r.answer_value; } })();
          if (r.section_id === "story") sMap[r.question_id] = val;
          else if (r.section_id === "growth") gMap[r.question_id] = val;
          if (!allMap[r.section_id]) allMap[r.section_id] = {};
          allMap[r.section_id][r.question_id] = val;
        });
        setStoryAnswers(sMap);
        setGrowthAnswers(gMap);
        setCharterAnswers(allMap);
      }
      
      
      setLoading(false);
    }
    if (userId) load();
  }, [userId]);

  if (loading) return <div style={{minHeight:"100vh",background:C.night,display:"flex",alignItems:"center",justifyContent:"center",color:C.sand,fontFamily:"Georgia,serif"}}>Loading...</div>;
  if (!profile) return <div style={{minHeight:"100vh",background:C.night,display:"flex",alignItems:"center",justifyContent:"center",color:C.sand,fontFamily:"Georgia,serif"}}>Profile not found.</div>;

  if (blocked) return <div style={{minHeight:"100vh",background:C.night,display:"flex",alignItems:"center",justifyContent:"center",padding:24,color:C.sand,fontFamily:"Georgia,serif",textAlign:"center",lineHeight:1.8}}>Blocked. You won't see this profile, and they can't connect with you.</div>;
  if (!committed && ownerId !== viewerId) return (
    <div style={{minHeight:"100vh",background:C.night,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{maxWidth:440,width:"100%",background:"rgba(255,255,255,0.04)",border:`1px solid ${tint(C.sand,0.2)}`,borderRadius:16,padding:40}}>
        <div style={{fontFamily:"Georgia,serif",color:C.sand,fontSize:12,letterSpacing:4,textTransform:"uppercase",marginBottom:16}}>Before You Continue</div>
        <h2 style={{fontFamily:"Georgia,serif",color:C.cream,fontSize:28,fontWeight:300,marginBottom:20}}>A Commitment of Respect</h2>
        <div style={{color:tint(C.cream,0.75),fontSize:14,lineHeight:1.8,marginBottom:32}}>
          <p style={{marginBottom:12}}>You are about to view someone's private reflections, shared with you in trust.</p>
          <p style={{marginBottom:12}}>By continuing, you commit to:</p>
          <ul style={{paddingLeft:20,marginBottom:12}}>
            <li style={{marginBottom:8}}>Treat this information with dignity and discretion</li>
            <li style={{marginBottom:8}}>Not share, screenshot, or discuss this with others without permission</li>
            <li style={{marginBottom:8}}>Approach their answers with curiosity, not judgment</li>
            <li style={{marginBottom:8}}>Honour their vulnerability, regardless of outcome</li>
          </ul>
        </div>
        <button onClick={()=>setCommitted(true)} style={{width:"100%",background:G_LEAF,color:C.cream,border:"none",borderRadius:8,padding:"14px 24px",fontSize:14,fontWeight:500,cursor:"pointer"}}>I understand and I commit →</button>
      </div>
    </div>
  ); 

  const shareSettings = profile?.share_settings || {};
  const canNote = !!viewerId && viewerId !== ownerId;
  const noteFor = (sectionId, qid, val) => canNote ? (
    <NoteControl dark note={notes[`${sectionId}__${qid}`]}
      onSave={(tag, body) => saveNote(sectionId, qid, val, tag, body)}
      onDelete={() => removeNote(sectionId, qid)} />
  ) : null;
  const publicCharters = completed.filter(id => !PRIVATE_SECTIONS.includes(id) && id !== "story" && id !== "growth" && shareSettings[id]);
  const privateCharters = completed.filter(id => PRIVATE_SECTIONS.includes(id));

  // Charter names come from the charters themselves, so new or renamed
  // charters show their current title.

  return (
    <div style={{minHeight:"100vh",background:C.night,padding:"40px 24px"}}>
      <div style={{maxWidth:600,margin:"0 auto"}}>
        <div style={{fontFamily:"Georgia,serif",color:C.sand,fontSize:12,letterSpacing:4,textTransform:"uppercase",marginBottom:8}}>Mithaq Profile</div>
        <h1 style={{fontFamily:"Georgia,serif",color:C.cream,fontSize:36,fontWeight:300,marginBottom:4}}>{profile.name}</h1>

        {prerequisite && (
          <div style={{margin:"24px 0",padding:"20px 24px",background:tint(C.gold,0.08),border:`1px solid ${tint(C.gold,0.2)}`,borderRadius:12}}>
            <div style={{fontSize:12,letterSpacing:3,textTransform:"uppercase",color:C.goldL,marginBottom:10}}>Before we go further</div>
            <p style={{color:tint(C.cream,0.85),fontSize:14,lineHeight:1.85,fontWeight:400,margin:0}}>{prerequisite}</p>
          </div>
        )}
        <p style={{color:tint(C.cream,0.75),fontSize:13,marginBottom:canNote||!viewerId?20:40}}>Shared with sincerity and intention</p>
        {viewerId !== ownerId && (
          <div className="no-print" style={{marginBottom:40,padding:"14px 18px",background:"rgba(255,255,255,0.04)",border:`1px solid ${tint(C.sand,0.15)}`,borderRadius:12,display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
            <div style={{flex:"1 1 240px",color:tint(C.cream,0.75),fontSize:13,lineHeight:1.7}}>
              📝 {canNote
                ? "Tap “＋ Add a note” under any answer to remember what to ask. Only you can see your notes — they're gathered in your Talk List for your next conversation."
                : "Want to keep private notes on answers for your next conversation? Sign in or create a free account — you'll come right back here."}
            </div>
            {canNote
              ? <button onClick={()=>{window.location.href="/";}} style={{background:"none",border:`1px solid ${tint(C.gold,0.4)}`,color:C.goldL,borderRadius:20,padding:"8px 14px",fontSize:12}}>My dashboard →</button>
              : <button onClick={()=>{setReturnProfile(userId);window.location.href="/";}} style={{background:G_GOLD,color:C.onGold,border:"none",borderRadius:20,padding:"8px 16px",fontSize:12,fontWeight:600}}>Sign in to take notes</button>}
          </div>
        )}

        {completed.includes("story") && (
          <div style={{marginBottom:40}}>
            <div style={{fontSize:12,letterSpacing:3,textTransform:"uppercase",color:C.sand,marginBottom:16}}>🪞 Your Story</div>
            {Object.entries(STORY_QS_MAP).map(([id, question]) => {
              const ans = storyAnswers[id];
              if (!ans || ans === "__working__") return null;
              return (
                <div key={id} style={{marginBottom:20}}>
                  <div style={{color:tint(C.cream,0.75),fontSize:12,marginBottom:6}}>{question}</div>
                  <div style={{color:C.cream,fontSize:15,lineHeight:1.7,fontStyle:"italic"}}>"{formatAns(ans)}"</div>
                  {noteFor("story", id, ans)}
                </div>
              );
            })}
          </div>
        )}

        {completed.includes("growth") && (
          <div style={{marginBottom:40}}>
            <div style={{fontSize:12,letterSpacing:3,textTransform:"uppercase",color:C.sand,marginBottom:16}}>🌱 Growth Portfolio</div>
            {Object.entries(GROWTH_QS_MAP).map(([id, question]) => {
              const ans = growthAnswers[id];
              if (!ans || ans === "__working__") return null;
              return (
                <div key={id} style={{marginBottom:20}}>
                  <div style={{color:tint(C.cream,0.75),fontSize:12,marginBottom:6}}>{question}</div>
                  <div style={{color:C.cream,fontSize:15,lineHeight:1.7,fontStyle:"italic"}}>"{formatAns(ans)}"</div>
                  {noteFor("growth", id, ans)}
                </div>
              );
            })}
          </div>
        )}

        {publicCharters.length > 0 && (
          <div style={{marginBottom:40}}>
            <div style={{fontSize:12,letterSpacing:3,textTransform:"uppercase",color:C.sand,marginBottom:16}}>Completed Charters</div>
            {publicCharters.map(id => {
              const isExpanded = expandedCharter === id;
              const answers = charterAnswers[id] || {};
              const hasAnswers = Object.keys(answers).length > 0;
              // Only a charter with answers to show can be opened, so only then is
              // its header a button.
              const Head = hasAnswers ? "button" : "div";
              const headProps = hasAnswers
                ? { type:"button", className:"btn-card", "aria-expanded":isExpanded, "aria-controls":`profile-charter-${id}`, onClick:()=>setExpandedCharter(isExpanded?null:id) }
                : {};
              return (
                <div key={id} style={{background:"rgba(255,255,255,0.04)",border:`1px solid ${isExpanded?tint(C.gold,0.4):tint(C.sand,0.15)}`,borderRadius:12,marginBottom:12,overflow:"hidden",transition:"border-color 0.2s"}}>
                  <Head {...headProps} style={{padding:"16px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:hasAnswers?"pointer":"default"}}>
                    <div style={{color:C.cream,fontSize:14,fontWeight:500}}>{SECTION_TITLES[id] || id}</div>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{color:C.sand,fontSize:12}}>✓ completed</div>
                      {hasAnswers && <div aria-hidden="true" style={{color:C.goldL,fontSize:12}}>{isExpanded?"▲":"▼"}</div>}
                    </div>
                  </Head>
                  {isExpanded && hasAnswers && (
                    <div id={`profile-charter-${id}`} style={{borderTop:`1px solid ${tint(C.sand,0.1)}`,padding:"16px 20px"}}>
                      {Object.entries(answers).map(([qid, val]) => {
                        // Answers to retired questions stay stored but are never shown.
                        if (!QUESTION_MAP[`${id}__${qid}`]) return null;
                        const questionText = QUESTION_MAP[`${id}__${qid}`];
                        const displayVal = formatAns(val);
                        if (!displayVal) return null;
                        return (
                          <div key={qid} style={{marginBottom:20}}>
                            <div style={{color:tint(C.sand,0.75),fontSize:12,marginBottom:8,lineHeight:1.6}}>{questionText}</div>
                            <div style={{color:tint(C.cream,0.85),fontSize:13,lineHeight:1.8,fontStyle:"italic",paddingLeft:12,borderLeft:`2px solid ${tint(C.gold,0.3)}`}}>
                              {displayVal}
                            </div>
                            {noteFor(id, qid, val)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {privateCharters.length > 0 && (
          <div style={{marginBottom:40}}>
            <div style={{fontSize:12,letterSpacing:3,textTransform:"uppercase",color:C.sand,marginBottom:16}}>Shared In Person</div>
            {privateCharters.map(id => (
              <div key={id} style={{background:"rgba(255,255,255,0.02)",border:`1px dashed ${tint(C.sand,0.1)}`,borderRadius:12,padding:"16px 20px",marginBottom:12,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{color:tint(C.cream,0.75),fontSize:14}}>{SECTION_TITLES[id] || id}</div>
                <div style={{color:tint(C.sand,0.75),fontSize:12}}>🔒 in person</div>
              </div>
            ))}
          </div>
        )}

        <div style={{borderTop:`1px solid ${tint(C.sand,0.1)}`,paddingTop:24,marginTop:24}}>
          <p style={{color:tint(C.cream,0.75),fontSize:12,textAlign:"center"}}>This profile was shared via Mithaq — a faith-aligned marriage preparation platform.</p>
          {canNote && (
            <div style={{textAlign:"center",marginTop:8}}>
              <SafetyLinks dark userId={ownerId} name={profile.name?.split(" ")[0]} context="profile" onBlocked={()=>setBlocked(true)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Auth({startMode,form,setForm,onSignup,onLogin,onForgot,onResend,onBack,onDemo}) {
  const [mode, setMode] = useState(startMode === "login" ? "login" : "signup");   // signup | login | forgot | checkEmail
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);         // { text, kind: "error" | "ok" }
  const [unconfirmed, setUnconfirmed] = useState(false);
  const go = (m) => { setMode(m); setMsg(null); setUnconfirmed(false); };
  const run = async (fn, check) => {
    const problem = check();
    if (problem) { setMsg({ text: problem, kind: "error" }); return; }
    setBusy(true); setMsg(null);
    const r = (await fn()) || {};
    setBusy(false);
    if (r.error) { setMsg({ text: r.error, kind: "error" }); setUnconfirmed(!!r.unconfirmed); }
    else if (r.checkEmail) go("checkEmail");
    else if (r.sent) setMsg({ text: mode === "forgot" ? "If that email has an account, a reset link is on its way. Check your inbox." : "Sent — check your inbox.", kind: "ok" });
  };
  const emailOk = /.+@.+\..+/.test(form.email.trim());
  const fields = mode === "signup"
    ? [["Your name","name","text","Your name","name"],["Email","email","email","you@email.com","email"],["Password","password","password","At least 8 characters","new-password"]]
    : mode === "login"
      ? [["Email","email","email","you@email.com","email"],["Password","password","password","Your password","current-password"]]
      : [["Email","email","email","you@email.com","email"]];
  const primary = {width:"100%",marginTop:22,background:FLAT?C.green:G_GREEN,color:C.cream,border:"none",borderRadius:8,padding:"14px 24px",fontSize:15,fontWeight:500,opacity:busy?0.6:1};
  const quiet = {background:"none",border:"none",color:C.muted,fontSize:14,padding:"8px 0",textDecoration:"underline",textUnderlineOffset:3};
  const titles = { signup: "Create your account", login: "Welcome back", forgot: "Reset your password", checkEmail: "Check your email" };

  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"50px 24px 60px",overflowY:"auto"}}>
      <div style={{width:"100%",maxWidth:400}} className="fade">
        <button onClick={mode === (startMode === "login" ? "login" : "signup") ? onBack : ()=>go(mode === "forgot" ? "login" : (startMode === "login" ? "login" : "signup"))} style={{background:"none",border:"none",color:C.muted,fontSize:13,marginBottom:32}}>← Back</button>
        <h2 style={{fontFamily:Fd,fontSize:34,fontWeight:FW,color:C.text,marginBottom:6}}>{titles[mode]}</h2>
        {mode === "checkEmail" ? (
          <>
            <p style={{color:C.muted,fontSize:15,lineHeight:1.75,marginBottom:20}}>We sent a confirmation link to <b style={{color:C.text}}>{form.email.trim()}</b>. Open it to finish creating your account — then you can sign in.</p>
            <button disabled={busy} onClick={()=>run(onResend, ()=>null)} style={quiet}>Send the link again</button>
            <div><button onClick={()=>go("login")} style={quiet}>I've confirmed — sign in</button></div>
          </>
        ) : (
          <>
            <p style={{color:C.muted,fontSize:14,marginBottom:28,lineHeight:1.6}}>
              {mode === "forgot" ? "Enter your email and we'll send you a link to choose a new password." : "Your answers are yours. Nothing is shared until you choose to share it."}
            </p>
            <form onSubmit={e=>{ e.preventDefault();
                if (mode === "signup") run(onSignup, ()=> !form.name.trim() ? "Please add your name." : !emailOk ? "Please enter a valid email." : form.password.length < 8 ? "Use at least 8 characters for your password." : null);
                else if (mode === "login") run(onLogin, ()=> !emailOk ? "Please enter a valid email." : !form.password ? "Please enter your password." : null);
                else run(onForgot, ()=> !emailOk ? "Please enter a valid email." : null);
              }} style={{display:"flex",flexDirection:"column",gap:14}}>
              {fields.map(([label,key,type,ph,ac])=>(
                <label key={key} style={{display:"block"}}>
                  <span style={{display:"block",fontSize:13,color:C.text,marginBottom:6}}>{label}</span>
                  <span style={{position:"relative",display:"block"}}>
                    <input type={key==="password"?(showPassword?"text":"password"):type} value={form[key]} autoComplete={ac}
                      onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} placeholder={ph}
                      style={{width:"100%",fontSize:15,padding:"12px 14px",border:`1.5px solid ${C.parchment}`,borderRadius:8,background:C.white,color:C.text}} />
                    {key==="password" && <button type="button" onClick={()=>setShowPassword(p=>!p)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",fontSize:12,color:C.muted}}>{showPassword?"Hide":"Show"}</button>}
                  </span>
                </label>
              ))}
              {msg && <div role={msg.kind === "error" ? "alert" : "status"} style={{fontSize:14,lineHeight:1.6,color:msg.kind === "error" ? C.rose : C.green,background:msg.kind === "error" ? "rgba(142,47,34,0.06)" : C.soft,padding:"10px 12px",borderRadius:8}}>{msg.text}</div>}
              <button type="submit" disabled={busy} style={primary}>
                {busy ? "One moment..." : mode === "signup" ? "Create account" : mode === "login" ? "Sign in" : "Send reset link"}
              </button>
            </form>
            {unconfirmed && <button disabled={busy} onClick={()=>run(onResend, ()=>null)} style={quiet}>Send the confirmation link again</button>}
            <div style={{marginTop:16,display:"flex",flexDirection:"column",alignItems:"flex-start",gap:2}}>
              {mode === "signup" && <button onClick={()=>go("login")} style={quiet}>Already have an account? Sign in</button>}
              {mode === "login" && <button onClick={()=>go("forgot")} style={quiet}>Forgot your password?</button>}
              {mode === "login" && <button onClick={()=>go("signup")} style={quiet}>New here? Create an account</button>}
            </div>
            {mode === "signup" && <div style={{marginTop:14,fontSize:13,color:C.muted}}>Want to look around first? <button onClick={onDemo} style={{background:"none",border:"none",padding:0,color:FLAT?C.turq:C.goldText,fontSize:13,fontWeight:500}}>Use the demo</button></div>}
          </>
        )}
      </div>
    </div>
  );
}

// After following a reset link: choose a new password.
function ResetPasswordScreen({ onDone }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [ready, setReady] = useState(null);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setReady(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => { if (session) setReady(true); });
    return () => sub?.subscription?.unsubscribe();
  }, []);
  const save = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setMsg({ kind: "error", text: "Use at least 8 characters." }); return; }
    setBusy(true); setMsg(null);
    const { error } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (error) setMsg({ kind: "error", text: error.message });
    else { setMsg({ kind: "ok", text: "Your password is updated." }); setTimeout(onDone, 1200); }
  };
  return (
    <div style={{minHeight:"100vh",display:"flex",justifyContent:"center",padding:"60px 24px"}}>
      <form onSubmit={save} style={{width:"100%",maxWidth:400}} className="fade">
        <h2 style={{fontFamily:Fd,fontSize:34,fontWeight:FW,color:C.text,marginBottom:10}}>Choose a new password</h2>
        {ready === false ? (
          <p style={{color:C.muted,fontSize:15,lineHeight:1.7}}>This reset link has expired or was already used. Go back to sign in and choose “Forgot your password?” to get a new one. <a href="/" style={{color:C.green}}>Back to Mithaq</a></p>
        ) : (
          <>
            <label style={{display:"block",margin:"20px 0 16px"}}>
              <span style={{display:"block",fontSize:13,color:C.text,marginBottom:6}}>New password</span>
              <input type="password" value={pw} onChange={e=>setPw(e.target.value)} autoComplete="new-password" placeholder="At least 8 characters"
                style={{width:"100%",fontSize:15,padding:"12px 14px",border:`1.5px solid ${C.parchment}`,borderRadius:8,background:C.white,color:C.text}} />
            </label>
            {msg && <div role={msg.kind === "error" ? "alert" : "status"} style={{fontSize:14,color:msg.kind === "error" ? C.rose : C.green,marginBottom:12}}>{msg.text}</div>}
            <button type="submit" disabled={busy || !ready} style={{width:"100%",background:FLAT?C.green:G_GREEN,color:C.cream,border:"none",borderRadius:8,padding:"14px",fontSize:15,opacity:busy||!ready?0.6:1}}>{busy ? "Saving..." : "Save new password"}</button>
          </>
        )}
      </form>
    </div>
  );
}

// Account: email updates, password, and deleting everything.
function AccountScreen({ user, setUser, onBack }) {
  const [emailOn, setEmailOn] = useState(user?.emailUpdates !== false);
  const [pw, setPw] = useState("");
  const [pwMsg, setPwMsg] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [delErr, setDelErr] = useState("");
  const toggleEmail = async () => {
    const next = !emailOn; setEmailOn(next);
    const { error } = await supabase.rpc("set_email_updates", { p_on: next });
    if (error) setEmailOn(!next); else setUser(u => ({ ...u, emailUpdates: next }));
  };
  const changePw = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setPwMsg({ kind: "error", text: "Use at least 8 characters." }); return; }
    const { error } = await supabase.auth.updateUser({ password: pw });
    setPwMsg(error ? { kind: "error", text: error.message } : { kind: "ok", text: "Password updated." });
    if (!error) setPw("");
  };
  const del = async () => {
    setDeleting(true); setDelErr("");
    const { error } = await supabase.rpc("delete_my_account");
    if (error) { setDelErr(error.message); setDeleting(false); return; }
    await supabase.auth.signOut();
    window.location.href = "/";
  };
  const card = {background:C.white,border:`1px solid ${C.parchment}`,borderRadius:FLAT?8:14,padding:"20px 22px",marginBottom:18};
  const h = {fontFamily:Fd,fontSize:21,color:C.green,marginBottom:6};
  if (!user?.id) return (
    <div style={{minHeight:"100vh",padding:"40px 24px"}}><div style={{maxWidth:560,margin:"0 auto"}}>
      <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:13,padding:0,marginBottom:28}}>← Back</button>
      <p style={{color:C.muted}}>You're using the demo. Create an account to manage these settings.</p>
    </div></div>
  );
  return (
    <div style={{minHeight:"100vh",background:C.cream,padding:"40px 24px 80px"}}>
      <div style={{maxWidth:560,margin:"0 auto"}} className="fade">
        <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:13,padding:0,marginBottom:28}}>← Back</button>
        <h1 style={{fontFamily:Fd,color:C.green,fontSize:34,fontWeight:FW,marginBottom:6}}>Account</h1>
        <p style={{color:C.muted,fontSize:14,marginBottom:28}}>{user.name} · {user.email}</p>

        <div style={card}>
          <div style={h}>Email reminders</div>
          <p style={{fontSize:14,color:C.muted,lineHeight:1.7,marginBottom:14}}>At most one short email a day when something new is waiting — like a check-in opening. For your privacy, emails never include names or details.</p>
          <label style={{display:"flex",alignItems:"center",gap:10,fontSize:15,color:C.text,cursor:"pointer"}}>
            <input type="checkbox" checked={emailOn} onChange={toggleEmail} style={{width:18,height:18,accentColor:C.green}} />
            Send me email reminders
          </label>
        </div>

        <form onSubmit={changePw} style={card}>
          <div style={h}>Change password</div>
          <input type="password" value={pw} onChange={e=>setPw(e.target.value)} autoComplete="new-password" placeholder="New password (at least 8 characters)" aria-label="New password"
            style={{width:"100%",fontSize:15,padding:"11px 13px",border:`1.5px solid ${C.parchment}`,borderRadius:8,background:C.cream,margin:"8px 0 12px"}} />
          {pwMsg && <div role="status" style={{fontSize:14,color:pwMsg.kind === "error" ? C.rose : C.green,marginBottom:10}}>{pwMsg.text}</div>}
          <button type="submit" style={{background:FLAT?C.green:G_GREEN,color:C.cream,border:"none",borderRadius:FLAT?6:8,padding:"10px 18px",fontSize:14}}>Update password</button>
        </form>

        <div style={{...card,borderColor:"rgba(142,47,34,0.3)"}}>
          <div style={{...h,color:C.rose}}>Delete my account</div>
          <p style={{fontSize:14,color:C.muted,lineHeight:1.7,marginBottom:12}}>
            This permanently deletes your profile, every answer, your notes, journal and family links. If you're connected with someone, the connection ends and everything you shared is deleted; they'll see a gentle notice. This can't be undone.
          </p>
          <label style={{display:"block",fontSize:14,color:C.text,marginBottom:6}}>Type <b>DELETE</b> to confirm</label>
          <input value={confirmText} onChange={e=>setConfirmText(e.target.value)} aria-label="Type DELETE to confirm"
            style={{width:"100%",fontSize:15,padding:"11px 13px",border:`1.5px solid ${C.parchment}`,borderRadius:8,background:C.cream,marginBottom:12}} />
          {delErr && <div role="alert" style={{fontSize:14,color:C.rose,marginBottom:10}}>{delErr}</div>}
          <button disabled={confirmText !== "DELETE" || deleting} onClick={del}
            style={{background:C.rose,color:"#fff",border:"none",borderRadius:FLAT?6:8,padding:"10px 18px",fontSize:14,opacity:confirmText !== "DELETE" || deleting ? 0.45 : 1}}>{deleting ? "Deleting..." : "Delete everything"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── REFRAME SCREEN ───────────────────────────────────────────────────────────
// Shown before every charter: why it matters, how long it takes, and — for
// sensitive charters — the reframe that sets the tone.
function ReframeScreen({charter, rf, partnership, onBack, onBegin}) {
  const g = guideFor(charter.id);
  const intro = rf || { title: g.intro?.title || charter.title, body: g.intro?.body || charter.sub, sub: null };
  const connected = partnership?.status === "active";
  const opensAt = charterStage(charter.id);
  const locked = connected && stageRank(opensAt) > stageRank(partnership.stage);
  const partnerFirst = partnership?.partner_name?.split(" ")[0] || "your partner";
  const after = locked
    ? `This charter opens for comparing when you and ${partnerFirst} are both ${stageInfo(opensAt).short}. Until then, your answers stay private.`
    : connected
      ? `When you and ${partnerFirst} have both finished, you can reveal it together — not before.`
      : "Your answers stay private. Nothing is shared until you choose to.";
  const light = PAPER ? C.muted : tint(C.cream,0.7);
  if (EDITORIAL) {
    const ids = STAGE_ORDER[opensAt] || [];
    const pos = ids.indexOf(charter.id);
    return (
      <div className="ed ed-chap">
        <div className="ed-wrap ed-chap-in">
          <button type="button" onClick={onBack} className="ed-link ed-back">← Back</button>
          <div className="ed-chap-grid">
            <div className="ed-chap-text">
              <p className="ed-chap-where">{stageInfo(opensAt).label}{pos >= 0 ? ` · Charter ${pos + 1} of ${ids.length}` : ""}</p>
              <p className="ed-chap-name">{charter.title}</p>
              <h1 className="ed-rise ed-chap-title">{intro.title}</h1>
              <p className="ed-chap-body">{intro.body}</p>
              {g.why && (
                <blockquote className="ed-chap-why">
                  <p className="ed-chap-why-label">Why this matters before marriage</p>
                  <p>{g.why}</p>
                </blockquote>
              )}
              {intro.sub && <p className="ed-chap-sub">{intro.sub}</p>}
              <dl className="ed-chap-facts">
                <div><dt>Questions</dt><dd>{charter.qs.length}</dd></div>
                <div><dt>Time</dt><dd>{estMinutes(charter.qs)} min</dd></div>
                <div><dt>Saving</dt><dd>As you go</dd></div>
              </dl>
              <p className="ed-chap-note">Answer in your own words. “I'm still working through this” is always a fine answer. {after}</p>
              <button type="button" onClick={onBegin} className="ed-btn">Begin</button>
            </div>
            <div className="ed-chap-art" aria-hidden="true"><KhatamLines size={460} opacity={0.5} /></div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={{minHeight:"100vh",background:PAPER?C.cream:C.green,backgroundImage:MODERN?latticeBg(C.goldL,0.08,48):undefined,position:"relative",overflow:"hidden"}}>
      {!FLAT && <div style={{position:"absolute",right:-80,top:-80,width:320,height:320,borderRadius:"50%",background:`radial-gradient(circle,${tint(C.gold,0.1)} 0%,transparent 70%)`}} />}
      <div style={{maxWidth:560,margin:"0 auto",padding:"40px 24px",display:"flex",flexDirection:"column",minHeight:"100vh",position:"relative"}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:PAPER?C.muted:tint(C.cream,0.75),fontSize:PAPER?14:13,padding:0,marginBottom:40,alignSelf:"flex-start"}}>← Back</button>
        <div className="fade" style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"center",paddingBottom:60}}>
          {FLAT ? <div style={{marginBottom:20}}><Medallion size={44} fill={PAPER?C.green:C.gold} inner={PAPER?C.goldL:C.white} /></div> : <div style={{fontSize:42,marginBottom:20}}>{rf?.icon || charter.icon}</div>}
          <div style={{color:PAPER?C.goldText:C.goldL,fontSize:FLAT?15:12,letterSpacing:FLAT?0:4,textTransform:FLAT?"none":"uppercase",marginBottom:14}}>
            {FLAT ? charter.title : `Before you begin · ${charter.title}`}
          </div>
          <h2 style={{fontFamily:Fd,color:PAPER?C.green:C.cream,fontSize:PAPER?"clamp(30px,5vw,42px)":"clamp(24px,5vw,36px)",fontWeight:FW,lineHeight:1.25,marginBottom:22,whiteSpace:"pre-line"}}>{intro.title}</h2>
          <p style={{color:PAPER?C.text:light,fontSize:PAPER?17:15,lineHeight:PAPER?1.65:1.85,fontWeight:400,marginBottom:22,whiteSpace:"pre-line"}}>{intro.body}</p>
          {g.why && (
            <div style={PAPER?{padding:"16px 18px",border:`1px solid ${C.parchment}`,borderRadius:12,background:C.white,marginBottom:18}:{padding:"14px 18px",borderLeft:`2px solid ${C.goldL}`,background:"rgba(255,255,255,0.05)",marginBottom:18}}>
              <div style={{color:PAPER?C.goldText:C.goldL,fontSize:13,fontWeight:500,marginBottom:4}}>Why this matters before marriage</div>
              <div style={{color:PAPER?C.text:"#F3F5F4",fontSize:PAPER?16:15,lineHeight:PAPER?1.6:1.65}}>{g.why}</div>
            </div>
          )}
          {intro.sub && <p style={{fontFamily:Fd,color:PAPER?C.green:C.goldL,fontSize:PAPER?19:17,fontStyle:FLAT?"normal":"italic",lineHeight:1.6,marginBottom:18}}>{intro.sub}</p>}
          <div style={{color:light,fontSize:PAPER?14:13,lineHeight:1.65,marginBottom:34,display:"grid",gap:8}}>
            <div>{charter.qs.length} questions · about {estMinutes(charter.qs)} minutes · your answers save as you go</div>
            <div>Answer in your own words. “I'm still working through this” is always a fine answer.</div>
            <div>{after}</div>
          </div>
          <button onClick={onBegin} style={{alignSelf:"flex-start",background:PAPER?C.green:FLAT?C.goldL:G_GOLD,color:PAPER?C.cream:C.onGold,border:"none",borderRadius:PAPER?999:FLAT?6:40,padding:"14px 30px",fontSize:PAPER?16:15,fontWeight:PAPER?500:600}}>Begin</button>
        </div>
      </div>
    </div>
  );
}

// After finishing something for the first time: what it means, what happens
// now, and the one next step.
function FinishedScreen({finished, partnership, next, onAct, onDashboard}) {
  const isCharter = finished.type === "charter";
  const c = finished.charter;
  const title = isCharter ? c.title : SELF_GUIDE[finished.type]?.title;
  const line = isCharter ? guideFor(c.id).after
    : finished.type === "story" ? "Anyone you share your profile with will start here — with the real you."
    : "You've shown your direction, not just where you stand today.";
  const connected = partnership?.status === "active";
  const partnerFirst = partnership?.partner_name?.split(" ")[0] || "your partner";
  let now;
  if (!isCharter) now = "This is part of your profile. You decide who sees it, from Share profile on your dashboard.";
  else if (connected && stageRank(charterStage(c.id)) > stageRank(partnership.stage))
    now = `It stays private until you and ${partnerFirst} are both ${stageInfo(charterStage(c.id)).short}. Then you can compare it together.`;
  else if (connected) now = `When ${partnerFirst} finishes it too, you can reveal it together from your Partner page.`;
  else now = "Your answers are private. When you connect with someone, you can compare this charter together.";

  return (
    <div style={{minHeight:"100vh",background:C.cream,padding:"48px 24px"}}>
      <div style={{maxWidth:560,margin:"0 auto"}} className="fade">
        {FLAT ? <Medallion size={52} fill={C.gold} inner={C.white} done /> : <div style={{fontSize:40}}>✓</div>}
        <div style={{color:FLAT?C.turq:C.goldText,fontSize:14,fontWeight:500,margin:"20px 0 8px"}}>{title} — complete</div>
        <h1 style={{fontFamily:Fd,color:C.green,fontSize:"clamp(26px,5vw,36px)",fontWeight:FW,lineHeight:1.25,marginBottom:16}}>{line}</h1>
        <p style={{color:C.muted,fontSize:15,lineHeight:1.8,marginBottom:36}}>{now}</p>
        {next && next.cta && <NextStepCard step={next} onAct={onAct} heading="Next" />}
        <button onClick={onDashboard} style={{background:"none",border:"none",color:C.muted,fontSize:14,textDecoration:"underline",textUnderlineOffset:3,padding:"12px 0"}}>Back to my dashboard</button>
      </div>
    </div>
  );
}

// The one recommended next step.
function NextStepCard({step, onAct, heading}) {
  const btn = {background:FLAT?C.green:G_GREEN,color:C.cream,border:"none",borderRadius:FLAT?6:40,padding:"12px 22px",fontSize:14,fontWeight:500};
  return (
    <div style={{background:C.white,border:`1px solid ${MODERN?C.gold:C.parchment}`,borderRadius:PAPER?12:FLAT?8:14,padding:"20px 22px",marginBottom:28,boxShadow:PAPER?`0 1px 2px ${tint(C.text,0.04)}, 0 8px 24px ${tint(C.text,0.05)}`:FLAT?"none":`0 2px 14px ${tint(C.green,0.06)}`}}>
      <div style={{display:"flex",alignItems:"center",gap:8,color:FLAT?C.turq:C.goldText,fontSize:13,fontWeight:500,marginBottom:8}}>
        {FLAT && <Star size={12} fill={C.gold} />}{heading || "Your next step"}
      </div>
      <div style={{fontFamily:Fd,fontSize:24,color:C.green,lineHeight:1.2,marginBottom:6}}>{step.title}</div>
      <div style={{fontSize:14,color:C.text,lineHeight:1.65,marginBottom:step.note?6:16}}>{step.why}</div>
      {step.note && <div style={{fontSize:13,color:C.muted,lineHeight:1.6,marginBottom:16}}>{step.note}</div>}
      <div style={{display:"flex",gap:14,alignItems:"center",flexWrap:"wrap"}}>
        {step.cta && <button onClick={()=>onAct(step)} style={btn}>{step.cta}{step.kind === "charter" ? ` ${step.title}` : ""}</button>}
        {step.minutes && <span style={{fontSize:13,color:C.muted}}>About {step.minutes} minutes</span>}
      </div>
      {step.alt && (
        <div style={{marginTop:14,paddingTop:12,borderTop:`1px solid ${C.parchment}`,fontSize:13,color:C.muted,lineHeight:1.6}}>
          Or continue with <button onClick={()=>onAct(step.alt)} style={{background:"none",border:"none",padding:0,color:FLAT?C.turq:C.goldText,fontSize:13,textDecoration:"underline",textUnderlineOffset:3}}>{step.alt.title}</button>
          {step.alt.note ? <span> — {step.alt.note}</span> : null}
        </div>
      )}
    </div>
  );
}

// A short, one-time welcome. Reopen it from "How Mithaq works".
const WELCOME_POINTS = [
  ["Start with yourself", "Write your story and the readiness survey. Everything else builds on them."],
  ["Invite the person you're getting to know", "You each answer privately. Nothing is shared until you both choose to compare."],
  ["Go deeper in stages", "Getting to know, then serious, then engaged. Harder topics open only when you're both ready."],
  ["Agree before the nikah", "Settle the differences that matter, sign together, and bring a summary to your imam."],
];
function WelcomeGuide({onClose}) {
  return (
    <Modal label="How Mithaq works" onClose={onClose}
        panelStyle={{background:C.white,borderRadius:FLAT?"12px 12px 0 0":"20px 20px 0 0",padding:"30px 24px 32px",maxWidth:540,width:"100%",maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{fontFamily:Fd,color:C.green,fontSize:"clamp(24px,5vw,30px)",fontWeight:FW,lineHeight:1.25,marginBottom:8}}>How Mithaq works</div>
        <p style={{color:C.muted,fontSize:14,lineHeight:1.7,marginBottom:22}}>The dashboard is laid out in the order these conversations usually happen. There's no rush — honest answers matter more than finished ones.</p>
        <ol style={{listStyle:"none",display:"grid",gap:16,marginBottom:26}}>
          {WELCOME_POINTS.map(([t, d], i) => (
            <li key={t} style={{display:"flex",gap:14,alignItems:"flex-start"}}>
              <span style={{width:28,height:28,borderRadius:"50%",border:`1.5px solid ${C.gold}`,color:C.green,fontFamily:Fd,fontSize:15,display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{i + 1}</span>
              <span>
                <span style={{display:"block",fontFamily:Fd,fontSize:19,color:C.green}}>{t}</span>
                <span style={{display:"block",fontSize:14,color:C.muted,lineHeight:1.6,marginTop:2}}>{d}</span>
              </span>
            </li>
          ))}
        </ol>
        <button onClick={onClose} style={{width:"100%",background:FLAT?C.green:G_GREEN,color:C.cream,border:"none",borderRadius:FLAT?6:10,padding:"15px",fontSize:15,fontWeight:500}}>Show me my first step</button>
    </Modal>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({user,done,storyDone,growthDone,witnessDone,progress,doneCount,totalSections,onCharter,onStory,onGrowth,onWitness,onProfile,onShare,shareSettings,setShareSettings,onPrerequisite,prerequisiteDone,onPreview,partnership,onPartner,openNotes,onTalkList,onJournal,onFamily,onUpdates,onAccount}) {
  const firstName = user?.name?.split(" ")[0];
  const connected = partnership?.status === "active";
  const pending = partnership?.status === "pending";
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!user?.id) return;
    supabase.rpc("get_notifications").then(({ data }) => setUnread(data?.unread || 0));
  }, [user?.id]);
  // The one gentle notice when someone else ended a connection with you.
  const [exitNotice, setExitNotice] = useState(null);
  useEffect(() => {
    if (!user?.id) return;
    supabase.rpc("my_exit_notice").then(({ data }) => setExitNotice(data || null));
  }, [user?.id]);
  const dismissNotice = async () => {
    const id = exitNotice?.id;
    setExitNotice(null);
    if (id) await supabase.rpc("dismiss_exit_notice", { p_id: id });
  };
  // Where to focus: the current stage when connected, otherwise the first
  // stage with something left to answer.
  const focusStage = connected ? (partnership.stage === "married" ? "engaged" : partnership.stage)
    : (STAGES.find(s => STAGE_ORDER[s.id].some(id => !done.includes(id)))?.id || "engaged");
  const focusIds = STAGE_ORDER[focusStage];
  const focusDone = focusIds.filter(id => done.includes(id)).length;
  const focusPct = Math.round((focusDone / focusIds.length) * 100);
  const focusLabel = `${stageInfo(focusStage).label}: ${focusDone} of ${focusIds.length} charters`;
  const next = computeNextStep({ storyDone, growthDone, done, partnership });
  const actOn = (st) => {
    if (st.kind === "story") onStory();
    else if (st.kind === "growth") onGrowth();
    else if (st.kind === "charter") onCharter(st.charter);
    else if (st.kind === "partner") onPartner();
  };
  const welcomeKey = `mithaq:welcomed:${user?.id || "demo"}`;
  const [showWelcome, setShowWelcome] = useState(() => { try { return !localStorage.getItem(welcomeKey); } catch (e) { return false; } });
  const closeWelcome = () => { setShowWelcome(false); try { localStorage.setItem(welcomeKey, "1"); } catch (e) {} };
  if (EDITORIAL) return (
    <DashboardEditorial {...{firstName,connected,pending,partnership,unread,exitNotice,dismissNotice,focusStage,next,actOn,showWelcome,setShowWelcome,closeWelcome,
      done,storyDone,growthDone,witnessDone,onCharter,onStory,onGrowth,onWitness,onShare,onPrerequisite,prerequisiteDone,onPreview,onPartner,openNotes,onTalkList,onJournal,onFamily,onUpdates,onAccount}} />
  );
  return (
    <div style={{minHeight:"100vh"}}>
      {FLAT ? (
        <div style={{background:PAPER?C.white:C.green,borderBottom:PAPER?`1px solid ${C.parchment}`:undefined,backgroundImage:MODERN?latticeBg(C.goldL,0.10,48):undefined,padding:"0 24px"}}>
          <div style={{maxWidth:880,margin:"0 auto"}}>
            <div style={{display:"flex",alignItems:"center",gap:16,paddingTop:18,flexWrap:"wrap"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginRight:"auto"}}>
                <Star size={16} fill={PAPER?C.gold:C.goldL} />
                <span style={{fontFamily:Fd,color:PAPER?C.green:C.cream,fontSize:22,fontWeight:PAPER?600:undefined}}>Mithaq</span>
              </div>
              <button onClick={onAccount} style={{background:"none",color:PAPER?C.muted:"rgba(243,245,244,0.75)",border:"none",fontSize:PAPER?14:13,padding:"6px 0"}}>Account</button>
              <button onClick={async()=>{await supabase.auth.signOut();window.location.reload();}} style={{background:"none",color:PAPER?C.muted:"rgba(243,245,244,0.75)",border:"none",fontSize:PAPER?14:13,padding:"6px 0"}}>Sign out</button>
            </div>
            <nav className="navrow" aria-label="Your tools" style={{marginTop:16}}>
              {[
                [unread ? `Updates (${unread})` : "Updates", onUpdates],
                ["Preview profile", onPreview],
                [prerequisiteDone ? "What to know first ✓" : "What to know first", onPrerequisite],
                ["Share profile", onShare],
                [`Talk list${openNotes ? ` (${openNotes})` : ""}`, onTalkList],
                ["Journal", onJournal],
                ["Family", onFamily],
                ["How Mithaq works", () => setShowWelcome(true)],
              ].map(([label, fn]) => (
                <button key={label} onClick={fn} style={PAPER?{background:C.white,color:C.text,border:`1px solid ${C.parchment}`,borderRadius:8,padding:"8px 14px",fontSize:14}:{background:"rgba(243,245,244,0.08)",color:"#EEF1F0",border:"1px solid rgba(201,162,74,0.35)",borderRadius:6,padding:"8px 14px",fontSize:13}}>{label}</button>
              ))}
            </nav>
            <div style={{padding:"30px 0 28px"}}>
              <div style={{color:PAPER?C.muted:"rgba(243,245,244,0.75)",fontSize:15,marginBottom:4}}>As-salamu alaykum,</div>
              <h1 style={{fontFamily:Fd,color:PAPER?C.green:C.cream,fontSize:"clamp(30px,5vw,40px)",fontWeight:PAPER?FW:400,marginBottom:18}}>{firstName}'s journey</h1>
              <div style={{display:"flex",alignItems:"center",gap:14}}>
                <div style={{flex:1,height:3,background:PAPER?C.parchment:"rgba(243,245,244,0.12)",borderRadius:2,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${focusPct}%`,background:PAPER?C.green:C.goldL,borderRadius:2,transition:"width 0.6s ease"}} />
                </div>
                <div style={{color:PAPER?C.muted:C.goldL,fontSize:13,whiteSpace:"nowrap"}}>{focusLabel}</div>
              </div>
            </div>
          </div>
        </div>
      ) : (
      <div style={{background:C.green,padding:"0 24px"}}>
        <div style={{maxWidth:800,margin:"0 auto"}}>
          <div className="classic-nav" style={{display:"flex",flexWrap:"wrap",gap:8,justifyContent:"flex-start",alignItems:"center",paddingTop:20}}>
            <div style={{fontFamily:Fd,color:C.goldL,fontSize:22,fontWeight:600,letterSpacing:1,marginRight:"auto",width:"100%"}}>Mithaq</div>
            <button onClick={onPreview} style={{background:tint(C.gold,0.1),color:C.goldL,border:`1px solid ${tint(C.gold,0.2)}`,borderRadius:40,padding:"8px 18px",fontSize:13,fontWeight:500}}>👁 Preview Profile</button>
            <button onClick={onPrerequisite} style={{background:prerequisiteDone?tint(C.gold,0.15):"none",color:prerequisiteDone?C.goldL:tint(C.cream,0.75),border:`1px solid ${prerequisiteDone?tint(C.gold,0.3):tint(C.cream,0.2)}`,borderRadius:40,padding:"8px 18px",fontSize:13,fontWeight:500}}>
              {prerequisiteDone?"✓ What to know first":"+ What to know first"}
            </button>
            <button onClick={onShare} style={{background:tint(C.gold,0.1),color:C.goldL,border:`1px solid ${tint(C.gold,0.2)}`,borderRadius:40,padding:"8px 18px",fontSize:13,fontWeight:500}}>🔗 Share Profile</button>
            <button onClick={onTalkList} style={{background:tint(C.gold,0.1),color:C.goldL,border:`1px solid ${tint(C.gold,0.2)}`,borderRadius:40,padding:"8px 18px",fontSize:13,fontWeight:500}}>📝 Talk List{openNotes ? ` (${openNotes})` : ""}</button>
            <button onClick={onJournal} style={{background:tint(C.gold,0.1),color:C.goldL,border:`1px solid ${tint(C.gold,0.2)}`,borderRadius:40,padding:"8px 18px",fontSize:13,fontWeight:500}}>📿 Journal</button>
            <button onClick={onFamily} style={{background:tint(C.gold,0.1),color:C.goldL,border:`1px solid ${tint(C.gold,0.2)}`,borderRadius:40,padding:"8px 18px",fontSize:13,fontWeight:500}}>👪 Family</button>
            <button onClick={onUpdates} style={{background:unread?tint(C.gold,0.25):tint(C.gold,0.1),color:C.goldL,border:`1px solid ${tint(C.gold,0.3)}`,borderRadius:40,padding:"8px 18px",fontSize:13,fontWeight:500}}>🔔 Updates{unread ? ` (${unread})` : ""}</button>
            <button onClick={onAccount} style={{background:"none",color:tint(C.cream,0.75),border:`1px solid ${tint(C.cream,0.15)}`,borderRadius:40,padding:"8px 18px",fontSize:13}}>Account</button>
            <button onClick={async()=>{await supabase.auth.signOut();window.location.reload();}} style={{background:"none",color:tint(C.cream,0.75),border:"none",borderRadius:40,padding:"8px 18px",fontSize:13,fontWeight:400}}>Sign Out</button>
          </div>
          <div style={{padding:"30px 0 26px"}}>
            <div style={{color:tint(C.cream,0.75),fontSize:12,letterSpacing:3,textTransform:"uppercase",marginBottom:6}}>As-salamu alaykum,</div>
            <h1 style={{fontFamily:Fd,color:C.cream,fontSize:30,fontWeight:300,marginBottom:18}}>{firstName}'s Journey</h1>
            <div style={{display:"flex",alignItems:"center",gap:14}}>
              <div style={{flex:1,height:4,background:tint(C.cream,0.07),borderRadius:2,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${focusPct}%`,background:G_GOLD90,borderRadius:2,transition:"width 0.6s ease"}} />
              </div>
              <div style={{color:C.goldL,fontSize:12,fontWeight:500,whiteSpace:"nowrap"}}>{focusLabel}</div>
            </div>
          </div>
        </div>
      </div>
      )}
      {MODERN && <div aria-hidden="true" style={{height:12,backgroundImage:latticeBg(C.gold,0.45,12),backgroundColor:C.soft,borderBottom:`1px solid ${C.parchment}`}} />}

      <div style={{maxWidth:800,margin:"0 auto",padding:"26px 24px 80px"}}>
        {exitNotice && (
          <div style={{background:C.white,border:`1.5px solid ${C.parchment}`,borderRadius:14,padding:"20px 22px",marginBottom:28}}>
            <div style={{fontFamily:Fd,fontSize:22,color:C.green,marginBottom:8}}>Your connection with {exitNotice.partner_name?.split(" ")[0] || "your partner"} has ended</div>
            <p style={{fontSize:14,color:C.text,lineHeight:1.8,marginBottom:8}}>
              Sometimes a path isn't written for two people, and that is part of Allah's qadr — not a judgment on either of you. What you shared together has been deleted, and your own answers are still yours.
            </p>
            <p style={{fontFamily:Fd,fontSize:16,color:C.goldText,fontStyle:"italic",lineHeight:1.6,marginBottom:16}}>May Allah write what is best for both of you, and make it easy.</p>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <button onClick={dismissNotice} style={{background:G_GREEN,color:C.cream,border:"none",borderRadius:8,padding:"10px 16px",fontSize:13}}>Close</button>
              <span style={{fontSize:12,color:C.muted,alignSelf:"center"}}>Before connecting again, you may want to review and update your answers below.</span>
            </div>
          </div>
        )}
        {showWelcome && <WelcomeGuide onClose={closeWelcome} />}
        {next && next.cta && <NextStepCard step={next} onAct={actOn} />}
        {next && !next.cta && (
          <div style={{background:C.soft,borderRadius:FLAT?8:14,padding:"18px 20px",marginBottom:28}}>
            <div style={{fontFamily:Fd,fontSize:21,color:C.green,marginBottom:4}}>{next.title}</div>
            <div style={{fontSize:14,color:C.muted,lineHeight:1.65}}>{next.why}</div>
          </div>
        )}
        <SectionLabel>Your Partner</SectionLabel>
        {FLAT ? (
          <button type="button" className="card btn-card" onClick={onPartner}
            style={{display:"flex",alignItems:"center",gap:16,background:connected?C.green:C.white,backgroundImage:connected&&MODERN?latticeBg(C.goldL,0.12,40):undefined,border:`1px solid ${connected?C.green:C.parchment}`,borderLeft:PAPER?undefined:`3px solid ${C.gold}`,borderRadius:8,padding:"18px 20px",marginBottom:32,cursor:"pointer"}}>
            <Medallion size={40} fill={connected?C.gold:C.green} inner={connected?C.white:C.goldL} />
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontFamily:Fd,fontSize:21,color:connected?C.cream:C.green,lineHeight:1.2,marginBottom:4}}>
                {connected ? `Connected with ${partnership.partner_name?.split(" ")[0] || "your partner"}` : pending ? "Invite sent — waiting for your partner" : "Invite your partner"}
              </div>
              <div style={{fontSize:13,color:connected?"rgba(243,245,244,0.75)":C.muted}}>
                {connected ? "Reveal and compare charters together" : pending ? `Your code: ${partnership.invite_code}` : "Link your accounts to compare charters side by side"}
              </div>
            </div>
          </button>
        ) : (
        <button type="button" className="card btn-card" onClick={onPartner} style={{display:"flex",alignItems:"center",gap:16,background:connected?G_GREEN:C.white,border:connected?"none":`1.5px solid ${C.parchment}`,borderRadius:14,padding:"18px 20px",marginBottom:28,cursor:"pointer",boxShadow:`0 2px 14px ${tint(C.green,0.06)}`}}>
          <div style={{fontSize:26,flexShrink:0}}>{connected?"💞":"🤲"}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontFamily:Fd,fontSize:19,fontWeight:600,color:connected?C.cream:C.text,lineHeight:1.2,marginBottom:3}}>
              {connected ? `Connected with ${partnership.partner_name?.split(" ")[0] || "your partner"}` : pending ? "Invite sent — waiting for your partner" : "Invite your partner"}
            </div>
            <div style={{fontSize:12,fontWeight:400,color:connected?tint(C.cream,0.75):C.muted}}>
              {connected ? "Reveal and compare charters together" : pending ? `Your code: ${partnership.invite_code}` : "Link your accounts to compare charters side by side"}
            </div>
          </div>
          <div style={{color:connected?C.goldL:C.goldText,fontSize:18}}>→</div>
        </button>
        )}

        <SectionLabel>Start with yourself</SectionLabel>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(230px,1fr))",gap:12,marginBottom:28}}>
          <SpecialCard icon="🪞" title="Your Story" sub={SELF_GUIDE.story.why} tag="Essential" done={storyDone} color="#2C3A1C" note="5 questions · Start here" onClick={onStory} />
          <SpecialCard icon="🌱" title="Growth Portfolio" sub={SELF_GUIDE.growth.why} tag="Essential" done={growthDone} color="#1C2A3A" note="5 questions · Show your direction" onClick={onGrowth} />
          <SpecialCard icon="🤝" title="Character Witness" sub={SELF_GUIDE.witness.why} tag="New" done={witnessDone} color="#2A1C1C" note="Invite a trusted person" onClick={onWitness} />
        </div>

        {STAGES.map(st => {
          const list = STAGE_ORDER[st.id].map(id => CHARTERS.find(c => c.id === id)).filter(Boolean);
          const n = list.filter(c => done.includes(c.id)).length;
          const later = connected ? stageRank(st.id) > stageRank(partnership.stage) : st.id !== "getting_to_know";
          const status = !later ? (connected ? "Open now" : "Start here")
            : connected ? `Answer ahead · private until you're both ${st.short}`
            : "For later · answer ahead anytime";
          return (
            <div key={st.id} style={{marginBottom:12}}>
              <SectionLabel>{st.label}</SectionLabel>
              <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap",fontSize:13,color:C.muted,lineHeight:1.6,marginTop:-4,marginBottom:14}}>
                <span style={{maxWidth:560}}>{st.sub}</span>
                <span style={{color:later?C.muted:(FLAT?C.turq:C.goldText),whiteSpace:"nowrap"}}>{status} · {n} of {list.length} done</span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(230px,1fr))",gap:12,marginBottom:28}}>
                {list.map(c => <CharterCard key={c.id} c={c} done={done.includes(c.id)} onClick={()=>onCharter(c)} highlight={later} />)}
              </div>
            </div>
          );
        })}

        <div style={{marginTop:36,paddingTop:22,borderTop:`1px solid ${C.parchment}`,textAlign:"center"}}>
          <div style={{fontFamily:Fd,color:C.muted,fontSize:14,fontStyle:"italic"}}>"The best of you are those who are best to their spouses."</div>
          {FLAT
            ? <div style={{color:C.muted,fontSize:12,marginTop:6}}>Prophet Muhammad ﷺ</div>
            : <div style={{color:C.muted,fontSize:12,letterSpacing:2,marginTop:4}}>— PROPHET MUHAMMAD ﷺ</div>}
          <div><button onClick={()=>setShowWelcome(true)} style={{marginTop:20,background:"none",border:"none",color:C.muted,fontSize:12,textDecoration:"underline",textUnderlineOffset:3}}>How Mithaq works</button></div>
          {(FLAT || THEME_TRIED) && (
            <div role="group" aria-label="Look" style={{marginTop:20,display:"flex",gap:16,justifyContent:"center",alignItems:"center",fontSize:12,color:C.muted}}>
              <span>Look:</span>
              {[["classic","Classic"],["paper","Paper"],["editorial","Editorial"],["modern","Modern"]].map(([t,label]) => (
                <button key={t} type="button" aria-pressed={THEME===t} onClick={()=>THEME!==t && switchTheme(t)}
                  style={{background:"none",border:"none",padding:"6px 0",fontSize:12,color:THEME===t?C.text:C.muted,fontWeight:THEME===t?600:400,textDecoration:THEME===t?"none":"underline",textUnderlineOffset:3}}>{label}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── EDITORIAL DASHBOARD ─────────────────────────────────────────────────────
// One thing at a time: the next step leads, tools live in a menu, the current
// stage is open and later stages stay folded until someone looks ahead.
function EdRow({ title, why, meta, done, onClick, mark }) {
  return (
    <button type="button" className="ed-row" onClick={onClick}>
      <span className="ed-row-mark" aria-hidden="true">{mark || <Medallion size={30} done={done} fill={done ? C.green : C.green} inner={C.goldL} />}</span>
      <span className="ed-row-body">
        <span className="ed-row-title">{title}</span>
        {why && <span className="ed-row-why">{why}</span>}
      </span>
      <span className={"ed-row-meta" + (done ? " is-done" : "")}>{meta}</span>
    </button>
  );
}
function DashboardEditorial(p) {
  const { firstName, connected, pending, partnership, unread, exitNotice, dismissNotice, focusStage, next, actOn, showWelcome, setShowWelcome, closeWelcome, done } = p;
  const [menu, setMenu] = useState(false);
  const [ahead, setAhead] = useState({});
  const curRank = stageRank(focusStage);
  const charterRow = (c) => {
    const isDone = done.includes(c.id);
    const soon = !c.qs.length && !isDone;
    return <EdRow key={c.id} title={c.title} why={guideFor(c.id).why || c.sub} done={isDone} onClick={() => p.onCharter(c)}
      meta={isDone ? "Done · revisit" : soon ? "Coming soon" : `${c.qs.length} questions · ${estMinutes(c.qs)} min`} />;
  };
  const self = [
    ["Your Story", SELF_GUIDE.story.why, p.storyDone, p.onStory, `5 questions · ${SELF_GUIDE.story.minutes} min`],
    ["Growth Portfolio", SELF_GUIDE.growth.why, p.growthDone, p.onGrowth, `5 questions · ${SELF_GUIDE.growth.minutes} min`],
    ["Character Witness", SELF_GUIDE.witness.why, p.witnessDone, p.onWitness, "Invite someone who knows you"],
  ];
  const selfDone = self.filter(x => x[2]).length;
  const tools = [
    ["Your profile", [["Preview profile", p.onPreview], ["Share profile", p.onShare], [p.prerequisiteDone ? "What to know first · done" : "What to know first", p.onPrerequisite]]],
    ["Together", [[`Talk list${p.openNotes ? ` · ${p.openNotes} open` : ""}`, p.onTalkList], ["Family", p.onFamily]]],
    ["For you", [["Journal", p.onJournal], ["How Mithaq works", () => setShowWelcome(true)]]],
    ["Account", [["Account settings", p.onAccount], ["Sign out", async () => { await supabase.auth.signOut(); window.location.reload(); }]]],
  ];
  const go = (fn) => { setMenu(false); fn(); };
  return (
    <div className="ed ed-dash">
      <header className="ed-dash-bar">
        <div className="ed-wrap ed-dash-bar-in">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Star size={14} fill={C.gold} />
            <span style={{ fontFamily: Fd, color: C.green, fontSize: 23, fontWeight: 600 }}>Mithaq</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button type="button" className="ed-chip" onClick={p.onUpdates}>
              Updates{unread ? <span className="ed-count" aria-label={`${unread} unread`}>{unread}</span> : null}
            </button>
            <button type="button" className="ed-chip" aria-haspopup="dialog" aria-expanded={menu} onClick={() => setMenu(true)}>
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M2 4.5h12M2 8h12M2 11.5h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>
              Menu
            </button>
          </div>
        </div>
      </header>

      <main className="ed-wrap ed-dash-main">
        {exitNotice && (
          <section className="ed-notice">
            <h2>Your connection with {exitNotice.partner_name?.split(" ")[0] || "your partner"} has ended</h2>
            <p>Sometimes a path isn't written for two people, and that is part of Allah's qadr — not a judgment on either of you. What you shared together has been deleted, and your own answers are still yours.</p>
            <p className="ed-notice-dua">May Allah write what is best for both of you, and make it easy.</p>
            <button type="button" className="ed-btn" onClick={dismissNotice}>Close</button>
          </section>
        )}
        {showWelcome && <WelcomeGuide onClose={closeWelcome} />}

        <section className="ed-greet" aria-labelledby="ed-greet-h">
          <p className="ed-greet-salam">As-salamu alaykum{firstName ? `, ${firstName}` : ""}</p>
          <h1 id="ed-greet-h" className="ed-greet-h">Your journey</h1>
          <ol className="ed-stagebar" aria-label="Stages">
            {STAGES.map(st => {
              const ids = STAGE_ORDER[st.id], n = ids.filter(id => done.includes(id)).length;
              const r = stageRank(st.id), state = r < curRank ? "past" : r === curRank ? "now" : "later";
              return (
                <li key={st.id} className={"is-" + state} aria-current={state === "now" ? "step" : undefined}>
                  <span className="ed-stagebar-fill" style={{ width: `${Math.round((n / ids.length) * 100)}%` }} />
                  <span className="ed-stagebar-label">{st.label}</span>
                  <span className="ed-stagebar-n">{n} of {ids.length}</span>
                </li>
              );
            })}
          </ol>
        </section>

        {next && (
          <section className="ed-next" aria-labelledby="ed-next-h">
            <p className="ed-next-kicker"><Star size={11} fill={C.goldL} /> Your next step</p>
            <h2 id="ed-next-h">{next.title}</h2>
            <p className="ed-next-why">{next.why}</p>
            {next.note && <p className="ed-next-note">{next.note}</p>}
            {next.cta && (
              <div className="ed-next-actions">
                <button type="button" className="ed-btn ed-btn-gold" onClick={() => actOn(next)}>{next.cta}</button>
                {next.minutes ? <span>About {next.minutes} minutes</span> : null}
              </div>
            )}
            {next.alt && <p className="ed-next-alt">Or continue with <button type="button" onClick={() => actOn(next.alt)}>{next.alt.title}</button></p>}
          </section>
        )}

        <section className="ed-sec" aria-labelledby="ed-self-h">
          <div className="ed-sec-head">
            <h2 id="ed-self-h">Begin with yourself</h2>
            <span>{selfDone} of 3 done</span>
          </div>
          <div className="ed-list">
            {self.map(([t, why, d, fn, meta]) => <EdRow key={t} title={t} why={why} done={d} onClick={fn} meta={d ? "Done · revisit" : meta} />)}
          </div>
        </section>

        <section className="ed-sec" aria-labelledby="ed-partner-h">
          <div className="ed-sec-head"><h2 id="ed-partner-h">Your partner</h2></div>
          <div className="ed-list">
            <EdRow onClick={p.onPartner} done={connected}
              title={connected ? `Connected with ${partnership.partner_name?.split(" ")[0] || "your partner"}` : pending ? "Invite sent" : "Invite your partner"}
              why={connected ? "Reveal and compare charters together." : pending ? `Waiting for them to accept. Your code: ${partnership.invite_code}` : "You each answer privately. Nothing is shared until you both choose to compare."}
              meta={connected ? "Open" : pending ? "Waiting" : "Invite"} />
          </div>
        </section>

        {STAGES.map(st => {
          const list = STAGE_ORDER[st.id].map(id => CHARTERS.find(c => c.id === id)).filter(Boolean);
          const n = list.filter(c => done.includes(c.id)).length;
          const r = stageRank(st.id);
          const folded = r > curRank && !ahead[st.id];
          const note = r > curRank
            ? (connected ? `Private until you're both ${st.short}. You can answer ahead.` : "For later. You can answer ahead anytime.")
            : st.sub;
          return (
            <section key={st.id} className={"ed-sec" + (r > curRank ? " is-later" : "")} aria-labelledby={`ed-st-${st.id}`}>
              <div className="ed-sec-head">
                <h2 id={`ed-st-${st.id}`}>{st.label}</h2>
                <span>{n} of {list.length} done</span>
              </div>
              <p className="ed-sec-note">{note}</p>
              {folded ? (
                <button type="button" className="ed-ahead" aria-expanded="false" onClick={() => setAhead(a => ({ ...a, [st.id]: true }))}>
                  Look ahead at {list.length} {list.length === 1 ? "charter" : "charters"}
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M3 5l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
              ) : (
                <div className="ed-list ed-list-2">{list.map(charterRow)}</div>
              )}
            </section>
          );
        })}

        <footer className="ed-dash-foot">
          <Star size={12} fill={C.gold} />
          <p className="ed-dash-hadith">“The best of you are those who are best to their spouses.”</p>
          <p>Prophet Muhammad ﷺ</p>
        </footer>
      </main>

      {menu && (
        <Modal label="Menu" onClose={() => setMenu(false)} panelStyle={{ background: C.white, borderRadius: "18px 18px 0 0", padding: "26px 24px 30px", maxWidth: 460, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
          <div className="ed-menu">
            <div className="ed-menu-top">
              <span style={{ fontFamily: Fd, fontSize: 26, color: C.green, fontWeight: 600 }}>Menu</span>
              <button type="button" className="ed-link" onClick={() => setMenu(false)}>Close</button>
            </div>
            {tools.map(([group, items]) => (
              <div key={group} className="ed-menu-group">
                <p>{group}</p>
                {items.map(([label, fn]) => <button key={label} type="button" onClick={() => go(fn)}>{label}</button>)}
              </div>
            ))}
            <div className="ed-menu-group">
              <p>Look</p>
              <div role="group" aria-label="Look" className="ed-looks">
                {[["classic", "Classic"], ["paper", "Paper"], ["editorial", "Editorial"], ["modern", "Modern"]].map(([t, label]) => (
                  <button key={t} type="button" aria-pressed={THEME === t} onClick={() => THEME !== t && switchTheme(t)}>{label}</button>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function SectionLabel({children}) {
  if (FLAT) return (
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
      <Star size={11} fill={C.gold} />
      <span style={{fontFamily:Fd,fontSize:20,color:C.green,whiteSpace:"nowrap"}}>{children}</span>
      <span style={{flex:1,height:1,background:`linear-gradient(90deg,${C.parchment},transparent)`}} />
    </div>
  );
  return <div style={{fontSize:12,letterSpacing:3,textTransform:"uppercase",color:C.muted,marginBottom:12,fontWeight:500}}>{children}</div>;
}

// Modern: the three self-portrait cards stand under an arch.
function SpecialCardModern({title,sub,done,note,onClick}) {
  return (
    <button type="button" className="card btn-card btn-stack" onClick={onClick}
      style={{background:done?C.green:C.white,borderRadius:"140px 140px 8px 8px",padding:"44px 22px 22px",border:`1px solid ${done?C.green:C.parchment}`,cursor:"pointer",textAlign:"center",backgroundImage:done&&MODERN?latticeBg(C.goldL,0.16,40):undefined}}>
      <div style={{display:"flex",justifyContent:"center",marginBottom:12}}>
        <Medallion size={38} fill={done?C.gold:C.green} inner={done?C.white:C.goldL} done={done} />
      </div>
      <div style={{fontFamily:Fd,fontSize:21,lineHeight:1.2,marginBottom:6,color:done?C.cream:C.green}}>{title}</div>
      <div style={{fontSize:13,color:done?"rgba(243,245,244,0.75)":C.muted,lineHeight:1.55,marginBottom:12}}>{sub}</div>
      <div style={{fontSize:12,color:done?C.goldL:C.turq,fontWeight:500}}>{done?"Completed":note}</div>
    </button>
  );
}

// Modern: charter tiles, like glazed tiles set in a panel.
function CharterCardModern({c,done,onClick,lockNote}) {
  const soon = !c.qs.length && !done;
  return (
    <button type="button" className="card btn-card" onClick={onClick}
      style={{display:"flex",gap:14,alignItems:"flex-start",background:done?C.soft:C.white,borderRadius:8,padding:"16px 16px 14px",border:`1px solid ${C.parchment}`,cursor:c.qs.length||done?"pointer":"default"}}>
      <Medallion size={34} fill={done?C.gold:C.green} inner={done?C.white:C.goldL} done={done} />
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontFamily:Fd,fontSize:17,lineHeight:1.25,color:C.green,marginBottom:3}}>{c.title}</div>
        <div style={{fontSize:13,color:C.muted,lineHeight:1.5}}>{guideFor(c.id).why || c.sub}</div>
        <div style={{fontSize:12,marginTop:8,color:done?C.goldText:C.turq}}>
          {done ? "Completed — tap to revisit" : soon ? "Coming soon" : `${c.qs.length} questions · about ${estMinutes(c.qs)} min`}
        </div>
        {lockNote && <div style={{fontSize:12,color:C.muted,marginTop:4}}>{lockNote}</div>}
      </div>
    </button>
  );
}

function SpecialCard(props) {
  if (FLAT) return <SpecialCardModern {...props} />;
  return <SpecialCardClassic {...props} />;
}
function SpecialCardClassic({icon,title,sub,tag,done,color,note,onClick}) {
  return (
    <button type="button" className="card btn-card btn-stack" onClick={onClick} style={{background:done?`linear-gradient(135deg,${color}ee,${color}bb)`:C.white,borderRadius:14,padding:"20px",border:done?"none":`1.5px solid ${C.parchment}`,boxShadow:"0 2px 14px rgba(28,58,46,0.06)",cursor:"pointer"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
        <span style={{fontSize:24}}>{icon}</span>
        <div style={{fontSize:12,letterSpacing:1.5,textTransform:"uppercase",fontWeight:600,color:done?"rgba(248,243,236,0.75)":C.goldText,background:done?"rgba(248,243,236,0.08)":"rgba(184,137,42,0.08)",padding:"3px 9px",borderRadius:20}}>{done?"✓ Done":tag}</div>
      </div>
      <div style={{fontFamily:Fd,fontSize:18,fontWeight:600,lineHeight:1.2,marginBottom:4,color:done?C.cream:C.text}}>{title}</div>
      <div style={{fontSize:12,color:done?"rgba(248,243,236,0.75)":C.muted,fontWeight:400,marginBottom:10}}>{sub}</div>
      <div style={{fontSize:12,color:done?"rgba(248,243,236,0.75)":C.goldText,fontWeight:done?400:500}}>{done?"Completed ✓":note}</div>
    </button>
  );
}
function CharterCard(props) {
  if (FLAT) return <CharterCardModern {...props} />;
  return <CharterCardClassic {...props} />;
}
function CharterCardClassic({c,done,onClick,highlight,lockNote}) {
  return (
    <button type="button" className="card btn-card btn-stack" onClick={onClick} style={{background:done?`linear-gradient(135deg,${c.color}ee,${c.color}bb)`:C.white,borderRadius:14,padding:"18px",border:done?"none":`1.5px solid ${C.parchment}`,boxShadow:"0 2px 12px rgba(28,58,46,0.05)",cursor:c.qs.length||done?"pointer":"default"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
        <span style={{fontSize:20}}>{c.icon}</span>
        <div style={{fontSize:12,letterSpacing:1.5,textTransform:"uppercase",fontWeight:600,color:done?"rgba(248,243,236,0.75)":C.goldText,background:done?"rgba(248,243,236,0.08)":highlight?"rgba(212,168,67,0.1)":"rgba(184,137,42,0.08)",padding:"3px 9px",borderRadius:20}}>{done?"✓":c.tag}</div>
      </div>
      <div style={{fontFamily:Fd,fontSize:15,fontWeight:600,lineHeight:1.2,marginBottom:3,color:done?C.cream:C.text}}>{c.title}</div>
      <div style={{fontSize:12,color:done?"rgba(248,243,236,0.75)":C.muted,fontWeight:400,lineHeight:1.5,marginBottom:8}}>{guideFor(c.id).why || c.sub}</div>
      <div style={{fontSize:12,color:done?"rgba(248,243,236,0.75)":C.muted}}>{done ? "Completed · tap to revisit" : `${c.qs.length} questions · about ${estMinutes(c.qs)} min`}{!c.qs.length&&!done&&<span style={{color:C.goldText,marginLeft:4}}>• Soon</span>}</div>
      {lockNote && <div style={{fontSize:12,color:C.muted,marginTop:6}}>🔒 {lockNote}</div>}
    </button>
  );
}

// ─── "SAY MORE" NOTE ──────────────────────────────────────────────────────────
// Optional, collapsed until asked for. Private questions keep the note to
// yourself; otherwise only your partner sees it, when you compare.
function SayMoreNote({ mode, value, onChange }) {
  const [open, setOpen] = useState(!!value);
  if (!open) {
    return (
      <button onClick={()=>setOpen(true)} style={{marginTop:14,background:"none",border:"none",fontSize:12,color:C.goldText,fontWeight:500,padding:0,cursor:"pointer",textAlign:"left"}}>
        + Say more, or how this feels for you <span style={{color:C.muted,fontWeight:300}}>(optional)</span>
      </button>
    );
  }
  return (
    <div style={{marginTop:14}}>
      <textarea aria-label="Say more, or how this feels for you (optional)" aria-describedby="say-more-help" value={value} onChange={e=>onChange(e.target.value)} maxLength={NOTE_MAX} rows={3}
        placeholder="Say more, or how this feels for you"
        style={{width:"100%",fontSize:13,fontWeight:400,padding:"12px",border:`1.5px solid ${C.parchment}`,borderRadius:10,background:C.white,color:C.text,lineHeight:1.7}} />
      <div style={{display:"flex",justifyContent:"space-between",gap:12,marginTop:4,fontSize:12,color:C.muted,lineHeight:1.5}}>
        <span id="say-more-help">{mode === "private"
          ? "🔒 Only you can see this note — this question is compared privately."
          : "Your partner sees this when you compare. It never appears on profile or family links."}</span>
        <span style={{whiteSpace:"nowrap"}}>{value.length}/{NOTE_MAX}</span>
      </div>
    </div>
  );
}

// ─── SURVEY SCREEN ────────────────────────────────────────────────────────────
function SurveyScreen({q,step,total,curAns,canNext,isWorking,setAns,onNext,toggleWorking,onBack,showExample,toggleExample,label,isNarrative,answerDate,bg,flagged,onToggleFlag,laterStage,noteMode,note,onNote}) {
  const bgColor = bg || C.cream;
  const isSensitive = q?.sensitive;
  // Moving to the next question isn't a screen change, so do the same here:
  // back to the top, and focus on the new question so it's announced.
  const qid = q?.id;
  const firstQuestion = useRef(true);
  useEffect(() => {
    if (firstQuestion.current) { firstQuestion.current = false; return; }
    window.scrollTo(0, 0);
    focusViewHeading();
  }, [qid]);
  // "Continue" stays pressable. If there's no answer yet, it says what's
  // needed (announced to screen readers) and moves focus to the answer.
  const [nudge, setNudge] = useState("");
  useEffect(() => { setNudge(""); }, [qid]);
  useEffect(() => { if (canNext) setNudge(""); }, [canNext]);
  const tryNext = () => {
    if (canNext) { onNext(); return; }
    const canDefer = q.type === "text" || q.type === "choice";
    const need = q.type === "text" ? "Write a few words first" : q.type === "multi" ? "Choose at least one answer first" : "Choose an answer first";
    setNudge(canDefer ? `${need}, or tap “I'm still working through this.”` : `${need}.`);
    const first = document.querySelector("#survey-answer, .vh-input");
    if (first) first.focus();
  };
  return (
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",background:bgColor}}>
      <div style={{padding:"16px 24px",display:"flex",alignItems:"center",gap:14,borderBottom:`1px solid ${C.parchment}`}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:13,padding:0}}><span aria-hidden="true">← </span>Exit</button>
        <div style={{flex:1,height:3,background:C.parchment,borderRadius:2,overflow:"hidden"}}>
          <div style={{height:"100%",width:`${((step+1)/total)*100}%`,background:PAPER?(isNarrative?C.gold:C.green):isNarrative?G_GOLD90:G_GREEN90,borderRadius:2,transition:"width 0.4s"}} />
        </div>
        <div style={{fontSize:12,color:C.muted,whiteSpace:"nowrap"}}>{step+1}/{total}</div>
      </div>

      <div style={{flex:1,maxWidth:580,margin:"0 auto",width:"100%",padding:"32px 24px 130px",display:"flex",flexDirection:"column",justifyContent:"center"}} className="fade" key={step}>
        <div style={{fontSize:12,letterSpacing:3,textTransform:"uppercase",color:C.goldText,marginBottom:q.label?6:14}}>{PAPER ? String(label).replace(/^[^\p{L}]+/u, "") : label}</div>
        {q.label && <div style={{fontFamily:Fd,color:C.muted,fontSize:13,fontStyle:"italic",marginBottom:8}}>{q.label}</div>}

        {laterStage && (
          <div style={{display:"inline-flex",alignItems:"center",gap:6,background:tint(C.stone,0.08),borderRadius:20,padding:"4px 12px",marginBottom:12,alignSelf:"flex-start"}}>
            <span aria-hidden="true" style={{fontSize:12}}>🔒</span>
            <span style={{fontSize:12,color:C.muted,letterSpacing:1,textTransform:"uppercase",fontWeight:500}}>Shared only once you're both {laterStage.short}</span>
          </div>
        )}

        {/* Sensitive marker */}
        {isSensitive && (
          <div style={{display:"inline-flex",alignItems:"center",gap:6,background:tint(C.stone,0.08),borderRadius:20,padding:"4px 12px",marginBottom:12,alignSelf:"flex-start"}}>
            <span aria-hidden="true" style={{fontSize:12}}>🔒</span>
            <span style={{fontSize:12,color:C.muted,letterSpacing:1,textTransform:"uppercase",fontWeight:500}}>Shared only with your partner</span>
          </div>
        )}

        <h2 id="survey-question" style={{fontFamily:Fd,fontSize:PAPER?"clamp(24px,4.5vw,32px)":"clamp(20px,4vw,29px)",fontWeight:PAPER?FW:400,lineHeight:PAPER?1.25:1.35,color:PAPER?C.green:C.text,textWrap:PAPER?"balance":undefined,marginBottom:q.hint?8:22}}>{q.text}</h2>

        {q.hint && !isWorking && (
          <div id="survey-hint" style={{fontSize:12,color:C.muted,fontStyle:"italic",marginBottom:20,lineHeight:1.6,paddingLeft:12,borderLeft:`2px solid ${C.parchment}`}}>{q.hint}</div>
        )}

        {isWorking && (
          <div style={{background:tint(C.gold,0.07),borderRadius:10,padding:"14px 16px",marginBottom:20,border:`1.5px dashed ${tint(C.gold,0.3)}`}}>
            <div style={{fontSize:13,color:C.amber,fontWeight:500,marginBottom:4}}>Still working through this</div>
            <div style={{fontSize:12,color:C.muted,lineHeight:1.6,fontWeight:400}}>This will show on your profile as a signal of self-awareness — not a gap. It's one of the most honest things you can say.</div>
          </div>
        )}

        {/* Answer options are real radio buttons / checkboxes inside labels, so they
            work with the keyboard (Tab into the group, arrows or Space to choose)
            and screen readers announce the question, each option and whether it's
            chosen. The native input is visually hidden; the drawn circle/box is
            the visible indicator. */}
        {!isWorking && (q.type==="choice" || q.type==="multi") && (
          <fieldset aria-labelledby="survey-question" style={{border:0,padding:0,margin:0,minWidth:0,display:"flex",flexDirection:"column",gap:10}}>
            {q.options.map(opt=>{
              const multi=q.type==="multi";
              const sel=multi?(Array.isArray(curAns)&&curAns.includes(opt)):curAns===opt;
              const choose=()=>{
                if (!multi) return setAns(opt);
                const p=Array.isArray(curAns)?curAns:[];
                setAns(sel?p.filter(x=>x!==opt):[...p,opt]);
              };
              return(
                <label key={opt} className="opt"
                  style={{position:"relative",padding:"12px 16px",borderRadius:10,border:`1.5px solid ${sel?C.green:C.parchment}`,background:sel?tint(C.green,0.05):C.white,cursor:"pointer",fontSize:14,color:sel?C.green:C.text,fontWeight:sel?500:400,display:"flex",alignItems:"center",gap:12,transition:"border-color 0.2s, background-color 0.2s",lineHeight:1.5}}>
                  <input type={multi?"checkbox":"radio"} name={q.id} value={opt} checked={sel} onChange={choose} className="vh-input" />
                  <span aria-hidden="true" className="opt-ind" style={{width:17,height:17,borderRadius:multi?4:"50%",border:`2px solid ${sel?C.green:C.muted}`,background:sel?C.green:"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {sel&&(multi
                      ? <span style={{color:C.white,fontSize:12}}>✓</span>
                      : <span style={{width:6,height:6,borderRadius:"50%",background:C.cream}} />)}
                  </span>
                  {opt}
                </label>
              );
            })}
          </fieldset>
        )}

        {!isWorking && q.type==="text" && (
          <div>
            <textarea id="survey-answer" aria-labelledby="survey-question" aria-describedby={q.hint ? "survey-hint" : undefined} value={curAns||""} onChange={e=>setAns(e.target.value)} placeholder={q.placeholder} rows={isNarrative?5:4}
              style={{width:"100%",fontSize:14,fontWeight:400,padding:"14px",border:`1.5px solid ${C.parchment}`,borderRadius:10,background:C.white,color:C.text,lineHeight:1.75}} />
            {q.example && (
              <div style={{marginTop:10}}>
                <button onClick={toggleExample} aria-expanded={!!showExample[q.id]} style={{background:"none",border:"none",fontSize:12,color:C.goldText,fontWeight:500,padding:0,cursor:"pointer"}}>
                  <span aria-hidden="true">{showExample[q.id]?"▲ ":"▾ "}</span>{showExample[q.id]?"Hide example":"Show me a concrete example"}
                </button>
                {showExample[q.id] && (
                  <div style={{marginTop:8,padding:"12px 14px",background:tint(C.gold,0.04),borderRadius:8,border:`1px solid ${tint(C.gold,0.12)}`,fontSize:12,color:C.muted,lineHeight:1.75,fontStyle:"italic"}}>{q.example}</div>
                )}
              </div>
            )}
          </div>
        )}

        {!isWorking && q.type==="scale" && (
          <div style={{display:"flex",flexDirection:"column",gap:18}}>
            {q.items.map(item=>{
              const val=(curAns||{})[item]||3;
              return(
                <div key={item}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                    <span style={{fontSize:14,color:C.text,fontWeight:400}}>{item}</span>
                    <span style={{fontSize:12,color:C.goldText,fontWeight:600}}>{val}/5</span>
                  </div>
                  <input type="range" min={1} max={5} value={val} aria-label={item} aria-valuetext={`${val} of 5`} onChange={e=>setAns({...(curAns||{}),[item]:Number(e.target.value)})} />
                </div>
              );
            })}
          </div>
        )}

        {noteMode && !isWorking && <SayMoreNote key={q.id} mode={noteMode} value={note} onChange={onNote} />}

        {answerDate && !isWorking && (
          <div style={{marginTop:12,fontSize:12,color:C.muted,display:"flex",alignItems:"center",gap:4}}>
            <span aria-hidden="true">📅</span> True as of {answerDate}
          </div>
        )}

        {onToggleFlag && !isWorking && (
          <button onClick={onToggleFlag} aria-pressed={!!flagged} style={{marginTop:14,background:"none",border:"none",fontSize:12,color:flagged?C.goldText:C.muted,padding:0,cursor:"pointer",textAlign:"left",fontWeight:flagged?500:400}}>
            <span aria-hidden="true">{flagged ? "★ " : "☆ "}</span>{flagged ? "This really matters to me" : <><span aria-hidden="true">Mark: </span>this really matters to me</>}
            <span style={{display:"block",fontSize:12,color:C.muted,fontWeight:400,marginTop:2}}>Private — only moves this higher if you two see it differently.</span>
          </button>
        )}

        {(q.type==="text"||q.type==="choice") && (
          <button onClick={toggleWorking} style={{marginTop:14,background:"none",border:"none",fontSize:12,color:isWorking?C.amber:C.muted,padding:0,cursor:"pointer",textAlign:"left",fontWeight:isWorking?500:400}}>
            <span aria-hidden="true">{isWorking?"✕ ":"🤍 "}</span>{isWorking?"Actually, I want to answer this":"I'm still working through this"}
          </button>
        )}
      </div>

      <div style={{position:"fixed",bottom:0,left:0,right:0,padding:"16px 24px",background:`linear-gradient(transparent,${bgColor} 50%)`}}>
        <div style={{maxWidth:580,margin:"0 auto"}}>
          <div role="status" style={{fontSize:13,color:C.amber,textAlign:"center",lineHeight:1.5,marginBottom:nudge?10:0,background:nudge?bgColor:"transparent",borderRadius:8,padding:nudge?"6px 10px":0}}>{nudge}</div>
          <button onClick={tryNext} style={{width:"100%",background:canNext?(isNarrative?G_GOLD:G_GREEN):C.parchment,color:canNext?(isNarrative?C.onGold:C.cream):C.muted,border:"none",borderRadius:10,padding:"15px 24px",fontSize:15,fontWeight:600,transition:"background-color 0.2s, color 0.2s",cursor:"pointer"}}>
            {step<total-1?"Continue":"Complete"}<span aria-hidden="true">{step<total-1?" →":" ✓"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── WITNESS SCREEN ───────────────────────────────────────────────────────────
function WitnessScreen({witness,setWitness,witnessDone,onSubmit,onBack}) {
  const ready = witness.name&&witness.relationship&&witness.note.length>20;
  // Submit stays pressable; if something is missing it says what and moves
  // focus to the first missing field.
  const [nudge, setNudge] = useState("");
  useEffect(() => { if (ready) setNudge(""); }, [ready]);
  const trySubmit = () => {
    if (ready) { onSubmit(); return; }
    const missing = [];
    if (!witness.name) missing.push(["witness-name", "their name"]);
    if (!witness.relationship) missing.push(["witness-relationship", "how they know you"]);
    if (witness.note.length <= 20) missing.push(["witness-note-field", "their note (a few sentences)"]);
    const list = missing.map(m => m[1]);
    setNudge(`Still needed: ${list.length > 1 ? list.slice(0, -1).join(", ") + " and " + list[list.length - 1] : list[0]}.`);
    const el = document.getElementById(missing[0][0]);
    if (el) el.focus();
  };
  return (
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column"}}>
      <div style={{background:C.green,padding:"0 24px"}}>
        <div style={{maxWidth:600,margin:"0 auto",paddingTop:20,paddingBottom:36}}>
          <button onClick={onBack} style={{background:"none",border:"none",color:tint(C.cream,0.75),fontSize:13,padding:0,marginBottom:24}}>← Dashboard</button>
          <div style={{fontSize:36,marginBottom:14}}>🤝</div>
          <div style={{color:C.goldL,fontSize:12,letterSpacing:4,textTransform:"uppercase",marginBottom:10}}>Character Witness</div>
          <h1 style={{fontFamily:Fd,color:C.cream,fontSize:"clamp(22px,5vw,34px)",fontWeight:300,lineHeight:1.2,marginBottom:14}}>The one thing on your profile you didn't write yourself.</h1>
          <p style={{color:tint(C.cream,0.75),fontSize:13,lineHeight:1.8,fontWeight:400,maxWidth:480}}>A trusted person adds an unedited note to your profile. You don't review it before it appears. That's the point.</p>
        </div>
      </div>
      <div style={{maxWidth:600,margin:"0 auto",padding:"28px 24px 80px",width:"100%"}}>
        <div style={{background:tint(C.gold,0.05),border:`1px solid ${tint(C.gold,0.15)}`,borderRadius:12,padding:"16px 18px",marginBottom:24}}>
          <div style={{fontSize:12,color:C.goldText,fontWeight:600,marginBottom:6}}>Why this matters</div>
          <p style={{fontSize:13,color:C.muted,lineHeight:1.75,fontWeight:400}}>In Islamic tradition, character witnesses have always been part of the marriage process. This digitizes that — not as a reference letter, but as a human voice that impression management cannot fake.</p>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {[["Their name","name","text","e.g. Yusuf Al-Amin"],["Their relationship to you","relationship","text","e.g. Close friend of 8 years, mentor"]].map(([label,key,type,ph])=>(
            <label key={key} style={{display:"block"}}>
              <span style={{display:"block",fontSize:12,letterSpacing:2,textTransform:"uppercase",color:C.muted,marginBottom:6,fontWeight:500}}>{label}</span>
              <input id={`witness-${key}`} type={type} value={witness[key]} onChange={e=>setWitness(w=>({...w,[key]:e.target.value}))} placeholder={ph}
                style={{width:"100%",fontSize:14,padding:"12px 14px",border:`1.5px solid ${C.parchment}`,borderRadius:8,background:C.white,color:C.text}}
                onFocus={e=>e.target.style.borderColor=C.gold} onBlur={e=>e.target.style.borderColor=C.parchment} />
            </label>
          ))}
          <div>
            <div id="witness-note-label" style={{fontSize:12,letterSpacing:2,textTransform:"uppercase",color:C.muted,marginBottom:6,fontWeight:500}}>Their note — in their own words</div>
            <div id="witness-note-help" style={{fontSize:12,color:C.muted,marginBottom:8,fontStyle:"italic",lineHeight:1.6}}>Ask them: "What would you want someone to know about me before they decided if I was worth knowing?"</div>
            <textarea id="witness-note-field" aria-labelledby="witness-note-label" aria-describedby="witness-note-help" value={witness.note} onChange={e=>setWitness(w=>({...w,note:e.target.value}))} placeholder="Ask your witness to write this directly — or draft it together honestly..." rows={5}
              style={{width:"100%",fontSize:14,fontWeight:400,padding:"14px",border:`1.5px solid ${C.parchment}`,borderRadius:10,background:C.white,color:C.text,lineHeight:1.75}} />
          </div>
        </div>
        <div role="status" style={{fontSize:13,color:C.amber,lineHeight:1.5,marginTop:nudge?16:0}}>{nudge}</div>
        <button onClick={trySubmit} style={{width:"100%",marginTop:22,background:ready?G_GREEN:C.parchment,color:ready?C.cream:C.muted,border:"none",borderRadius:10,padding:"15px 24px",fontSize:15,fontWeight:500,cursor:"pointer",transition:"background-color 0.2s, color 0.2s"}}>
          {witnessDone?"Update Witness Note →":"Save Witness Note →"}
        </button>
      </div>
    </div>
  );
}

// ─── PROFILE SCREEN ───────────────────────────────────────────────────────────
function ProfileScreen({user,done,storyDone,growthDone,witnessDone,storyAnswers,growthAnswers,workingThrough,witness,answerDates,progress,profileUrl,profileTab,setProfileTab,onBack,onStory,onGrowth,onWitness,onShare}) {
  const tabs = [["story","My Story"],["growth","Growth"],["witness","Witness"],["charters","Charters"],["share","Share"]];
  return (
    <div style={{minHeight:"100vh"}}>
      <div style={{background:C.green,padding:"0 24px"}}>
        <div style={{maxWidth:660,margin:"0 auto"}}>
          <div style={{paddingTop:20}}><button onClick={onBack} style={{background:"none",border:"none",color:tint(C.cream,0.75),fontSize:13,padding:0}}>← Dashboard</button></div>
          <div style={{padding:"26px 0 30px",textAlign:"center"}}>
            <div style={{width:62,height:62,borderRadius:"50%",background:G_GOLD,margin:"0 auto 12px",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:Fd,color:C.onGold,fontSize:22,fontWeight:600}}>{user?.name?.[0]}</div>
            <h1 style={{fontFamily:Fd,color:C.cream,fontSize:24,fontWeight:300,marginBottom:4}}>{user?.name}</h1>
            <div style={{color:C.goldL,fontSize:12,marginTop:6}}>{progress}% complete</div>
          </div>
        </div>
      </div>

      <div style={{background:C.white,borderBottom:`1px solid ${C.parchment}`,padding:"0 24px",position:"sticky",top:0,zIndex:10}}>
        <div style={{maxWidth:660,margin:"0 auto",display:"flex",gap:0,overflowX:"auto"}}>
          {tabs.map(([id,label])=>(
            <button key={id} onClick={()=>setProfileTab(id)} style={{padding:"12px 14px",fontSize:13,fontWeight:profileTab===id?500:400,color:profileTab===id?C.text:C.muted,background:"none",border:"none",borderBottom:profileTab===id?`2px solid ${C.gold}`:"2px solid transparent",whiteSpace:"nowrap",transition:"color 0.2s, border-color 0.2s",cursor:"pointer"}}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{maxWidth:660,margin:"0 auto",padding:"22px 24px 60px"}} className="fade" key={profileTab}>

        {profileTab==="story" && (
          !storyDone ? <EmptyState icon="🪞" title="Your story isn't written yet" sub="5 questions that let someone see the real you." cta="Write Your Story →" onClick={onStory} gold /> :
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {STORY_QS.map((q,i)=><AnswerCard key={q.id} label={["On a hard day","Love's first teacher","The gap","The question nobody asks","Right now"][i]} prompt={q.text} answer={workingThrough[q.id]?"🤍 Still working through this":storyAnswers[q.id]} date={answerDates[q.id]} isWorking={workingThrough[q.id]} />)}
          </div>
        )}

        {profileTab==="growth" && (
          !growthDone ? <EmptyState icon="🌱" title="Show your trajectory" sub="Not where you are — where you're going. 5 questions." cta="Build Your Portfolio →" onClick={onGrowth} /> :
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {GROWTH_QS.map((q,i)=><AnswerCard key={q.id} label={["Active work","Deen trajectory","Five-year self","Growth edges","Most proud of"][i]} prompt={q.text||q.prompt} answer={workingThrough[q.id]?"🤍 Still working through this":Array.isArray(growthAnswers[q.id])?growthAnswers[q.id].join(" · "):growthAnswers[q.id]} date={answerDates[q.id]} isWorking={workingThrough[q.id]} />)}
          </div>
        )}

        {profileTab==="witness" && (
          !witnessDone ? <EmptyState icon="🤝" title="No witness note yet" sub="Invite someone who knows you to add their unedited voice." cta="Add Character Witness →" onClick={onWitness} /> :
          <div>
            <div style={{background:C.white,borderRadius:14,padding:"24px",border:`1.5px solid ${C.parchment}`,marginBottom:14}}>
              <div style={{fontSize:12,letterSpacing:2.5,textTransform:"uppercase",color:C.muted,marginBottom:4}}>Written by</div>
              <div style={{fontSize:15,fontWeight:500,color:C.text,marginBottom:2}}>{witness.name}</div>
              <div style={{fontSize:12,color:C.muted,marginBottom:18}}>{witness.relationship}</div>
              <div style={{width:"100%",height:1,background:C.parchment,marginBottom:18}} />
              <div style={{fontFamily:Fd,fontSize:17,color:C.text,lineHeight:1.8,fontStyle:"italic"}}>"{witness.note}"</div>
            </div>
            <div style={{fontSize:12,color:C.muted,textAlign:"center",lineHeight:1.6}}>This note appears on your shareable profile exactly as written.</div>
            <button onClick={onWitness} style={{width:"100%",marginTop:14,background:"none",border:`1px solid ${C.parchment}`,borderRadius:8,padding:"11px",fontSize:13,color:C.muted,cursor:"pointer"}}>Update witness note</button>
          </div>
        )}

        {profileTab==="charters" && (
          done.length===0 ? <EmptyState icon="📋" title="No charters completed yet" sub="Head to the dashboard to begin." cta={null} /> :
          <div style={{display:"flex",flexDirection:"column",gap:9}}>
            {CHARTERS.filter(c=>done.includes(c.id)).map(c=>(
              <div key={c.id} style={{background:C.white,borderRadius:10,padding:"13px 16px",display:"flex",alignItems:"center",gap:12,border:`1px solid ${C.parchment}`}}>
                <span style={{fontSize:18}}>{c.icon}</span>
                <div style={{flex:1}}><div style={{fontSize:13,fontWeight:500,color:C.text}}>{c.title}</div><div style={{fontSize:12,color:C.muted,marginTop:1}}>{c.sub}</div></div>
                <div style={{fontSize:12,color:C.green,fontWeight:600,background:tint(C.green,0.07),padding:"3px 10px",borderRadius:20}}>✓</div>
              </div>
            ))}
          </div>
        )}

        {profileTab==="share" && (
          <div>
            <div style={{background:C.white,borderRadius:14,padding:"22px",marginBottom:16,border:`1.5px solid ${C.parchment}`}}>
              <div style={{fontSize:12,letterSpacing:2.5,textTransform:"uppercase",color:C.muted,marginBottom:10}}>Your Shareable Profile</div>
              <div style={{fontSize:13,color:C.text,background:C.cream,padding:"10px 13px",borderRadius:8,marginBottom:14,wordBreak:"break-all"}}>{profileUrl}</div>
              <p style={{fontSize:12,color:C.muted,marginBottom:16,lineHeight:1.75,fontWeight:400}}>Sensitive answers — attraction, mental health, conflict, intimacy — stay private until you explicitly share them. The person receiving this will be asked to make a confidentiality commitment before the link opens.</p>
              <button onClick={onShare} style={{width:"100%",background:G_GREEN,color:C.cream,border:"none",borderRadius:8,padding:"13px 20px",fontSize:14,fontWeight:500}}>
                Share Profile →
              </button>
            </div>

            <div style={{background:tint(C.gold,0.05),borderRadius:12,padding:"16px 18px",border:`1px solid ${tint(C.gold,0.12)}`,marginBottom:14}}>
              <div style={{fontSize:12,color:C.goldText,fontWeight:600,marginBottom:10}}>What a potential spouse will see</div>
              {[
                ["🪞 Your Story", storyDone?"Visible — 5 reflections":"Not yet written"],
                ["🌱 Growth Portfolio", growthDone?"Visible — trajectory & growth edges":"Not yet written"],
                ["🤝 Character Witness", witnessDone?`Written by ${witness.name}`:"Not yet added"],
                [`📋 ${done.length} Charters`, done.length>0?"Summary visible":"None completed yet"],
                ["🌸 Physical Expectations", done.includes("physical")?"Visible — your relationship with attraction":"Not yet done"],
                ["💰 Money, Values & History", done.includes("money_values")?"Visible — your relationship with wealth":"Not yet done"],
                ["🔒 Sensitive answers", "Private — mental health, conflict, intimacy details"],
              ].map(([label,status])=>(
                <div key={label} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",fontSize:13,padding:"7px 0",borderBottom:`1px solid ${tint(C.gold,0.08)}`,gap:10}}>
                  <span style={{fontWeight:300,color:C.text,flex:1}}>{label}</span>
                  <span style={{color:status.includes("Not")||status.includes("None")||status.includes("Private")?C.muted:C.green,fontWeight:500,fontSize:12,textAlign:"right",flexShrink:0}}>{status}</span>
                </div>
              ))}
            </div>

            <div style={{background:tint(C.green,0.04),borderRadius:10,padding:"13px 15px",border:`1px solid ${tint(C.green,0.08)}`}}>
              <div style={{fontSize:12,color:C.green,fontWeight:600,marginBottom:4}}>📅 Answers are timestamped</div>
              <div style={{fontSize:12,color:C.muted,lineHeight:1.7,fontWeight:400}}>Everything is marked "True as of {SHORT_DATE}". In 90 days, Mithaq will ask: does this still feel true?</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AnswerCard({label,prompt,answer,date,isWorking}) {
  return (
    <div style={{background:C.white,borderRadius:12,padding:"18px",border:`1px solid ${isWorking?tint(C.gold,0.2):C.parchment}`,borderStyle:isWorking?"dashed":"solid"}}>
      <div style={{fontSize:12,letterSpacing:2,textTransform:"uppercase",color:C.goldText,marginBottom:4}}>{label}</div>
      <div style={{fontFamily:Fd,fontSize:14,color:C.muted,marginBottom:8,fontStyle:"italic",lineHeight:1.4}}>{prompt}</div>
      <div style={{fontSize:14,color:isWorking?C.amber:C.text,lineHeight:1.8,fontWeight:isWorking?400:400,fontStyle:isWorking?"italic":"normal"}}>
        {answer||<span style={{color:C.muted,fontStyle:"italic"}}>Not answered</span>}
      </div>
      {date&&<div style={{marginTop:8,fontSize:12,color:C.muted,display:"flex",alignItems:"center",gap:4}}>📅 True as of {date}</div>}
    </div>
  );
}

function EmptyState({icon,title,sub,cta,onClick,gold}) {
  return (
    <div style={{textAlign:"center",padding:"44px 20px"}}>
      <div style={{fontSize:36,marginBottom:12}}>{icon}</div>
      <h3 style={{fontFamily:Fd,fontSize:22,fontWeight:300,color:C.text,marginBottom:8}}>{title}</h3>
      <p style={{color:C.muted,fontSize:13,lineHeight:1.7,maxWidth:300,margin:cta?"0 auto 22px":"0 auto"}}>{sub}</p>
      {cta&&<button onClick={onClick} style={{background:gold?G_GOLD:G_GREEN,color:gold?C.onGold:C.cream,border:"none",borderRadius:40,padding:"12px 26px",fontSize:13,fontWeight:600,cursor:"pointer"}}>{cta}</button>}
    </div>
  );
}